import { normalizeCountryCode } from "@src/packages/location/countryPreference";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  useWindowDimensions,
  Pressable,
  Image,
  StyleSheet,
} from "react-native";
import {
  ArrowLeft,
  Activity,
  History,
  CalendarClock,
  ClipboardList,
  Pencil,
  Plus,
  ChevronRight,
  XCircle,
  HeartHandshake,
  type LucideIcon,
} from "lucide-react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useFocusEffect } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import { useQueryClient } from "@tanstack/react-query";
import type { CareStackParamList, MainTabParamList } from "../types";
import type { RootStackParamList } from "../../App";
import endingIllustration from "../../assets/images/end-treatment-cycle-illustration.png";
import { useDocumentUploadController } from "../DocumentUploadController";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  DialogTitleText,
  ActionRowCard,
  SectionTitle,
  KebabMenu,
  SecondaryButton,
  type KebabMenuItem,
  BackLink,
  IconButton,
  Tag,
  TextActionButton,
  useToast,
} from "@louise/moon-ui";
import {
  SCREEN_PADDING_TOP,
  semanticColors,
  withAlpha,
} from "@louise/moon-ui/theme/tokens";
import { toLocalDateString, getTodayString } from "@src/packages/utils/date";
import { DEV_STEP_STATES_OVERRIDE } from "@src/packages/cycles/dev/devActiveCycleOverride";
import {
  outcomeNoteNeedsWriting,
  outcomeWriteFor,
} from "@src/packages/cycles/presenters/outcomePatch";
import MedicationFactsheetSheet from "../components/MedicationFactsheetSheet";
import ProcedureDetailSheet from "../components/ProcedureDetailSheet";
import { getProcedureByName } from "@src/packages/content";
import { CycleIllustration } from "../components/CycleIllustration";
import {
  useActiveCycleWithPhases,
  useCycles,
  useCycleTypeLabels,
  useUpdateCycle,
  useCompleteCycle,
  useRecordCycleOutcome,
  CYCLE_TYPE_LABELS,
  isDevNavigableCycleId,
  type Phase,
  type CycleOutcomeResultStatus,
} from "@src/packages/cycles/hooks/useCycles";
import { useCreateTreatmentSnapshot } from "@src/packages/cycles/hooks/usePastCycleDetail";
import {
  usePhases,
  deriveStepProgress,
  getPhaseTypeForStep,
  buildFertilityMetrics,
  clearedOocyteCounts,
  type FertilityMetricWrite,
  buildPhaseSubObjects,
  buildPgtTestResult,
  pgtTestResultMatches,
  phaseKeys,
  type ApiPhase,
  type ApiFertilityMetric,
} from "@src/packages/patients/hooks/usePhases";
import { hasConfirmedPregnancyProcedure } from "@src/packages/cycles/ivfFlowGuards";
import { useIsAuthReady } from "@src/packages/api/useStytch";
import { resolveAuthorizedProjectionStatus } from "@src/packages/experience/resolveAuthorizedProjectionStatus";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  buildBetaTestResultPayload,
  buildBetaTestResultUpdate,
  PATIENT_BETA_OUTCOME_SOURCE_KIND,
} from "@src/packages/cycles/presenters/betaTestResult";
import UpdatePregnancyModal from "./care/modals/UpdatePregnancyModal";
import { WhatToKnowView } from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { buildEarlyPregnancyWhatToKnowCards } from "@src/packages/homepage/plan/earlyPregnancyCards";
import { getPostProcedureOutcomeContext } from "@src/packages/cycles/presenters/postProcedureOutcome";
import {
  isStepPassedUnfilled,
  resolveJourneyPointer,
} from "@src/packages/cycles/presenters/journeyPointer";
import {
  buildPatientPregnancyStatusWrite,
  summarisePregnancyStatus,
  type PregnancyStatus,
} from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import {
  outcomeWriteTarget,
  resolveOutcomeWriteRequestId,
  pregnancyStatusWriteTarget,
  endingAfterBetaWrite,
  betaWritableEnding,
  type OutcomeWriteRequestId,
} from "@src/packages/cycles/presenters/patientOutcomeWrite";
import {
  betaResultErrorMessage,
  pregnancyStatusErrorMessage,
} from "./care/modals/betaResultErrorMessage";
import {
  getStepsForCycle,
  type TreatmentStepConfig,
  type StepPerspective,
} from "@src/packages/homepage/sections/TreatmentProgressModal";
import { findLatestPerformedProcedurePhase } from "@src/packages/cycles/presenters/performedProcedure";
import { stepMayCreatePhase } from "@src/packages/cycles/presenters/stepPhaseWrite";
import { completionMayBeCelebrated } from "@src/packages/cycles/presenters/completionTone";
import {
  DAY_IN_MS,
  estimateTimelineWindow,
  resolveOutcomeTiming,
} from "@src/packages/cycles/presenters/timelineWindow";
import {
  FETBetaOrderUnavailableError,
  FETBetaOrderLookupPendingError,
  getFETBetaOrderDate,
  isFETWaitEligible,
  fetBetaLifecycleQueryKey,
  selectLatestFETBetaOrder,
  selectRecordableFETBetaOrder,
  useCreateFETBetaOrder,
  useFETBetaLifecycle,
  useRecordFETBetaResult,
} from "@src/packages/cycles/useFETBetaLifecycle";
import {
  isPatientOwnedFETProcedure,
  isReportedFETTransfer,
} from "@src/packages/cycles/fetProcedureOwnership";
import { useFETPlanForCycle } from "@src/packages/cycles/useFETPlan";
import {
  useReportFETTransferSelf,
  useUpdateFETProcedureReport,
} from "@src/packages/cycles/useFETProcedure";
import { isDurationPhaseType } from "@src/packages/cycles/presenters/phaseSemantics";
import {
  isCurrentPartnerCareViewer,
  resolveStepCopyPerspective,
  shouldUsePartnerCyclePerspective,
} from "@src/packages/cycles/partnerCycleContext";
import type {
  TreatmentStepProgress,
  CycleType,
} from "@louise/care/treatment-phases";
import {
  MoonTreatmentHubCard,
  type MoonTreatmentHubCardHandle,
  HubCycle,
} from "./care/sections/treatment-hub";
import {
  getProcedureAppointmentPrefill,
  getProcedureSchedulingEntry,
  findScheduledProcedureStep,
} from "./care/sections/treatment-hub/procedureScheduling";
import { AddMedicationModal } from "./care/modals/AddMedicationModal";
import type { EditMedicationData } from "./care/modals/AddMedicationModal";
import {
  deriveRecurrence,
  type AppointmentDetailItem,
  type MedicationScheduleItem,
} from "./care/schedule.types";
import CycleProtocolSection, {
  type CycleProtocolDoseLog,
  type CycleProtocolData,
  type CycleProtocolMedication,
  type CycleProtocolMedicationPeriod,
} from "./care/sections/CycleProtocolSection";
import CycleRelatedDocumentsSection, {
  getCycleDocumentFilterLabel,
  getCycleDocuments,
  getCycleDocumentTag,
} from "./care/sections/CycleRelatedDocumentsSection";
import CycleTestsSection from "./care/sections/CycleTestsSection";
import OvarianStimulationMonitoringSection from "./care/sections/OvarianStimulationMonitoringSection";
import { isMonitoredCycleType } from "@src/packages/cycles";
import {
  buildClinicalAppointmentPrefill,
  getClinicalAppointmentPreset,
} from "@src/packages/care/appointment-presets";
import {
  CalendarOverviewCard,
  useCalendarLoggingTabBarOverride,
  type CalendarSymptomOverlayState,
} from "./care/suggested/CareReviewConceptScreen";
import { MoonStartTreatmentModal } from "./care/modals/MoonStartTreatmentModal";
import {
  createClientRequestId,
  useAcceptEmbryoFreezingPlan,
  useCreateEmbryoFreezingPlan,
  useEmbryoFreezingPlan,
} from "@src/packages/embryo-freezing-plans/hooks/useEmbryoFreezingPlans";
import { createMinimalEmbryoFreezingPlanInput } from "@src/packages/embryo-freezing-plans/minimalStartPlan";
import { AddAppointmentModal } from "./care/modals/AddAppointmentModal";
import CycleCareTeamModal from "./care/modals/CycleCareTeamModal";
import { AppointmentDetailSheet } from "./care/modals/AppointmentDetailSheet";
import EndCycleModal, {
  type PreservationOutcomeCounts,
} from "./YouScreen/modals/EndCycleModal";
import {
  createIdempotencyKey,
  useFrozenSpecimensForCycle,
  useOocyteCohorts,
  useSpecimens,
  useCreateSpecimen,
  useRecordCycleUse,
  useRecordCycleThaw,
  useUpdateOocyteCohort,
} from "@src/packages/care/cryovault/useSpecimens";
import { logClinicalMutationError } from "@src/packages/care/clinical-log-redaction";
import {
  applyFrozenOocyteFlowRewrites,
  getFrozenSpecimenSelectionForCycle,
  isFrozenOocyteCycle,
} from "@src/packages/care/cryovault/frozenCycleSelection";
import {
  getEmbryoIdsToMarkUsed,
  getPickedEmbryoIdsForTransfer,
  needsTransferredEmbryoChoice,
} from "@src/packages/care/cryovault/fetTransferEmbryos";
import {
  buildInventorySyncCycle,
  FROZEN_COUNT_SOURCE,
  isPreservationCycleType,
  MAX_SYNCED_SPECIMENS,
} from "@src/packages/care/cryovault/cycleInventorySync";
import {
  OOCYTE_COUNT_TARGET,
  type OocyteCountMetric,
} from "@src/packages/care/components/frozen-storage-data";
import { buildIntakePrefillFromSpecimens } from "@src/packages/care/cryovault/specimenIntakePrefill";
import {
  buildCryoSpecimenSnapshotsFromStorageGroups,
  CryoVaultOverviewCard,
  type CryoSpecimenSnapshot,
} from "@src/packages/care/cryovault/CryoVaultOverviewCard";
import {
  CryoSpecimenDetailModal,
  buildCryoSpecimenLabel,
  mapApiSpecimensToCryoDetails,
  type CryoSpecimenDetail,
} from "../components/CryoSpecimenDetailModal";
import { InjectionCoachPanel } from "./care/sections/treatment-hub/InjectionCoachPanel";
import { INJECTION_COACH_STEP_ID } from "./care/sections/treatment-hub/injectionCoachEligibility";
import CycleClinicTeamCard, {
  buildCycleTeamMembers,
} from "./care/sections/CycleClinicTeamCard";
import {
  AddSpecimenModal,
  type SpecimenPrefill,
} from "./care/modals/AddSpecimenModal";
import { resolvePartnerCounterparty } from "@src/packages/patients/resolvePartnerCounterparty";
import { resolveProcedureAppointmentPlan } from "@src/packages/cycles/presenters/procedureAppointmentSync";
import {
  betaHcgSeriesQueryKey,
  useBetaHcgSeries,
} from "@src/packages/cycles/useBetaHcgSeries";
import { toBetaHcgDisplayRows } from "@src/packages/cycles/presenters/betaHcgSeries";
import { RecordBetaResultModal } from "./care/modals/RecordBetaResultModal";
import { WaitingPhaseView } from "./care/sections/treatment-hub/WaitingPhaseView";
import { FETBetaResultSeries } from "./care/sections/treatment-hub/FETBetaResultSeries";
import { BetaHcgSeriesCard } from "./care/sections/treatment-hub/BetaHcgSeriesList";
import { RecordedResultView } from "./care/sections/treatment-hub/RecordedResultView";
import type {
  StartTreatmentFormData,
  EditTreatmentData,
  StartTreatmentSubmitError,
} from "./care/modals/MoonStartTreatmentModal";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useCreateCycle } from "@src/packages/cycles/hooks/useCycles";
import {
  useCreatePhase,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import {
  gradedEmbryoGrades,
  readEmbryoGrades,
} from "@src/packages/patients/embryoGrades";
import { isPregnancyDetailsNote } from "@src/packages/homepage/pregnancy/pregnancyDetailsNote";
import { usePregnancyDetailsNote } from "@src/packages/homepage/pregnancy/usePregnancyDetailsNote";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import {
  useCreateTestResult,
  useTestResults,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { appointmentKeys } from "@src/packages/care/hooks/useAppointments";
import {
  calendarKeys,
  useCreateAppointment,
  useDeleteAppointment,
  useMedicationDoseLogs,
  useUpdateAppointment,
} from "@src/packages/care/useCalendarData";
import { useLogMedicationDose } from "@src/packages/care/hooks/useMedicationLogs";
import { usePatientNotes } from "@src/packages/care/hooks/usePatientNotes";
import { useTimezone } from "@src/packages/preferences";
import { buildInjectionDoseRecord } from "@src/packages/injection-protocol/doseRecord";
import type {
  HomepageMedicationFactSheetPayload,
  HomepageProcedureFactSheetPayload,
} from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import { useCycleClinic } from "@src/packages/cycles/hooks/useCycleClinic";
import { useCareConnections } from "@src/packages/patients/hooks/useCareConnections";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";
import { formatDate, formatDateTime } from "@src/packages/i18n/formatters";
import { useCycleHeaderState } from "@src/packages/cycles/hooks/useCycleHeaderState";
import { presentCycleHeaderForCare } from "@src/packages/cycles/presenters/cycleHeaderCare";
import { useSmartBack } from "../hooks/useSmartBack";
import ScreenHeader from "../components/ScreenHeader";
import AddPastCycleDialog from "./care/modals/AddPastCycleDialog";
import { useCycleNavigation } from "../hooks/useCycleNavigation";
import { ArticleRailCard } from "../components/ArticleSectionCard";
import { NegativeCloseoutSupport } from "./care/modals/NegativeCloseoutSupport";
import { getPostCycleArticles } from "./care/postCycleArticles";
import { OrganicExplanatoryIllustration } from "@src/packages/homepage/plan/components/EggFreezingBiologyDeepCarouselCard";
import {
  CyclePregnancyStatusWriteError,
  useCyclePregnancyFollowUp,
  useCyclePregnancyStatus,
  useRecordCyclePregnancyStatus,
  useUpdateCyclePregnancyFollowUp,
  buildEarlyPregnancyDating,
  gestationFromReportedWeeks,
} from "@src/packages/homepage/pregnancy";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
function isDevCryoBridgeFixture(cycleId?: string | null) {
  if (!__DEV__) return false;
  if (isDevNavigableCycleId(cycleId)) return true;
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get(
      "dev_cycle_detail_scenario",
    ) === "cryo_bridge"
  );
}

const DEV_CRYO_BRIDGE_SPECIMENS: CryoSpecimenDetail[] = [
  {
    id: "dev-cycle-egg-1",
    type: "oocyte",
    label: "Egg #1",
    facility: "Kindbody San Francisco",
    status: "stored",
    cycleId: "dev-cycle-bridge",
    cycleLabel: "Egg freezing #1",
    frozenDate: "2026-02-12",
    source: "self",
    storageId: "Tank 2 - Cane 4",
    oocyteRecordKind: "individual",
  },
  {
    id: "dev-cycle-embryo-1",
    type: "embryo",
    label: "Embryo #1",
    facility: "Kindbody San Francisco",
    status: "stored",
    cycleId: "dev-cycle-bridge",
    cycleLabel: "Egg freezing #1",
    frozenDate: "2026-02-18",
    source: "self",
    sourceEggId: "dev-cycle-egg-1",
    grade: "5AA",
    pgtResult: "untested",
    transferCandidate: true,
    storageId: "Tank 2 - Cane 5",
  },
  {
    id: "dev-cycle-sperm-1",
    type: "sperm",
    label: "Semen sample #1",
    facility: "NY Fertility Center",
    status: "stored",
    cycleId: "dev-cycle-bridge",
    cycleLabel: "Egg freezing #1",
    frozenDate: "2026-02-10",
    source: "partner",
    storageId: "Tank 1 - Cane 2",
  },
];

const DEV_CRYO_BRIDGE_SNAPSHOTS: CryoSpecimenSnapshot[] = [
  {
    id: "dev-cycle-egg-group",
    label: "Egg",
    count: "1",
    facility: "Kindbody San Francisco",
    renewal: "Not set",
    detail: "Stored Feb 12, 2026",
    status: "Stored",
    organicIcon: "eggs",
    specimenIds: ["dev-cycle-egg-1"],
  },
  {
    id: "dev-cycle-embryo-group",
    label: "Embryo",
    count: "1",
    facility: "Kindbody San Francisco",
    renewal: "Not set",
    detail: "Stored Feb 18, 2026",
    status: "Stored",
    organicIcon: "genetics",
    specimenIds: ["dev-cycle-embryo-1"],
  },
  {
    id: "dev-cycle-sperm-group",
    label: "Sperm sample",
    count: "1",
    facility: "NY Fertility Center",
    renewal: "Not set",
    detail: "Stored Feb 10, 2026",
    status: "Stored",
    organicIcon: "sperm",
    specimenIds: ["dev-cycle-sperm-1"],
  },
];

type Props = NativeStackScreenProps<CareStackParamList, "CycleDetail">;

// ─── EMBER PALETTE ───────────────────────────────────────────

/**
 * The window shown after a first pregnancy announcement wears the same
 * illustration as the pregnancy-follow-up dialog. Hoisted rather than required
 * inside JSX, which the repo forbids and this file's neighbours avoid.
 */
const PREGNANCY_RECAP_ILLUSTRATION =
  require("../../assets/images/calendar-reminder-dialog-illustration.png") as number;

function normalizeMedicationTimes(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((time): time is string => typeof time === "string");
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
}

function formatMedicationDose(
  dose: number | string | null | undefined,
  doseUnit: string | null | undefined,
) {
  if (dose === null || dose === undefined || dose === "") return "";
  return `${dose}${doseUnit ? ` ${doseUnit}` : ""}`.trim();
}

function getCycleDayLabel(
  cycleStartDate: string | null | undefined,
  date: string | null | undefined,
  t: TranslationFunction,
) {
  if (!cycleStartDate || !date) return undefined;
  const start = new Date(`${cycleStartDate}T00:00:00`).getTime();
  const target = new Date(`${date}T00:00:00`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(target)) return undefined;
  const dayNum = Math.max(1, Math.round((target - start) / DAY_IN_MS) + 1);
  return t("care.cycle_detail.timeline.day_label", {
    params: { day: String(dayNum) },
  });
}

function getMedicationTiming(
  times: string[],
): MedicationScheduleItem["timing"] {
  if (times.length === 0) return "as-needed";
  const hours = times
    .map((time) => parseInt(time, 10))
    .filter((hour) => Number.isFinite(hour));
  if (hours.length === 0) return "as-needed";
  if (hours.every((hour) => hour >= 12)) return "evening";
  if (hours.every((hour) => hour < 12)) return "morning";
  return "as-needed";
}

// ─── TYPE DEFINITIONS (used by sub-components) ──────────────

/**
 * Not a rendered timeline, despite the name.
 *
 * Its two readers take `timing` and `status` and nothing else — the hub's step
 * badge and its "you are here" pointer. It once carried a `title`, an
 * `isEstimate` flag and an `onPress`, all computed on every render and read by
 * nobody.
 */
type CycleChartTimelineItem = {
  stepId: string;
  /** Null on a step she has moved past unanswered: there is nothing true to say. */
  timing: string | null;
  status: "completed" | "active" | "upcoming";
};

type CycleChartTimelineStepConfig = {
  id: string;
  title: string;
  shortTitle?: string;
};

const OVARIAN_CYCLE_TYPES = new Set<string>([
  "egg_freezing",
  "embryo_freezing",
  "ivf",
  "icsi",
]);

const EMBRYO_LAB_CYCLE_TYPES = new Set<string>([
  "embryo_freezing",
  "ivf",
  "icsi",
]);

function getLocalDateMs(date?: string | null) {
  if (!date) return null;
  const ms = new Date(date.includes("T") ? date : `${date}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function formatTimelineDate(ms: number, locale: Locale) {
  return formatDate(ms, locale, {
    month: "short",
    day: "numeric",
  });
}

function formatTimelineWindow(startMs: number, endMs: number, locale: Locale) {
  const start = formatTimelineDate(startMs, locale);
  const end = formatTimelineDate(endMs, locale);
  return start === end ? start : `${start} – ${end}`;
}

function formatPhaseTiming(
  phase: Phase | null | undefined,
  status: CycleChartTimelineItem["status"],
  todayStr: string,
  t: TranslationFunction,
  locale: Locale,
) {
  const startMs = getLocalDateMs(phase?.startDate);
  const endMs = getLocalDateMs(phase?.endDate);
  if (!startMs) return null;

  if (status === "completed" && endMs) {
    return formatTimelineWindow(startMs, endMs, locale);
  }

  if (status === "active") {
    const todayMs = getLocalDateMs(todayStr) ?? Date.now();
    if (!isDurationPhaseType(phase?.type)) {
      return t("care.cycle_detail.timeline.started_on", {
        params: { date: formatTimelineDate(startMs, locale) },
      });
    }
    const dayNum = Math.max(1, Math.round((todayMs - startMs) / DAY_IN_MS) + 1);
    return t("care.cycle_detail.timeline.started_with_day", {
      params: {
        date: formatTimelineDate(startMs, locale),
        day: String(dayNum),
      },
    });
  }

  if (endMs && endMs !== startMs) {
    return formatTimelineWindow(startMs, endMs, locale);
  }

  return t("care.cycle_detail.timeline.starts", {
    params: { date: formatTimelineDate(startMs, locale) },
  });
}

function insertTimelineStepAfter(
  steps: CycleChartTimelineStepConfig[],
  anchorIds: string[],
  step: CycleChartTimelineStepConfig,
) {
  if (steps.some((item) => item.id === step.id)) return steps;
  const anchorIndex = steps.findIndex((item) => anchorIds.includes(item.id));
  if (anchorIndex < 0) return [...steps, step];
  return [
    ...steps.slice(0, anchorIndex + 1),
    step,
    ...steps.slice(anchorIndex + 1),
  ];
}

function buildCycleTimelineSteps(
  steps: TreatmentStepConfig[],
  cycleType: string,
  t: TranslationFunction,
) {
  let timelineSteps: CycleChartTimelineStepConfig[] = steps.map((step) => ({
    id: step.id,
    title: step.title,
    shortTitle: step.shortTitle,
  }));

  if (OVARIAN_CYCLE_TYPES.has(cycleType)) {
    timelineSteps = insertTimelineStepAfter(
      timelineSteps,
      ["protocol", "stimulation"],
      {
        id: "trigger_shot",
        title: t("care.cycle_detail.timeline_steps.trigger_shot_full"),
        shortTitle: t("care.cycle_detail.timeline_steps.trigger"),
      },
    );
  }

  if (EMBRYO_LAB_CYCLE_TYPES.has(cycleType)) {
    timelineSteps = insertTimelineStepAfter(timelineSteps, ["egg_retrieval"], {
      id: "sperm_collection",
      title: t("care.cycle_detail.timeline_steps.sperm_sample"),
      shortTitle: t("care.cycle_detail.timeline_steps.sperm_short"),
    });
    timelineSteps = insertTimelineStepAfter(timelineSteps, ["fertilization"], {
      id: "embryo_development",
      title: t("care.cycle_detail.timeline_steps.embryo_culture"),
      shortTitle: t("care.cycle_detail.timeline_steps.embryo_culture"),
    });
  }

  return timelineSteps;
}

// ─── SCREEN ──────────────────────────────────────────────────

export default function CycleDetailScreen({ navigation, route }: Props) {
  const { openDocumentUpload, pickAndOpenDocumentUpload } =
    useDocumentUploadController();
  const insets = useSafeAreaInsets();
  const t = useTranslations();
  const { showToast } = useToast();
  const capturePress = usePressCapture("CycleDetailScreen");
  const devCryoBridgeFixture = isDevCryoBridgeFixture(route.params?.cycleId);
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const initialPhaseId = route.params?.initialPhaseId;
  useEffect(() => {
    if (initialPhaseId) {
      // Phase deep-link entry point. The screen renders the active cycle's
      // current phase by default; explicit per-phase scroll is a follow-up.
    }
  }, [initialPhaseId]);

  // ── Local UI state ──
  const [calendarMedicationSheet, setCalendarMedicationSheet] =
    useState<HomepageMedicationFactSheetPayload | null>(null);
  const [calendarAppointmentSheet, setCalendarAppointmentSheet] =
    useState<AppointmentDetailItem | null>(null);
  const [editAppointment, setEditAppointment] =
    useState<AppointmentDetailItem | null>(null);
  const [calendarProcedureSheet, setCalendarProcedureSheet] =
    useState<HomepageProcedureFactSheetPayload | null>(null);
  const [calendarProcedureEducationOnly, setCalendarProcedureEducationOnly] =
    useState(false);
  const [calendarSymptomOverlay, setCalendarSymptomOverlay] =
    useState<CalendarSymptomOverlayState | null>(null);
  /**
   * Shown when she dates an upcoming collection of her partner's sample and no
   * partner account is linked. The appointment is still hers; this is the offer
   * to put it in his calendar too, which needs him connected.
   */
  const [partnerCalendarPromptVisible, setPartnerCalendarPromptVisible] =
    useState(false);
  const [careTeamModalVisible, setCareTeamModalVisible] = useState(() => {
    if (!__DEV__ || typeof window === "undefined") return false;
    return (
      new URLSearchParams(window.location.search).get(
        "dev_open_cycle_care_team",
      ) === "1"
    );
  });
  /**
   * The device she told the coach she has, when her prescription does not record
   * one (#2267).
   *
   * Session-local on purpose. Remembering it across sessions would mean writing
   * patient data, and there is no API field for it yet — storing it on the device
   * instead would be a local shim standing in for real data, which this repo does
   * not do. So on the rare dose whose form is missing she is asked again, and
   * sourcing it properly belongs with #2272.
   */
  // An administration method in her calendar's own words — the same five values
  // her add-medication form writes — and not a translated device concept. See
  // `ADMINISTRATION_METHODS`.
  const [injectionCoachForm, setInjectionCoachForm] = useState<string | null>(
    null,
  );

  /**
   * The route she told us, when her prescription does not record one.
   *
   * Session-local for the same reason as the device above, and asked for a
   * different one: urinary hCG arrives as one vial with two routes, and the
   * site, the needle and whether she aspirates all follow from which. Her
   * prescription answers it whenever it carries an intake type; this holds the
   * answer only on the doses where it does not.
   */
  const [injectionCoachRoute, setInjectionCoachRoute] = useState<
    "intramuscular" | "subcutaneous" | null
  >(null);

  const handleOpenCalendarProcedure = useCallback(
    (payload: HomepageProcedureFactSheetPayload) => {
      setCalendarProcedureEducationOnly(false);
      setCalendarProcedureSheet(payload);
    },
    [],
  );
  const handleOpenCalendarAppointmentEducation = useCallback(
    (appointment: AppointmentDetailItem) => {
      const item = getProcedureByName(appointment.title);
      if (!item) return;

      setCalendarAppointmentSheet(null);
      setCalendarProcedureEducationOnly(true);
      setCalendarProcedureSheet({
        item,
        appointment: {
          id: appointment.id,
          appointmentId: appointment.appointmentId,
          title: appointment.title,
          subtitle: appointment.subtitle,
          time: appointment.time,
          date: appointment.date,
          notes: appointment.notes ?? undefined,
          ownership: appointment.ownership,
          ownerId: appointment.ownerId,
        },
      });
    },
    [],
  );
  useCalendarLoggingTabBarOverride(calendarSymptomOverlay);
  // ── Real data hooks ──
  const { data: patient, isLoading: isPatientLoading } = usePatient();
  const isPartnerViewer = isCurrentPartnerCareViewer(patient);
  const cyclePatientId = isPartnerViewer ? patient?.partnerId : undefined;
  const viewerCycle = useActiveCycleWithPhases({
    cycleId: route.params?.cycleId,
  });
  const linkedPartnerCycle = useActiveCycleWithPhases({
    cycleId: route.params?.cycleId,
    patientId: cyclePatientId,
  });
  const isPartnerCycle =
    isPartnerViewer &&
    shouldUsePartnerCyclePerspective({
      patient,
      cycleType: linkedPartnerCycle.cycle?.cycleType,
    });
  const activeCycle = isPartnerCycle ? linkedPartnerCycle : viewerCycle;
  const resolveCycleNavigation = useCycleNavigation();
  /**
   * The cycles list, and whether it is known yet.
   *
   * `useCycles` is `enabled: !!patientId && !!tenantId`, so while the Stytch
   * session hydrates the query is *disabled* — `isLoading` false, `data`
   * undefined — indistinguishable by those two fields from a patient with no
   * cycles. Read that way, two things on this screen were briefly wrong: the
   * `cycleNumber` badge fell back to `1`, so a third embryo cycle announced
   * itself as "#1" before correcting, and `lastClosedOutcome` fell back to null,
   * which is exactly the "bare first-time copy" its own comment below warns
   * about. Same defect as the Home flash, on a different surface.
   *
   * An empty list is data: a patient with no cycles is not made to wait.
   */
  const viewerCyclesQuery = useCycles();
  const linkedPartnerCyclesQuery = useCycles({ patientId: cyclePatientId });
  const viewerCycles = viewerCyclesQuery.data;
  const linkedPartnerCycles = linkedPartnerCyclesQuery.data;
  const allCycles = isPartnerCycle ? linkedPartnerCycles : viewerCycles;
  const isAuthReady = useIsAuthReady();
  const { tenantId, patientId } = useApiClient();
  const allCyclesQuery = isPartnerCycle
    ? linkedPartnerCyclesQuery
    : viewerCyclesQuery;
  const allCyclesStatus = resolveAuthorizedProjectionStatus({
    isAuthReady,
    hasPatient: Boolean(isPartnerCycle ? cyclePatientId : patientId),
    isSettled: allCyclesQuery.isSuccess || allCyclesQuery.isError,
    hasError: allCyclesQuery.isError,
    hasData: Boolean(allCyclesQuery.data),
    isPaused: allCyclesQuery.isPaused,
  });
  const { data: hubPhases = [] } = usePhases(activeCycle.cycle?.id);
  const { mutateAsync: createPhase } = useCreatePhase();
  const { mutateAsync: updatePhase } = useUpdatePhase();
  const updateCycleMutation = useUpdateCycle();
  const completeCycle = useCompleteCycle();
  const recordCycleUse = useRecordCycleUse();
  const recordCycleThaw = useRecordCycleThaw();
  const updateOocyteCohort = useUpdateOocyteCohort();
  const createSnapshot = useCreateTreatmentSnapshot();
  const recordCycleOutcome = useRecordCycleOutcome();
  const createBetaTestResult = useCreateTestResult();
  const updateBetaTestResult = useUpdateTestResult();
  const createFETBetaOrder = useCreateFETBetaOrder();
  const recordFETBetaResult = useRecordFETBetaResult();
  const reportFETTransferSelf = useReportFETTransferSelf();
  const updateFETProcedureReport = useUpdateFETProcedureReport();
  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const deleteAppointment = useDeleteAppointment();
  const queryClient = useQueryClient();
  const { data: partnerPatient } = usePatient({
    patientId: cyclePatientId ?? null,
  });
  /**
   * The typed test result this recording created, and the value it holds. The
   * value is retained with the id because a failed outcome write leaves the
   * modal editable: a retry with a corrected number has to re-stamp that row
   * rather than link a row that still says the old one.
   */
  const [betaTestResult, setBetaTestResult] = useState<{
    id: string;
    value: number;
  } | null>(null);
  const fetBetaOrderRequestIdRef = useRef<string | null>(null);
  const fetBetaResultRequestIdRef = useRef<string | null>(null);
  const fetProcedureReportRequestIdRef = useRef<string | null>(null);
  const betaOutcomeRequestIdRef = useRef<OutcomeWriteRequestId | null>(null);
  // Handle used to push a "scheduled" optimistic state into the treatment hub
  // the moment `AddAppointmentModal` saves a procedure. Without it the hub
  // waits on the phase refetch and the "Planifier …" schedule row keeps
  // rendering until the round-trip completes.
  const treatmentHubRef = useRef<MoonTreatmentHubCardHandle | null>(null);

  // ── Derive cycle info ──
  const cycle = activeCycle.cycle;
  const cycleType = (cycle?.cycleType ?? "ivf") as CycleType;
  const isMonitoredCycle = isMonitoredCycleType(cycle?.cycleType);
  // Declared here rather than beside the other cycle queries because the hub's
  // step progress is derived from it: an FET transfer lives in this ledger, not
  // in a phase.
  const fetBetaLifecycleQuery = useFETBetaLifecycle({
    cycleId: cycle?.id ?? null,
    enabled: cycleType === "fet" && !isPartnerCycle,
  });

  // The canonical beta-hCG series: one measurement source for every modality
  // (#3490). Not gated on `cycleType` — an IVF, IUI or OI beta is the same
  // clinical fact as an FET one and reaches the patient through this query.
  const betaHcgSeriesQuery = useBetaHcgSeries({
    cycleId: cycle?.id ?? null,
    enabled: !isPartnerCycle,
  });
  const betaHcgRows = useMemo(
    () => toBetaHcgDisplayRows(betaHcgSeriesQuery.data),
    [betaHcgSeriesQuery.data],
  );

  const fetPlanForCycleQuery = useFETPlanForCycle({
    cycleId: cycle?.id ?? null,
    enabled: cycleType === "fet" && !isPartnerCycle,
  });
  const embryoFreezingPlanId =
    !isPartnerCycle && cycle?.cycleType === "embryo_freezing"
      ? (cycle.embryoFreezingPlanSnapshot?.planId ?? null)
      : null;
  const { data: embryoFreezingPlan } =
    useEmbryoFreezingPlan(embryoFreezingPlanId);
  const pgtPlan = useMemo(
    () =>
      embryoFreezingPlan?.versions.find(
        (version) => version.version === embryoFreezingPlan.currentVersion,
      )?.pgtPlan ?? null,
    [embryoFreezingPlan],
  );
  const cycleTypeKey = `care.cycle_type.${cycleType}`;
  const displayName =
    t(cycleTypeKey) !== cycleTypeKey
      ? t(cycleTypeKey)
      : (CYCLE_TYPE_LABELS[cycleType] ?? cycleType);
  const { data: documents } = useDocuments();
  const { data: cycleCareTeamMembers } = useCareTeam(cycle?.id);
  const { data: cycleClinic } = useCycleClinic(cycle?.id);
  const { data: careConnections = [] } = useCareConnections();

  const cycleNumber = useMemo(() => {
    if (!cycle) return 1;
    const sameType = (allCycles ?? []).filter(
      (c) => c.cycleType === cycle.cycleType,
    );
    const idx = sameType.findIndex((c) => c.id === cycle.id);
    return idx >= 0 ? idx + 1 : 1;
  }, [cycle, allCycles]);

  // Most recently-closed cycle with a recorded outcome — used to render an
  // outcome-aware close-out card on this screen's empty state, so a patient
  // who just finished a cycle doesn't land on the bare first-time copy.
  // Sort by updatedAt: cycles closed in the same session share an endDate,
  // so we want the one whose outcome was saved last.
  const lastClosedOutcome = useMemo(() => {
    const closed = (allCycles ?? []).filter((c) => c.outcome?.type);
    if (closed.length === 0) return null;
    const sorted = [...closed].sort((a, b) =>
      (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""),
    );
    return sorted[0]?.outcome?.type ?? null;
  }, [allCycles]);

  const phases = activeCycle.phases ?? [];
  const currentPhase = activeCycle.currentPhase;

  // Copy perspective is anatomy-driven: AMAB viewers on shared cycles read
  // partner copy even when they own the record. Role-typed checks below
  // (e.g. `isSupporting`, blocking pregnancy updates) still key on
  // `isPartnerCycle`.
  const perspective: StepPerspective = resolveStepCopyPerspective({
    patient,
    cycleType,
  });
  const partnerDisplayName =
    perspective === "partner"
      ? partnerPatient?.firstName || partnerPatient?.preferredName || undefined
      : undefined;

  const { data: linkedSpecimens, refetch: refetchLinkedSpecimens } =
    useSpecimens();
  const frozenCycleSelection = useMemo(
    () =>
      getFrozenSpecimenSelectionForCycle(linkedSpecimens, cycle?.id ?? null),
    [cycle?.id, linkedSpecimens],
  );
  // A reservation write can fail or lag; the cycle's own
  // `contextSnapshot.materialIds` and `rawValues.eggState` remain the honest
  // signals that this is a frozen-oocyte cycle. Passing `linkedSpecimens`
  // lets the gate verify that materialIds resolve to oocyte-typed rows —
  // sperm-only selections must not trip it. See `isFrozenOocyteCycle`.
  const usingFrozenOocytes = isFrozenOocyteCycle(
    cycle,
    frozenCycleSelection,
    linkedSpecimens,
  );

  // Labels for the embryos the patient picked from the CryoVault for this
  // FET cycle, shown in a banner above the transfer step so she can see which
  // vault embryos (and where they're stored) this transfer is for. Labels are
  // derived through the same builder the vault uses so the numbering matches
  // what she saw when she picked them.
  const pickedEmbryoLabels = useMemo<string[]>(() => {
    if (cycleType !== "fet") return [];
    const pickerIds = cycle?.contextSnapshot?.materialIds ?? [];
    const ids = new Set(
      pickerIds.length > 0 ? pickerIds : frozenCycleSelection.specimenIds,
    );
    if (ids.size === 0) return [];
    const details = mapApiSpecimensToCryoDetails(
      linkedSpecimens ?? [],
      (apiType, index) => buildCryoSpecimenLabel(t, apiType, index),
    );
    return details
      .filter((detail) => detail.type === "embryo" && ids.has(detail.id))
      .map((detail) => {
        const facility = detail.facility?.trim();
        return facility ? `${detail.label} · ${facility}` : detail.label;
      });
  }, [
    cycleType,
    frozenCycleSelection.specimenIds,
    cycle?.contextSnapshot?.materialIds,
    linkedSpecimens,
    t,
  ]);

  const steps = useMemo(() => {
    const base = getStepsForCycle(cycleType, perspective, t, {
      protocol: cycle?.protocol,
      phases: hubPhases,
      pgtPlan,
      cyclePlanSnapshot: cycle?.cyclePlanSnapshot,
    });
    return applyFrozenOocyteFlowRewrites(
      base,
      cycleType,
      usingFrozenOocytes,
      t,
    );
  }, [
    cycle?.protocol,
    cycle?.cyclePlanSnapshot,
    cycleType,
    hubPhases,
    pgtPlan,
    perspective,
    usingFrozenOocytes,
    t,
  ]);

  const derivedStepProgress = useMemo((): TreatmentStepProgress[] => {
    if (!cycle) return [];
    const base = deriveStepProgress(hubPhases, steps).map((p) => {
      const stepData = { ...p.data };
      const cycleOutcomeNotes = cycle?.outcome?.notes;
      if (p.stepId === "freezing" && cycleOutcomeNotes) {
        stepData.notes = cycleOutcomeNotes;
      }
      return {
        stepId: p.stepId,
        phaseId: p.phaseId,
        status: p.status,
        completedAt: p.completedAt,
        medications: [],
        data: stepData,
        notes: null,
      };
    });
    // An FET transfer is recorded in the FET procedure ledger, not as a phase,
    // so the phase-derived status above reads "pending" for a transfer she has
    // already confirmed — the step kept offering its entry form with the embryo
    // count nowhere in sight. Derived rather than written: the procedure holds
    // the date and the count already, and writing them onto a phase as well
    // would let the two records disagree. Reading the ledger also repairs every
    // cycle confirmed before this existed, which a write at confirmation time
    // could not.
    const fetTransfer =
      cycleType === "fet" ? fetBetaLifecycleQuery.data?.procedure : null;
    // Only a status that actually says the transfer was reported counts. Reading
    // "anything not cancelled" showed a `scheduled` or `rescheduled` row as a
    // confirmed transfer — green, dated, with an embryo count — while
    // `isFETWaitEligible` and the backend's beta-order gate both refused it. She
    // would have seen a confirmed transfer with no way to record a beta and no
    // way to end the cycle.
    const withFET = isReportedFETTransfer(fetTransfer)
      ? base.map((sp) =>
          sp.stepId === "transfer"
            ? {
                ...sp,
                status: "completed" as const,
                completedAt: fetTransfer.actualDate,
                data: {
                  ...sp.data,
                  // Same shape `deriveStepProgress` writes for a phase-backed
                  // step, seconds included — the form and the save path both
                  // read this field back.
                  transferDate: fetTransfer.actualTime
                    ? `${fetTransfer.actualDate}T${fetTransfer.actualTime}:00`
                    : fetTransfer.actualDate,
                  ...(fetTransfer.actualTransferCount != null
                    ? { embryosTransferred: fetTransfer.actualTransferCount }
                    : {}),
                  ...(fetTransfer.embryoType
                    ? { embryoType: fetTransfer.embryoType }
                    : {}),
                  ...(fetTransfer.notes ? { notes: fetTransfer.notes } : {}),
                },
              }
            : sp,
        )
      : base;
    // When the FET cycle was started from the CryoVault, prefill the count
    // with the embryos she picked that are still hers to transfer.
    const pickerIds = cycle?.contextSnapshot?.materialIds ?? [];
    const selectedIds =
      pickerIds.length > 0 ? pickerIds : frozenCycleSelection.specimenIds;
    const pickedEmbryoIds =
      cycleType === "fet" && cycle?.id && linkedSpecimens
        ? getPickedEmbryoIdsForTransfer(linkedSpecimens, cycle.id, selectedIds)
        : [];
    const pickedEmbryoCount = pickedEmbryoIds.length;
    const withPickedEmbryoCount =
      pickedEmbryoCount > 0
        ? withFET.map((sp) =>
            // Only prefill when there is genuinely nothing recorded yet. A
            // recorded 0 is a valid transfer outcome (backend accepts it) and
            // must not be overwritten by the picked count.
            sp.stepId === "transfer" && sp.data.embryosTransferred == null
              ? {
                  ...sp,
                  data: {
                    ...sp.data,
                    embryosTransferred: pickedEmbryoCount,
                  },
                }
              : sp,
          )
        : withFET;
    // Dev-only: force specific panel states so completed/skipped/scheduled can
    // be reviewed (see `?dev_stepStates=`).
    const forcedStates = DEV_STEP_STATES_OVERRIDE;
    if (!forcedStates) return withPickedEmbryoCount;
    const todayStr = toLocalDateString(new Date());
    const soon = new Date();
    soon.setDate(soon.getDate() + 3);
    const soonStr = toLocalDateString(soon);
    return withPickedEmbryoCount.map((sp) => {
      const forced = forcedStates[sp.stepId];
      if (!forced) return sp;
      if (forced === "completed") {
        return { ...sp, status: "completed" as const, completedAt: todayStr };
      }
      if (forced === "skipped") {
        return { ...sp, status: "skipped" as const };
      }
      if (forced === "outcome") {
        return {
          ...sp,
          status: "pending" as const,
          data: {
            ...sp.data,
            procedureDate: todayStr,
            eggsCollected: 12,
          },
        };
      }
      // scheduled: future procedure/transfer date drives MoonScheduledStepView
      return {
        ...sp,
        status: "scheduled" as const,
        scheduledFor: soonStr,
        data: {
          ...sp.data,
          procedureDate: `${soonStr}T09:00:00`,
          transferDate: `${soonStr}T09:00:00`,
        },
      };
    });
  }, [
    cycle,
    cycleType,
    hubPhases,
    steps,
    fetBetaLifecycleQuery.data?.procedure,
    frozenCycleSelection.specimenIds,
    linkedSpecimens,
  ]);

  /**
   * Steps whose record belongs to the clinic rather than to her.
   *
   * The FET transfer is the one step read out of a ledger a clinic writes to as
   * well, and a row it authored is not hers to correct: the save below refuses
   * to PATCH over it, and the self-report endpoint answers a replay by returning
   * the clinic's own values. Until this, that refusal was silent — she edited,
   * confirmed, and the centre's figures came straight back as if she had typed
   * them. The hub now locks the step and says where the values came from.
   *
   * Only when the clinic has the hand. A transfer she reported herself stays
   * editable and carries no provenance line.
   *
   * The limit this makes visible rather than fixes: there is no patient-side way
   * to correct a clinic's transfer at all. That is deliberate — her app is not
   * the system of record for what a centre performed — so the answer is a
   * sentence pointing her at her care team, not an edit that would need a
   * backend able to accept a patient amendment to a provider row.
   */
  const clinicRecordedStepIds = useMemo(() => {
    const procedure =
      cycleType === "fet" ? fetBetaLifecycleQuery.data?.procedure : null;
    // `isReportedFETTransfer` is the same test that decides the step is
    // complete. It has to be: the lock renders inside the completed-step panel,
    // so flagging a row this call would not complete produced no lock at all.
    const isClinicsRecord =
      isReportedFETTransfer(procedure) &&
      !isPatientOwnedFETProcedure(procedure);
    return isClinicsRecord ? ["transfer"] : [];
  }, [cycleType, fetBetaLifecycleQuery.data?.procedure]);

  const currentStepId = useMemo(() => {
    if (devCryoBridgeFixture) return null;
    return resolveJourneyPointer(steps, derivedStepProgress);
  }, [devCryoBridgeFixture, steps, derivedStepProgress]);

  const hubCycle = useMemo((): HubCycle | null => {
    if (!cycle) return null;
    return {
      id: cycle.id,
      cycleType: cycle.cycleType,
      startDate: cycle.startDate ?? null,
      clinic: cycle.provider ?? null,
      stepProgress: derivedStepProgress,
      currentPhase: currentPhase?.type as
        | import("@louise/care/treatment-phases").TreatmentPhaseName
        | null
        | undefined,
      phaseStartDate: currentPhase?.startDate ?? null,
      pgtPlan,
      contextSnapshot: cycle.contextSnapshot,
      cyclePlanSnapshot: cycle.cyclePlanSnapshot,
    };
  }, [cycle, currentPhase, derivedStepProgress, pgtPlan]);

  const treatmentGoal = useMemo((): "pregnancy" | "preserve" => {
    const ct = cycle?.cycleType;
    if (
      ct === "egg_freezing" ||
      ct === "embryo_freezing" ||
      ct === "sperm_freezing"
    ) {
      return "preserve";
    }
    return "pregnancy";
  }, [cycle]);

  const specimenPrefill = useMemo((): SpecimenPrefill | undefined => {
    if (treatmentGoal !== "preserve") return undefined;
    const getStepData = (stepId: string): Record<string, unknown> =>
      (derivedStepProgress.find((p) => p.stepId === stepId)?.data ??
        {}) as Record<string, unknown>;
    /**
     * What her own saved specimens already answer.
     *
     * Read from the unfiltered query on purpose: `useFrozenSpecimensForCycle`
     * keeps only rows that are stored with an active frozen location, and a row
     * saved before that was possible is exactly the row whose dates need
     * recovering. Filtering here would hide the answer in the case that needs
     * it.
     */
    const expectedSpecimenType =
      cycle?.cycleType === "embryo_freezing"
        ? "embryo"
        : cycle?.cycleType === "sperm_freezing"
          ? "semen"
          : "oocyte";
    const specimensForIntake = (linkedSpecimens ?? []).filter(
      (specimen) =>
        cycle?.id &&
        specimen.cycleId === cycle.id &&
        // Type as well as cycle. One cycle can hold both its oocytes and the
        // embryos made from them, and this takes the earliest date across the
        // rows it is given — so without this the embryo intake reopened
        // showing the oocytes' freeze date, and their facility, as its own.
        specimen.type === expectedSpecimenType,
    );

    /*
     * How many rows are still frozen, when this cycle has any rows at all.
     *
     * The count and the rows have to come from the same place. The step data
     * (`freezing.eggsFrozen`, `fertilization.embryosFrozen`,
     * `freezing.vialsFrozen`) is written by this form and never updated by the
     * CryoVault's own add flow or by a thaw, so it drifts — and the write loop
     * reads the difference between the two as rows she has just taken back.
     * Reopening on the row count shows her the truth and makes any reduction
     * her own explicit act.
     *
     * `savedRowTotal` rather than the id list decides whether to fall back:
     * ids cover only the rows the intake may act on, so a cohort she has
     * entirely thawed would otherwise look like a cohort never saved and be
     * written a second time beside the first.
     */
    const savedRowCount = (saved: {
      specimenIds?: string[];
      savedRowTotal?: number;
    }): number | undefined =>
      saved.savedRowTotal ? (saved.specimenIds?.length ?? 0) : undefined;

    const savedFromSpecimens =
      buildIntakePrefillFromSpecimens(specimensForIntake);
    /** The day alone, for a step field that may carry the booked hour. */
    const dayOf = (value: unknown): string | undefined =>
      typeof value === "string" && value.trim()
        ? value.slice(0, 10)
        : undefined;

    const retrieval = getStepData("egg_retrieval");
    const freezing = getStepData("freezing");
    const fertilization = getStepData("fertilization");
    const collection = getStepData("sperm_collection");
    const clinic = cycle?.provider ?? undefined;
    const isEmbryoFreezing = cycle?.cycleType === "embryo_freezing";
    const isSpermFreezing = cycle?.cycleType === "sperm_freezing";

    if (isSpermFreezing) {
      const analysis = getStepData("analysis");
      // sperm_collection's date round-trips as `procedureDate` (the generic
      // date field name used by deriveStepProgress) but the form-side field
      // name is `collectionDate`. Read either.
      const rawCollectionDate =
        (collection.collectionDate as string | undefined) ??
        (collection.procedureDate as string | undefined);
      return {
        type: "sperm",
        // The rows this was read from. Without them a reopened intake saved a
        // second cohort beside the first instead of correcting it.
        specimenIds: savedFromSpecimens.specimenIds,
        savedRowTotal: savedFromSpecimens.savedRowTotal,
        specimenRefs: savedFromSpecimens.specimenRefs,
        // The whole form as she saved it, so nothing reopens blank and no
        // save writes a blank back.
        savedNotes: savedFromSpecimens.savedNotes,
        /*
         * The storage block belongs with the ids, not after them.
         *
         * This branch never carried it, which was harmless while a reopened
         * save could only add invisible rows. Once the save UPDATES her vials,
         * a storage question left unanswered writes them `processed` and the
         * vault — which counts only `stored` rows with an active placement —
         * stops showing sperm she still has. The city, country and facility
         * type come along for the same reason: `buildFrozenStorageLocation`
         * always rewrites the location meta, so anything missing here would be
         * overwritten with device defaults.
         */
        storageState: savedFromSpecimens.storageState,
        storageLocation: savedFromSpecimens.storageLocation,
        storageCity: savedFromSpecimens.storageCity,
        storageCountry: savedFromSpecimens.storageCountry,
        storageCountryName: savedFromSpecimens.storageCountryName,
        storageLocationType: savedFromSpecimens.storageLocationType,
        clinicName: clinic || savedFromSpecimens.clinicName || "",
        collectedAt:
          dayOf(rawCollectionDate) ?? savedFromSpecimens.retrievalDate ?? "",
        freezeDate:
          dayOf(freezing.freezeDate) ?? savedFromSpecimens.freezeDate ?? "",
        numberFrozen:
          savedRowCount(savedFromSpecimens) ??
          ((freezing.vialsFrozen as number) || undefined),
        abstinencePeriod: (collection.abstinenceDays as number) ?? undefined,
        // Pull semen analysis values from the analysis step so the user can
        // confirm/edit them on the specimen form.
        concentration: (analysis.concentration as number) ?? undefined,
        totalCount: (analysis.totalCount as number) ?? undefined,
        motilityPercent: (analysis.motilityPercent as number) ?? undefined,
        morphologyPercent: (analysis.morphologyPercent as number) ?? undefined,
        volumeMl: (analysis.volumeMl as number) ?? undefined,
      };
    }

    // For eggs the freezing step writes its date as `procedureDate` (the
    // generic date field name used by `deriveStepProgress`). Reading it here
    // lets the cryo-bridge prefill the freeze date the same way embryos do.
    /**
     * The freezing step has no date of its own to read: it shares the retrieval
     * phase, and `deriveStepProgress` maps a phase date onto `egg_retrieval` but
     * not onto `freezing`. The specimen's own storage location is where the
     * freeze date actually lives, so it answers here instead of teaching that
     * shared presenter a new field.
     */
    const eggFreezeDate =
      dayOf(freezing.freezeDate) ??
      dayOf(freezing.procedureDate) ??
      savedFromSpecimens.freezeDate;
    // Her grade for each embryo, from the step that keeps one per embryo; a
    // cycle graded before that kept its grade on fertilization.
    const developmentGrades = gradedEmbryoGrades(
      readEmbryoGrades(getStepData("embryo_development")),
    );
    const embryoGrades =
      developmentGrades.length > 0
        ? developmentGrades
        : gradedEmbryoGrades(readEmbryoGrades(fertilization));
    return {
      type: isEmbryoFreezing ? "embryos" : "eggs",
      // The rows this was read from. Without them a reopened intake saved a
      // second cohort beside the first instead of correcting it.
      specimenIds: savedFromSpecimens.specimenIds,
      savedRowTotal: savedFromSpecimens.savedRowTotal,
      specimenRefs: savedFromSpecimens.specimenRefs,
      // The whole form as she saved it, so nothing reopens blank and no
      // save writes a blank back.
      savedNotes: savedFromSpecimens.savedNotes,
      /*
       * Which counts her record already marks unknown, so the answer she gave
       * comes back as the answer she gave. The clear held without this — the
       * figure does not return — but the form showed it as a field simply not
       * filled in, which says something different back to her.
       */
      clearedCounts: (() => {
        /*
         * Both steps are read, and de-duplicated: the freezing tab shares the
         * retrieval phase, so `deriveStepProgress` can hand the same metric
         * rows to each of them.
         */
        const cleared = [
          ...new Set([
            ...clearedOocyteCounts(retrieval),
            ...clearedOocyteCounts(freezing),
          ]),
        ];
        return cleared.length > 0 ? cleared : undefined;
      })(),
      clinicName:
        clinic ||
        savedFromSpecimens.clinicName ||
        (fertilization.storageLocation as string) ||
        "",
      retrievalDate:
        dayOf(retrieval.procedureDate) ??
        savedFromSpecimens.retrievalDate ??
        "",
      freezeDate:
        (isEmbryoFreezing
          ? (dayOf(fertilization.procedureDate) ??
            savedFromSpecimens.freezeDate)
          : eggFreezeDate) ?? "",
      totalRetrieved: (retrieval.eggsCollected as number) ?? undefined,
      matureMII: (freezing.matureEggs as number) ?? undefined,
      // Read back from the retrieval phase's metrics, so reopening the intake
      // shows what she already entered instead of two blank fields.
      immatureMI: (retrieval.immatureMI as number) ?? undefined,
      degenerated: (retrieval.degenerated as number) ?? undefined,
      numberFrozen:
        savedRowCount(savedFromSpecimens) ??
        (isEmbryoFreezing
          ? // See `preservationOutcomeCounts`: the embryo count lives on
            // `embryo_development`, so reading `fertilization` alone reopened
            // the intake with a blank count on every embryo cycle.
            ((getStepData("embryo_development").embryosFrozen as number) ??
            (fertilization.embryosFrozen as number) ??
            undefined)
          : ((freezing.eggsFrozen as number) ?? undefined)),
      fertilizationMethod:
        (fertilization.fertilizationMethod as string) ?? undefined,
      embryoGrades,
      // `||`, not `??`: step data is form-sourced, so a cleared field is `""`,
      // and `"" ?? saved` kept the empty string and dropped the tank reference.
      storageLocation:
        (fertilization.storageLocation as string) ||
        savedFromSpecimens.storageLocation,
      storageState: savedFromSpecimens.storageState,
      storageCity: savedFromSpecimens.storageCity,
      storageCountry: savedFromSpecimens.storageCountry,
      storageCountryName: savedFromSpecimens.storageCountryName,
      storageLocationType: savedFromSpecimens.storageLocationType,
    };
  }, [treatmentGoal, derivedStepProgress, cycle, linkedSpecimens]);

  // ── TWW derivation ──
  // The cycle is in the two-week-wait surface when:
  //   - it's a transfer-bearing cycle (treatmentGoal === "pregnancy")
  //   - status is open (not closed)
  //   - the luteal_phase row exists with a startDate ≤ today
  //   - no outcome.type has been recorded yet
  const lutealPhase = useMemo(
    () => hubPhases.find((p) => p.type === "luteal_phase") ?? null,
    [hubPhases],
  );

  const cycleOutcome = useMemo(() => cycle?.outcome ?? null, [cycle]);

  /**
   * Confetti over a recorded loss is the one thing this surface must never do,
   * so that rule lives in a presenter where it can be asserted.
   *
   * It does not also require the cycle to be closed. That was tried, on the
   * reasoning that filling the last step is not the end of a treatment — true
   * in itself, and already carried by `treatmentIsDone`, which the hub tests
   * alongside `isComplete`. But it made the banner unreachable rather than
   * rarer: `useCycleNavigation` sends a closed cycle to `PastCycleDetail`, this
   * screen is the only place `MoonTreatmentHubCard` renders, and closing here
   * resets the stack to Care overview. So no patient could ever be on this
   * screen with a closed cycle, and the celebration — the one this branch had
   * pointed the deleted completion panel at — simply stopped existing.
   */
  const mayCelebrateCompletion = useMemo(
    () => completionMayBeCelebrated(cycleOutcome),
    [cycleOutcome],
  );

  /**
   * Whether the treatment is behind her, however much of it she logged.
   *
   * Deliberately not `isInWaitingPhase`: that needs a `luteal_phase` row, which
   * only exists once someone logs luteal support or sets a test date. A patient
   * who did neither is in the two-week wait as far as her body is concerned and
   * invisible to the app — the same gap that left her with no way to record a
   * result. The procedure having happened is the fact the app actually holds.
   */
  const treatmentIsDone = useMemo(() => {
    if (treatmentGoal !== "pregnancy") return false;
    if (cycleOutcome?.type) return true;
    const today = toLocalDateString(new Date());
    return !!findLatestPerformedProcedurePhase(
      hubPhases.filter((phase) => phase.startDate && phase.startDate <= today),
      ["transfer", "insemination"],
    )?.startDate;
  }, [treatmentGoal, cycleOutcome?.type, hubPhases]);

  const hasConfirmedTransfer = useMemo(
    () => hasConfirmedPregnancyProcedure(hubPhases),
    [hubPhases],
  );

  const isInWaitingPhase = useMemo(() => {
    if (treatmentGoal !== "pregnancy") return false;
    if (!cycle || cycle.status !== "open") return false;
    if (cycleType === "fet") {
      return (
        !cycleOutcome?.type &&
        isFETWaitEligible(
          fetBetaLifecycleQuery.data?.procedure,
          selectLatestFETBetaOrder(fetBetaLifecycleQuery.data),
        )
      );
    }
    const today = toLocalDateString(new Date());
    const performedProcedure = findLatestPerformedProcedurePhase(
      hubPhases.filter((phase) => phase.startDate && phase.startDate <= today),
      ["transfer", "insemination"],
    );
    if (!hasConfirmedTransfer || !performedProcedure?.startDate) return false;
    if (!lutealPhase?.startDate) return false;
    if (lutealPhase.startDate < performedProcedure.startDate) return false;
    const startMs = new Date(lutealPhase.startDate + "T00:00:00").getTime();
    if (lutealPhase.endDate) {
      const endMs = new Date(lutealPhase.endDate + "T00:00:00").getTime();
      const windowDays = Math.round((endMs - startMs) / (1000 * 60 * 60 * 24));
      if (windowDays < 7) return false;
    }
    if (lutealPhase.startDate > today) return false;
    if (cycleOutcome?.type) return false;
    return true;
  }, [
    treatmentGoal,
    cycle,
    cycleType,
    cycleOutcome?.type,
    fetBetaLifecycleQuery.data,
    hubPhases,
    hasConfirmedTransfer,
    lutealPhase,
  ]);

  /**
   * A result is recorded but the cycle is still open — the state that exists
   * because recording no longer closes the cycle.
   *
   * The patient is still on the outcome step here, so this has to keep the hub
   * there. `isInWaitingPhase` goes false the moment an outcome type exists, and
   * the hub sits on the furthest step marked "active", falling back to the first
   * unfinished one — which walks the pointer *backwards* onto an earlier step,
   * away from the result that was just recorded.
   */
  const hasRecordedOpenResult = useMemo(
    () => cycle?.status === "open" && !!cycleOutcome?.type,
    [cycle?.status, cycleOutcome?.type],
  );

  /** The outcome step is where the patient stands for both of those states. */
  const isOnOutcomeStep = isInWaitingPhase || hasRecordedOpenResult;

  const waitingFETOrder =
    cycleType === "fet"
      ? selectLatestFETBetaOrder(fetBetaLifecycleQuery.data)
      : null;
  const waitingStartDate =
    cycleType === "fet"
      ? (fetBetaLifecycleQuery.data?.procedure.actualDate ?? null)
      : (lutealPhase?.startDate ?? null);
  const waitingTestDate =
    cycleType === "fet"
      ? getFETBetaOrderDate(waitingFETOrder)
      : (lutealPhase?.endDate ?? null);

  // ── Treatment Hub state ──
  const [addMedVisible, setAddMedVisible] = useState(false);
  const [hubMedCycleId, setHubMedCycleId] = useState<string | null>(null);
  const [hubMedPhaseId, setHubMedPhaseId] = useState<string | null>(null);
  const [hubMedRole, setHubMedRole] = useState<
    "standard" | "trigger_shot" | null
  >(null);
  const [editMed, setEditMed] = useState<EditMedicationData | null>(null);
  const [startTreatmentVisible, setStartTreatmentVisible] = useState(false);
  const [addPastCycleVisible, setAddPastCycleVisible] = useState(false);
  const [addAppointmentVisible, setAddAppointmentVisible] = useState(false);
  // Seeds AddAppointmentModal for a "create" flow launched pre-filled — used by
  // the two-week-wait card's pregnancy-test row to schedule a beta hCG test.
  const [appointmentPrefill, setAppointmentPrefill] = useState<NonNullable<
    React.ComponentProps<typeof AddAppointmentModal>["prefill"]
  > | null>(null);
  const openBetaAppointmentModal = useCallback(() => {
    setAppointmentPrefill(
      buildClinicalAppointmentPrefill("beta_hcg", {
        date: waitingTestDate ?? "",
        cycleId: cycle?.id ?? null,
      }),
    );
    setAddAppointmentVisible(true);
  }, [cycle?.id, waitingTestDate]);
  const [editTreatmentData, setEditTreatmentData] =
    useState<EditTreatmentData | null>(null);
  const [editTreatmentInitialStep, setEditTreatmentInitialStep] = useState<
    1 | 2
  >(1);
  const [
    allowTreatmentTypeBackNavigation,
    setAllowTreatmentTypeBackNavigation,
  ] = useState(true);
  // The last start or edit didn't save. It shows inside the open dialog,
  // above its buttons, so her answers stay put while she tries again.
  const [cycleSaveFailed, setCycleSaveFailed] = useState(false);
  const [endCycleVisible, setEndCycleVisible] = useState(false);
  const [endCycleMode, setEndCycleMode] = useState<"end" | "complete">("end");
  const [addSpecimenVisible, setAddSpecimenVisible] = useState(false);
  const [addSpecimenForRetrieval, setAddSpecimenForRetrieval] = useState(false);
  // When the embryo-development step records N embryos frozen, offer to add
  // those N to the CryoVault right away rather than making her navigate to
  // Add Specimen from scratch. Non-null count = the prompt dialog is
  // showing; a non-null `embryoAddPrefillOverride` swaps in the embryo
  // prefill for the next AddSpecimenModal open. Only fires for IVF/ICSI
  // cycles — FET, egg-freezing, and sperm-freezing do not produce embryos
  // through this step.
  const [embryoAddPromptCount, setEmbryoAddPromptCount] = useState<
    number | null
  >(null);
  const [embryoAddPrefillOverride, setEmbryoAddPrefillOverride] =
    useState<SpecimenPrefill | null>(null);
  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string | null>(
    null,
  );
  const [betaResultVisible, setBetaResultVisible] = useState(false);
  const [betaResultError, setBetaResultError] = useState<string | null>(null);
  const [updatePregnancyVisible, setUpdatePregnancyVisible] = useState(false);
  const [updatePregnancyError, setUpdatePregnancyError] = useState<
    string | null
  >(null);
  const updatePregnancyRequestIdRef = useRef<OutcomeWriteRequestId | null>(
    null,
  );
  const [isSavingTestDate, setIsSavingTestDate] = useState(false);
  const [testDateSaveError, setTestDateSaveError] = useState<string | null>(
    null,
  );
  const [pendingTestDate, setPendingTestDate] = useState<string | null>(null);
  const createCycle = useCreateCycle();
  const createEmbryoFreezingPlan = useCreateEmbryoFreezingPlan();
  const acceptEmbryoFreezingPlan = useAcceptEmbryoFreezingPlan();
  const {
    cycleTotalEggs: totalEggs,
    cycleTotalEmbryos: totalEmbryos,
    cycleTotalSperm: totalSperm,
    cycleSpecimens,
    cycleStorageGroups: storageGroups,
  } = useFrozenSpecimensForCycle(cycle?.id);
  const createSpecimen = useCreateSpecimen();
  /*
   * Her country, or none at all — never a guess.
   *
   * `buildPrefillCycle` defaults to "US" and `buildStorageLocationMeta`
   * serialises whatever the cycle carries into the placement's notes, which is
   * what the vault and the detail sheets read back. So an unattended sync would
   * have stamped `US` on a French patient's Paris clinic.
   *
   * The intake falls back to the device's region, and that is right there: it
   * shows her the field and she can correct it before saving. This writes with
   * nothing on screen, so an unreviewed guess would become a fact in her record
   * that she never saw. Absent says what is actually known.
   */
  const specimenCountry =
    normalizeCountryCode(patient?.addresses?.[0]?.country) ?? "";
  const { data: oocyteCohorts = [] } = useOocyteCohorts();

  const preservationOutcomeCounts = useMemo<PreservationOutcomeCounts>(() => {
    const dataFor = (stepId: string) =>
      derivedStepProgress.find((progress) => progress.stepId === stepId)
        ?.data ?? {};
    const retrieval = dataFor("egg_retrieval");
    const freezing = dataFor("freezing");
    const fertilization = dataFor("fertilization");
    const embryoDevelopment = dataFor("embryo_development");
    const numberOrNull = (value: unknown): number | null =>
      typeof value === "number" && Number.isFinite(value) ? value : null;

    if (cycle?.cycleType === "sperm_freezing") {
      return {
        kind: "sperm",
        retrieved: null,
        mature: null,
        fertilized: null,
        frozen: numberOrNull(freezing.vialsFrozen),
      };
    }

    return {
      kind: cycle?.cycleType === "embryo_freezing" ? "embryos" : "eggs",
      retrieved: numberOrNull(retrieval.eggsCollected),
      mature: numberOrNull(freezing.matureEggs),
      fertilized:
        cycle?.cycleType === "embryo_freezing"
          ? numberOrNull(fertilization.eggsFertilized)
          : null,
      frozen:
        cycle?.cycleType === "embryo_freezing"
          ? numberOrNull(
              /*
               * `embryo_development`, not `fertilization`. That step is
               * inserted into the embryo flow at runtime and is the only one
               * that carries `embryosFrozen` — `usePhases` maps `frozen_embryo`
               * back to that field for that step id alone. Read from the wrong
               * step this was always null, so an embryo cycle could record four
               * frozen and still show no count in its closing recap.
               */
              embryoDevelopment.embryosFrozen ??
                fertilization.embryosFrozen ??
                fertilization.eggsFrozen,
            )
          : numberOrNull(freezing.eggsFrozen),
    };
  }, [cycle?.cycleType, derivedStepProgress]);
  const hasRecordedPreservationOutcome =
    (cycle?.cycleType === "egg_freezing" ||
      cycle?.cycleType === "embryo_freezing" ||
      cycle?.cycleType === "sperm_freezing") &&
    preservationOutcomeCounts.frozen != null;
  const cryoSpecimenDetails = useMemo(
    () =>
      devCryoBridgeFixture
        ? DEV_CRYO_BRIDGE_SPECIMENS
        : mapApiSpecimensToCryoDetails(cycleSpecimens, (apiType, index) =>
            buildCryoSpecimenLabel(t, apiType, index),
          ),
    [cycleSpecimens, devCryoBridgeFixture, t],
  );
  const bridgeSpecimens = useMemo<CryoSpecimenSnapshot[]>(
    () =>
      devCryoBridgeFixture
        ? DEV_CRYO_BRIDGE_SNAPSHOTS
        : buildCryoSpecimenSnapshotsFromStorageGroups(
            t,
            locale,
            storageGroups,
            oocyteCohorts,
          ),
    [devCryoBridgeFixture, locale, oocyteCohorts, storageGroups, t],
  );

  const handleEditTreatment = useCallback(() => {
    if (!cycle) return;
    setEditTreatmentData({
      cycleType: cycle.cycleType as CycleType,
      clinicName: cycle.provider ?? "",
      startDate: cycle.startDate
        ? new Date(cycle.startDate + "T00:00:00")
        : null,
    });
    setEditTreatmentInitialStep(1);
    setAllowTreatmentTypeBackNavigation(true);
    setStartTreatmentVisible(true);
  }, [cycle]);
  const handleEditTreatmentWhereWhen = useCallback(() => {
    if (!cycle) return;
    setEditTreatmentData({
      cycleType: cycle.cycleType as CycleType,
      clinicName: cycle.provider ?? "",
      startDate: cycle.startDate
        ? new Date(cycle.startDate + "T00:00:00")
        : null,
    });
    setEditTreatmentInitialStep(2);
    setAllowTreatmentTypeBackNavigation(false);
    setStartTreatmentVisible(true);
  }, [cycle]);
  const handleOpenStartTreatment = useCallback(() => {
    setEditTreatmentData(null);
    setEditTreatmentInitialStep(1);
    setAllowTreatmentTypeBackNavigation(true);
    setStartTreatmentVisible(true);
  }, []);

  const heroMenuItems = useMemo<KebabMenuItem[]>(() => {
    const items: KebabMenuItem[] = [
      {
        id: "edit",
        label: t("care.cycle_detail.menu.edit_cycle"),
        icon: Pencil,
        onPress: handleEditTreatment,
      },
    ];
    const isPreservation =
      cycle?.cycleType === "egg_freezing" ||
      cycle?.cycleType === "embryo_freezing" ||
      cycle?.cycleType === "sperm_freezing";
    if (isPreservation) {
      items.push({
        id: "add-specimen",
        label: t("care.cycle_detail.menu.add_specimen"),
        icon: Plus,
        onPress: () => setAddSpecimenVisible(true),
      });
    }
    items.push({
      id: "end",
      label: t("care.cycle_detail.menu.end_cycle"),
      icon: XCircle,
      tone: "destructive",
      onPress: () => {
        setEndCycleMode("end");
        setEndCycleVisible(true);
      },
    });
    return items;
  }, [cycle?.cycleType, handleEditTreatment, t]);

  const handleStartTreatment = useCallback(
    async (formData: StartTreatmentFormData) => {
      const startDateStr = formData.startDate
        ? toLocalDateString(formData.startDate)
        : null;

      const cycleType = formData.treatmentType;

      setCycleSaveFailed(false);
      try {
        if (editTreatmentData && cycle?.id) {
          await updateCycleMutation.mutateAsync({
            cycleId: cycle.id,
            data: {
              cycleType,
              provider: formData.clinicName || null,
              startDate: startDateStr,
            },
          });
        } else {
          let embryoFreezingPlan: Awaited<
            ReturnType<typeof createEmbryoFreezingPlan.mutateAsync>
          > | null = null;
          if (cycleType === "embryo_freezing") {
            const draftPlan = await createEmbryoFreezingPlan.mutateAsync(
              createMinimalEmbryoFreezingPlanInput(createClientRequestId()),
            );
            embryoFreezingPlan = await acceptEmbryoFreezingPlan.mutateAsync({
              planId: draftPlan.id,
              input: {
                clientRequestId: createClientRequestId(),
                expectedVersion: draftPlan.currentVersion,
              },
            });
          }
          await createCycle.mutateAsync({
            cycleType,
            provider: formData.clinicName || null,
            startDate: startDateStr,
            status: "open",
            ...(embryoFreezingPlan
              ? {
                  embryoFreezingPlanSnapshot: {
                    planId: embryoFreezingPlan.id,
                    expectedVersion: embryoFreezingPlan.currentVersion,
                    clientRequestId: createClientRequestId(),
                  },
                }
              : {}),
          });
        }
        await queryClient.refetchQueries({
          queryKey: ["active-cycle"],
          type: "all",
        });
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
        setEditTreatmentData(null);
        setEditTreatmentInitialStep(1);
        setAllowTreatmentTypeBackNavigation(true);
        setStartTreatmentVisible(false);
      } catch (e) {
        console.error("[CycleDetail] Failed to save cycle:", e);
        setCycleSaveFailed(true);
      }
    },
    [
      createCycle,
      createEmbryoFreezingPlan,
      acceptEmbryoFreezingPlan,
      updateCycleMutation,
      queryClient,
      editTreatmentData,
      cycle,
    ],
  );

  const handleCloseStartTreatment = useCallback(() => {
    setStartTreatmentVisible(false);
    setEditTreatmentData(null);
    setEditTreatmentInitialStep(1);
    setAllowTreatmentTypeBackNavigation(true);
    setCycleSaveFailed(false);
  }, []);
  const clearCycleSaveFailure = useCallback(
    () => setCycleSaveFailed(false),
    [],
  );
  const startTreatmentSubmitError =
    useMemo((): StartTreatmentSubmitError | null => {
      if (!cycleSaveFailed) return null;
      return editTreatmentData
        ? {
            title: t("care.cycle_detail.alerts.cycle_changes_not_saved"),
            message: t("care.cycle_detail.alerts.error_generic"),
            canRetry: true,
          }
        : {
            title: t("care.overview.alerts.unable_to_start_title"),
            message: t("care.overview.alerts.unable_to_start_message"),
            canRetry: true,
          };
    }, [cycleSaveFailed, editTreatmentData, t]);

  const handleOpenMedicationModal = useCallback(
    (opts: {
      cycleId: string;
      phaseId?: string | null;
      medicationRole?: "standard" | "trigger_shot" | null;
    }) => {
      setHubMedCycleId(opts.cycleId);
      setHubMedPhaseId(opts.phaseId ?? null);
      setHubMedRole(opts.medicationRole ?? null);
      setAddMedVisible(true);
    },
    [],
  );

  /**
   * Recording a count is recording the samples.
   *
   * The hub's freezing step wrote a phase and its `fertilityMetrics` and
   * nothing else, while the only thing in the app that creates a specimen row
   * is `AddSpecimenModal` — so a cycle filled here always ended up reporting
   * "8 frozen, 0 attached" and asking her to enter it a second time by hand.
   * That is what the recap's orange banner was reporting, on the ordinary path,
   * for everyone.
   *
   * The write goes through the intake's own mutation rather than a second way
   * of creating specimens, so the rules are the same whichever surface she
   * used: a re-save updates the rows it already made, a larger count adds to
   * them, a smaller one retires the tail.
   *
   * A failure here is logged and swallowed. The phase write has already
   * committed, and throwing would tell the hub its own save failed and send it
   * back to PATCH a phase that is already correct. The recap's banner is the
   * honest fallback: it will simply say what it used to say.
   */
  const syncCycleInventory = useCallback(
    async (
      stepId: string,
      stepData: Record<string, string | number | boolean | null>,
      /** The date the phase this save wrote now carries. */
      phaseDate: string | null,
    ) => {
      const cycleType = cycle?.cycleType;
      if (!cycle?.id || !isPreservationCycleType(cycleType)) return;
      const source = FROZEN_COUNT_SOURCE[cycleType];
      if (stepId !== source.step) return;
      /*
       * Coerced, not type-checked. The step's fields are declared `number` but
       * arrive as whatever the form put in them — `buildFertilityMetrics` calls
       * `Number(val)` for the same reason. A `typeof` guard here would have
       * made the whole sync a silent no-op for a value like "8".
       */
      const raw = stepData[source.field];
      if (raw == null || raw === "" || typeof raw === "boolean") return;
      const recorded = Number(raw);
      /*
       * A whole number of samples, within reach of a real cohort.
       *
       * `useCreateSpecimen` loops to this count, and nothing on this path can
       * undo the result: once rows exist the sync refuses to run again, by
       * design. So "8.5" would have written nine rows and a mistyped "80" would
       * have written eighty, in one unattended batch, with no way back except
       * retiring them one cohort at a time through the intake.
       */
      if (!Number.isInteger(recorded) || recorded > MAX_SYNCED_SPECIMENS) {
        return;
      }

      /*
       * A date from this very save, not only from the memo.
       *
       * `specimenPrefill` is computed before the write, so a step that carries
       * the count and the date together — semen's does — would find no date on
       * the first save and write nothing at all, silently.
       */
      const savedDate =
        stepData.freezeDate ?? stepData.procedureDate ?? phaseDate;

      const inventoryCycle = buildInventorySyncCycle({
        cycleType,
        frozenCount: recorded,
        prefill: specimenPrefill ?? {},
        country: specimenCountry,
        recordedDate:
          typeof savedDate === "string" && savedDate.trim()
            ? savedDate.slice(0, 10)
            : undefined,
      });
      if (!inventoryCycle) return;

      try {
        await createSpecimen.mutateAsync({
          cycles: [inventoryCycle],
          cycleId: cycle.id,
        });
      } catch (e) {
        console.error(
          "[CycleDetailScreen] inventory sync failed for step",
          stepId,
          "count",
          recorded,
          "→",
          e,
        );
      }
    },
    [
      cycle?.id,
      cycle?.cycleType,
      createSpecimen,
      specimenPrefill,
      specimenCountry,
    ],
  );

  // The transfer is already saved when any of these show. What is left to do
  // is in the vault, so the toast leads there, and it stays up longer because
  // it explains more than a plain failure does.
  const showCryoVaultFollowUpToast = useCallback(
    (variant: "error" | "info", message: string) => {
      showToast({
        variant,
        message,
        duration: 10000,
        action: {
          label: t("care.treatment_hub.open_cryo_vault"),
          onPress: () => navigation.navigate("CryoVault"),
          analyticsAction: "open_cryo_vault_from_toast",
        },
      });
    },
    [navigation, showToast, t],
  );

  const handleUpdateStepProgress = useCallback(
    async (
      stepId: string,
      data: Partial<TreatmentStepProgress>,
      options?: {
        /**
         * Step fields she explicitly marked unknown, to erase rather than skip.
         * Only an explicit signal can do this — see `buildFertilityMetrics`.
         */
        clearedFields?: readonly string[];
        /**
         * Step fields whose recorded answer she has withdrawn — she had said "I
         * don't know" and has un-said it without giving a figure. The row goes,
         * because a null row still reads back as "I don't know".
         */
        removedFields?: readonly string[];
        /**
         * Set by the appointment flow, which has just written the appointment
         * itself and is only mirroring its date onto the phase.
         *
         * Without it the sync below saw the `appointmentId` that mirror carries,
         * resolved to `update`, and PATCHed the appointment one tick after
         * `AddAppointmentModal` saved it — with a payload rebuilt from the step's
         * canonical scheduling entry. Her title became "Egg retrieval", a
         * multi-activity visit lost every activity but one, and the duration she
         * chose collapsed to the 60-minute default. The step modal still syncs;
         * only the path that is echoing an appointment opts out.
         */
        skipCalendarSync?: boolean;
        /**
         * Set by the specimen intake, which has just written the rows itself.
         *
         * Its `onSaved` bridge writes `eggsFrozen` back onto the freezing step,
         * which is exactly what the inventory sync listens for — and the sync's
         * view of the cycle's rows is a memo from before that save, so it would
         * have seen none and created a second cohort beside the one the intake
         * had just made. The duplication this area spent a day removing.
         */
        skipInventorySync?: boolean;
      },
    ) => {
      if (!cycle?.id) return;

      const phaseType = getPhaseTypeForStep(stepId);
      if (!phaseType) {
        throw new Error(
          `[CycleDetailScreen] Cannot save ${stepId}: no API phase mapping exists.`,
        );
      }
      try {
        const now = new Date();
        const today = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
        const latestPhases =
          queryClient.getQueryData<ApiPhase[]>(
            phaseKeys.cycle(tenantId ?? "", cycle.id),
          ) ?? hubPhases;
        const existingPhase =
          latestPhases.find((p) => p.type === phaseType) ?? null;

        // A step that borrows another step's phase may read it, not summon it.
        // Saving `outcome` with no luteal phase yet would create one dated
        // today, and a luteal phase that predates the transfer permanently
        // fails the `isInWaitingPhase` guard below — the two-week wait would
        // never appear for this cycle again. The panel no longer offers the
        // action; this is the half that does not rely on it.
        if (!existingPhase && !stepMayCreatePhase(stepId)) return;

        const stepData = data.data ?? {};

        /**
         * Put an upcoming procedure in her calendar, the way the scheduling row
         * already does.
         *
         * The hub told her, on the very card that collects this date, that
         * scheduling it would keep her calendar up to date — and typing the date
         * into the step reached the phase and stopped there. So the two ways of
         * dating a procedure now do the same thing.
         *
         * Before the phase write, not after, because the appointment's id has to
         * ride along in the same payload: `addProcedure` persists
         * `appointmentId` on the procedure, which is what lets a later
         * correction move that appointment instead of booking a second one. It
         * is also the order the scheduling row uses — appointment first, date
         * mirrored onto the phase second.
         *
         * A failure here must not cost her the step. The record is the thing she
         * asked to save; the calendar entry is a courtesy on top, and the phase
         * write below is unconditional.
         */
        const schedulingEntry = getProcedureSchedulingEntry(stepId);

        /**
         * Skipping a procedure retires the appointment it booked.
         *
         * She dates the collection for Tuesday, the appointment is created, and
         * she then learns they are using frozen sperm and skips the step. Without
         * this the Tuesday appointment stays — in her calendar and, once the
         * sharing grant is there, in her partner's — for a procedure the app now
         * records as not happening.
         */
        if (
          schedulingEntry &&
          data.status === "skipped" &&
          !options?.skipCalendarSync
        ) {
          const bookedId = stepData.appointmentId;
          if (typeof bookedId === "string" && bookedId) {
            try {
              await deleteAppointment.mutateAsync(bookedId);
              // Cleared in the same payload, so the phase stops naming an
              // appointment that no longer exists.
              stepData.appointmentId = null;
            } catch (cancelError) {
              console.error(
                "[CycleDetailScreen] could not retire the appointment for skipped step",
                stepId,
                "→",
                cancelError,
              );
              showToast({
                variant: "error",
                message: t(
                  "care.cycle_detail.alerts.skipped_step_appointment_not_removed",
                ),
              });
            }
          }
        }

        if (
          schedulingEntry &&
          data.status !== "skipped" &&
          !options?.skipCalendarSync
        ) {
          const appointmentPlan = resolveProcedureAppointmentPlan({
            stepId,
            data: stepData,
            dateField: schedulingEntry.dateField,
            todayIso: today,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            partner: resolvePartnerCounterparty(patient?.counterparties),
          });
          // Only when the appointment is first booked. Asked again on every
          // correction of the date it stops being an offer and becomes a thing
          // she has to dismiss, which is the opposite of the point.
          if (
            appointmentPlan.invitePartnerToConnect &&
            appointmentPlan.action === "create"
          ) {
            setPartnerCalendarPromptVisible(true);
          }
          if (appointmentPlan.action !== "none" && appointmentPlan.startDate) {
            try {
              const schedulingPreset = getClinicalAppointmentPreset(
                schedulingEntry.preset,
              );
              // All four time fields, always: `createAppointment` throws a
              // ValidationError on a missing `startTime`, `endDate` or `endTime`
              // and `mergeAppointmentFormats` fills in none of them, so a partial
              // window is a write that only ever fails — in the catch below,
              // where she would never see it.
              const shared = {
                cycleId: cycle.id,
                title: schedulingPreset.title,
                consultationType: [schedulingPreset.activity],
                startDate: appointmentPlan.startDate,
                startTime: appointmentPlan.startTime ?? undefined,
                endDate: appointmentPlan.endDate ?? undefined,
                endTime: appointmentPlan.endTime ?? undefined,
                additionalParticipantIds:
                  appointmentPlan.additionalParticipantIds,
              };
              if (appointmentPlan.appointmentId) {
                await updateAppointment.mutateAsync({
                  appointmentId: appointmentPlan.appointmentId,
                  ...shared,
                });
              } else {
                const created = await createAppointment.mutateAsync(shared);
                const createdId = created?.data?.id;
                if (createdId) stepData.appointmentId = createdId;
              }
            } catch (appointmentError) {
              console.error(
                "[CycleDetailScreen] could not sync the calendar for step",
                stepId,
                "→",
                appointmentError,
              );
              showToast({
                variant: "error",
                message: t(
                  "care.cycle_detail.alerts.calendar_not_updated_for_step",
                ),
              });
            }
          }
        }

        /*
         * The cleared fields travel beside the data, not inside it: a null in
         * `stepData` cannot say whether she took a figure back or never gave
         * one, and treating the two alike erased recorded counts once already.
         */
        const fertilityMetrics =
          Object.keys(stepData).length > 0 ||
          options?.clearedFields?.length ||
          options?.removedFields?.length
            ? buildFertilityMetrics(
                stepData,
                existingPhase,
                options?.clearedFields,
                options?.removedFields,
              )
            : undefined;
        /*
         * A phase being created has no rows to remove, so a withdrawal cannot
         * appear on that path — `buildFertilityMetrics` only emits one against
         * an existing row's id. Filtered rather than asserted, so that stays
         * true if the rule above it ever changes.
         */
        const metricsForCreate = fertilityMetrics
          ?.filter(
            (
              metric,
            ): metric is Extract<FertilityMetricWrite, { type: string }> =>
              !("op" in metric),
          )
          .map((metric) => ({ type: metric.type, value: metric.value }));

        const subObjects = buildPhaseSubObjects(
          stepId,
          stepData,
          patientId,
          existingPhase,
          cycleType,
        );

        // PGT has no standalone phase in the current API. Persist its
        // patient-entered summary as a typed genetics test result on the
        // existing embryo-development phase, without changing that phase's
        // dates. This keeps skip/report/pending distinct and prevents an
        // optimistic completion from disappearing on reload.
        if (stepId === "pgt_testing") {
          const pgtStatus =
            data.status === "skipped"
              ? "not_performed"
              : data.status === "pending"
                ? "pending"
                : "reported";
          const hasCount =
            stepData.euploidCount !== null &&
            stepData.euploidCount !== undefined &&
            stepData.euploidCount !== "";
          const hasNotes =
            typeof stepData.pgtNotes === "string" &&
            stepData.pgtNotes.trim().length > 0;
          if (pgtStatus === "reported" && !hasCount && !hasNotes) {
            throw new Error("PGT results need a count or note before saving");
          }

          const pgtTestResult = buildPgtTestResult(
            stepData,
            pgtStatus,
            today,
            existingPhase,
          );
          const otherTestResults = (existingPhase?.testResults ?? []).filter(
            (result) => result.name?.toLowerCase() !== "pgt",
          );
          const testResults = [...otherTestResults, pgtTestResult];

          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: { testResults },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: "embryo_development",
                startDate: today,
                endDate: null,
                testResults,
              },
            });
          }
          const phaseQueryKey = phaseKeys.cycle(tenantId ?? "", cycle.id);
          await queryClient.refetchQueries({
            queryKey: phaseQueryKey,
            type: "all",
          });
          const persistedPhase =
            queryClient
              .getQueryData<ApiPhase[]>(phaseQueryKey)
              ?.find((phase) => phase.type === "embryo_development") ?? null;
          if (!pgtTestResultMatches(persistedPhase, pgtTestResult)) {
            throw new Error("PGT decision did not round-trip from the API");
          }
          return;
        }

        let procedureStartDate: string | undefined;
        let procedureStartTime: string | undefined;
        const rawProcDate = (stepData.procedureDate ??
          stepData.transferDate ??
          stepData.collectionDate) as string | null;
        if (rawProcDate && rawProcDate.includes("T")) {
          const d = new Date(rawProcDate);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = (d.getMonth() + 1).toString().padStart(2, "0");
            const day = d.getDate().toString().padStart(2, "0");
            procedureStartDate = `${y}-${m}-${day}`;
            const hours = d.getHours().toString().padStart(2, "0");
            const minutes = d.getMinutes().toString().padStart(2, "0");
            procedureStartTime = `${hours}:${minutes}`;
          }
        } else if (rawProcDate) {
          procedureStartDate = rawProcDate.split("T")[0]!;
        }

        if (
          data.status === "completed" &&
          stepId === "transfer" &&
          cycle.cycleType === "fet"
        ) {
          if (!procedureStartDate) {
            throw new Error("A transfer date is required before saving");
          }
          if (fetPlanForCycleQuery.isPending) {
            throw new Error("The accepted FET plan is still loading");
          }
          if (fetPlanForCycleQuery.error) {
            throw fetPlanForCycleQuery.error;
          }
          const plan = fetPlanForCycleQuery.data;
          const rawTransferCount = stepData.embryosTransferred;
          const actualTransferCount =
            typeof rawTransferCount === "number"
              ? rawTransferCount
              : typeof rawTransferCount === "string" && rawTransferCount
                ? Number(rawTransferCount)
                : null;
          const clientRequestId =
            fetProcedureReportRequestIdRef.current ?? createIdempotencyKey();
          fetProcedureReportRequestIdRef.current = clientRequestId;
          const transferFacts = {
            actualDate: procedureStartDate,
            ...(procedureStartTime
              ? {
                  actualTime: procedureStartTime,
                  // `procedureStartTime` is local wall-clock, so without this the
                  // backend's "UTC" default stamps it as UTC.
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }
              : {}),
            ...(typeof actualTransferCount === "number" &&
            Number.isFinite(actualTransferCount)
              ? { actualTransferCount }
              : {}),
            ...(typeof stepData.notes === "string" && stepData.notes
              ? { notes: stepData.notes }
              : {}),
            clientRequestId,
          };
          // Two cases, not three. The self-report endpoint already decides
          // between "no plan yet", "a plan but no procedure" and "a procedure
          // without a date" — server-side, in one transaction, under a lock on
          // the cycle, and against the version the cycle was actually
          // snapshotted from. Reproducing that decision here meant doing it with
          // less information: a create on a plan whose current version is not the
          // snapshotted one is refused, and a create raced against a still-loading
          // lifecycle query added a second transfer to a cycle that can hold one.
          //
          // The one case it cannot serve is a correction: it answers a replay by
          // returning the dated procedure untouched, which would silently discard
          // an edited date.
          //
          // And one case nobody can serve from here: a procedure row left with no
          // date by an older path. `recordStatus` is read-only over the API — it
          // can be read as `current` or `superseded`, never written — so there is
          // no way to retire such a row and start clean, and the self-report will
          // keep landing on it. Left as is on purpose: the row is empty, so a
          // clinic filing its own transfer on the same cycle simply fills it, and
          // nothing the patient can see is wrong in the meantime. Fixing it would
          // need the backend to expose a way to supersede a procedure.
          // Read fresh rather than from cache. `expectedVersion` comes off this
          // row, and a cached version is stale the moment an earlier correction
          // landed — the backend then refuses with a conflict, the optimistic
          // status rolls back, and the panel drops to the form as if the tap had
          // done nothing. Deciding from a cache that had not answered yet had the
          // same shape of consequence: a second transfer on a cycle that holds
          // one. A save is user-initiated, so one authoritative read is the right
          // price. The query is disabled for a partner-viewed cycle, where the
          // refetch is a no-op and there is nothing for a supporter to correct.
          if (fetBetaLifecycleQuery.error) {
            throw fetBetaLifecycleQuery.error;
          }
          const standingProcedure = isPartnerCycle
            ? undefined
            : (await fetBetaLifecycleQuery.refetch()).data?.procedure;
          // Only her own report is hers to correct — the reasons live with the
          // predicate, which the hub's lock reads too so the screen refuses
          // exactly what the save refuses.
          const isHerOwnReport = isPatientOwnedFETProcedure(standingProcedure);
          if (standingProcedure?.actualDate && plan && isHerOwnReport) {
            await updateFETProcedureReport.mutateAsync({
              planId: plan.id,
              procedureId: standingProcedure.id,
              cycleId: cycle.id,
              expectedVersion: standingProcedure.version,
              ...transferFacts,
            });
          } else {
            await reportFETTransferSelf.mutateAsync({
              cycleId: cycle.id,
              ...transferFacts,
            });
          }
          fetProcedureReportRequestIdRef.current = null;
          // Mark the picked embryos used only when the count she reported
          // says which ones went in: all of them. Read fresh specimens first
          // so an edit does not write again for embryos already marked.
          if (
            typeof actualTransferCount === "number" &&
            Number.isFinite(actualTransferCount) &&
            actualTransferCount > 0
          ) {
            const refreshedSpecimens = await refetchLinkedSpecimens();
            if (refreshedSpecimens.error || !refreshedSpecimens.data) {
              showCryoVaultFollowUpToast(
                "error",
                t("care.cycle_detail.alerts.vault_write_failed_message"),
              );
            } else {
              const snapshotIds = cycle.contextSnapshot?.materialIds ?? [];
              const pickedIds = getPickedEmbryoIdsForTransfer(
                refreshedSpecimens.data,
                cycle.id,
                snapshotIds.length > 0
                  ? snapshotIds
                  : getFrozenSpecimenSelectionForCycle(
                      refreshedSpecimens.data,
                      cycle.id,
                    ).specimenIds,
              );
              const embryoIdsToMarkUsed = getEmbryoIdsToMarkUsed(
                refreshedSpecimens.data,
                cycle.id,
                pickedIds,
                actualTransferCount,
              );
              if (
                needsTransferredEmbryoChoice(
                  refreshedSpecimens.data,
                  cycle.id,
                  pickedIds,
                  actualTransferCount,
                )
              ) {
                // The count does not say which picked embryos went in, so none
                // were marked. Tell her where to record it rather than leave
                // the vault quietly showing them all as frozen.
                showCryoVaultFollowUpToast(
                  "info",
                  t(
                    "care.cycle_detail.alerts.record_transferred_embryos_in_vault",
                    {
                      params: {
                        picked: String(pickedIds.length),
                        transferred: String(actualTransferCount),
                      },
                    },
                  ),
                );
              }
              if (embryoIdsToMarkUsed.length > 0) {
                const occurrenceTime = procedureStartTime ?? "12:00";
                const occurredAt = `${procedureStartDate}T${occurrenceTime}:00.000Z`;
                try {
                  const { failed } = await recordCycleUse.mutateAsync({
                    cycleId: cycle.id,
                    specimenIds: embryoIdsToMarkUsed,
                    occurredAt,
                    hasDecisionAndConsent: true,
                  });
                  if (failed > 0) {
                    showCryoVaultFollowUpToast(
                      "error",
                      t("care.cycle_detail.alerts.vault_write_partial_message"),
                    );
                  }
                } catch (transferError) {
                  logClinicalMutationError(
                    "[CycleDetailScreen]",
                    "/{tenantId}/specimens/{id}/material-operations",
                    transferError,
                  );
                  showCryoVaultFollowUpToast(
                    "error",
                    t("care.cycle_detail.alerts.vault_write_failed_message"),
                  );
                }
              }
            }
          }
          await queryClient.refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          });
          await queryClient.refetchQueries({
            queryKey: ["specimens"],
            type: "all",
          });
          return;
        }

        if (data.status === "completed") {
          const effectiveStart =
            procedureStartDate ?? existingPhase?.startDate ?? today;
          const nextDay = (dateStr: string) => {
            const d = new Date(dateStr + "T00:00:00");
            d.setDate(d.getDate() + 1);
            const y = d.getFullYear();
            const m = (d.getMonth() + 1).toString().padStart(2, "0");
            const day = d.getDate().toString().padStart(2, "0");
            return `${y}-${m}-${day}`;
          };
          // Date in the future ⇒ user is *scheduling* the step, not
          // completing it. Persist the date as startDate but leave endDate
          // null so deriveStepProgress returns "scheduled" and the user
          // can come back to confirm completion on the day.
          const isFutureStart = effectiveStart > today;
          const endDate: string | null = isFutureStart
            ? null
            : nextDay(effectiveStart);

          if (stepId === "trigger_shot" || stepId === "freezing") {
            const isSpermFreezingFreezing =
              stepId === "freezing" && cycle.cycleType === "sperm_freezing";
            const stepDataNotes = isSpermFreezingFreezing
              ? JSON.stringify(stepData)
              : undefined;
            if (existingPhase) {
              await updatePhase({
                cycleId: cycle.id,
                phaseId: existingPhase.id,
                body: {
                  ...(stepId === "freezing" && { endDate }),
                  ...(stepDataNotes != null && { notes: stepDataNotes }),
                  ...(fertilityMetrics && { fertilityMetrics }),
                  ...subObjects,
                },
              });
            } else {
              await createPhase({
                cycleId: cycle.id,
                body: {
                  type: phaseType,
                  startDate: effectiveStart,
                  ...(stepId === "freezing" && { endDate }),
                  ...(stepDataNotes != null && { notes: stepDataNotes }),
                  ...(metricsForCreate?.length && {
                    fertilityMetrics: metricsForCreate,
                  }),
                  ...subObjects,
                },
              });
            }
            /**
             * Carry a lab note from the outcome tab onto the cycle's record,
             * naming the row it belongs to.
             *
             * This fired unconditionally and wrote `{ notes: null }` with no
             * `id`. The reported symptom was that saving frozen eggs summoned
             * "you made it to the end" and that "Got it" came undone. The cause
             * is not the one the comment here used to give: the server routes an
             * outcome body with no id to an INSERT, `outcomes` has no unique
             * index on `cycle_id`, and reads order by `recorded_at` — so every
             * save added a fresh outcome row that stood in front of the
             * acknowledged one. An update keyed by `id` writes only the columns
             * it carries, which is why `acknowledgedAt` — absent from the PATCH
             * surface entirely — survives one.
             *
             * Gating it on a non-empty note then made clearing a note
             * impossible. `outcomeNoteNeedsWriting` decides that; `outcomeWriteFor`
             * supplies the id.
             */
            const freezingNotes =
              typeof stepData.notes === "string" && stepData.notes.trim()
                ? stepData.notes
                : null;
            if (
              stepId === "freezing" &&
              cycle.cycleType !== "sperm_freezing" &&
              outcomeNoteNeedsWriting({
                recorded: cycle.outcome,
                note: freezingNotes,
              })
            ) {
              await updateCycleMutation.mutateAsync({
                cycleId: cycle.id,
                data: {
                  outcome: outcomeWriteFor(cycle.outcome, {
                    notes: freezingNotes,
                  }),
                },
              });
            }
          } else if (existingPhase) {
            const needsNotesJson =
              stepId === "sperm_collection" ||
              (cycle.cycleType === "sperm_freezing" && stepId === "analysis");
            const stepDataNotes = needsNotesJson
              ? JSON.stringify(stepData)
              : undefined;
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: {
                startDate:
                  procedureStartDate ??
                  existingPhase.startDate ??
                  effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate,
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics: metricsForCreate,
                ...subObjects,
              },
            });
          } else {
            const needsNotesJson =
              stepId === "sperm_collection" ||
              (cycle.cycleType === "sperm_freezing" && stepId === "analysis");
            const stepDataNotes = needsNotesJson
              ? JSON.stringify(stepData)
              : undefined;
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: phaseType,
                startDate: effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate,
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics: metricsForCreate,
                ...subObjects,
              },
            });
          }
        } else if (data.status === "scheduled") {
          // A scheduled appointment records the intent, not the outcome —
          // even if the chosen day is today or in the past. Persist the
          // date as the phase startDate and leave endDate null so
          // `deriveStepProgress` reads it as "scheduled" and the hub keeps
          // offering the log-outcome affordance instead of auto-advancing.
          const effectiveStart =
            procedureStartDate ?? existingPhase?.startDate ?? today;
          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: {
                startDate: effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate: null,
              },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: phaseType,
                startDate: effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate: null,
              },
            });
          }
        } else if (data.status === "skipped") {
          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: { startDate: today, endDate: today },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: { type: phaseType, startDate: today, endDate: today },
            });
          }
          // Cascade: skipping the transfer ("freezing all embryos") leaves
          // nothing to support with luteal meds, so the luteal_phase step
          // is dead. Auto-write a matching skip so the cycle reads as done.
          if (stepId === "transfer") {
            const lutealPhase = (activeCycle.phases ?? []).find(
              (p: Phase) => p.type === "luteal_phase",
            );
            if (lutealPhase) {
              await updatePhase({
                cycleId: cycle.id,
                phaseId: lutealPhase.id,
                body: { startDate: today, endDate: today },
              });
            } else {
              await createPhase({
                cycleId: cycle.id,
                body: {
                  type: "luteal_phase",
                  startDate: today,
                  endDate: today,
                },
              });
            }
          }
        } else if (data.status === "pending") {
          if (stepId === "trigger_shot") {
            if (existingPhase) {
              await updatePhase({
                cycleId: cycle.id,
                phaseId: existingPhase.id,
                body: { triggerShot: null },
              });
            }
          } else if (stepId === "freezing") {
            if (existingPhase) {
              const keepMetrics = (existingPhase.fertilityMetrics ?? [])
                .filter(
                  (m: ApiFertilityMetric) =>
                    m.type &&
                    m.type !== "m2_count" &&
                    m.type !== "frozen_embryo",
                )
                .map((m: ApiFertilityMetric) => ({
                  id: m.id,
                  type: m.type!,
                  value: m.value as number | null,
                }));
              await updatePhase({
                cycleId: cycle.id,
                phaseId: existingPhase.id,
                body: {
                  fertilityMetrics: keepMetrics.length > 0 ? keepMetrics : [],
                },
              });
            }
            await updateCycleMutation.mutateAsync({
              cycleId: cycle.id,
              data: { outcome: { notes: null } },
            });
          } else if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: { endDate: null },
            });
          }
        } else if (data.data) {
          if (
            cycle.cycleType === "fet" &&
            stepId === "transfer" &&
            !procedureStartDate
          ) {
            throw new Error("A transfer date is required before saving");
          }
          const needsNotesJson =
            stepId === "sperm_collection" ||
            (cycle.cycleType === "sperm_freezing" && stepId === "analysis");
          const stepDataNotes = needsNotesJson
            ? JSON.stringify(stepData)
            : undefined;
          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: {
                ...(procedureStartDate && {
                  startDate: procedureStartDate,
                }),
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics: metricsForCreate,
                ...subObjects,
              },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: phaseType,
                startDate: procedureStartDate ?? today,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics: metricsForCreate,
                ...subObjects,
              },
            });
          }
        }
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
        if (!options?.skipInventorySync) {
          /*
           * The date this save established, so the sync is never left without
           * one.
           *
           * The egg-freezing Outcome step carries no date field at all, and the
           * prefill it would otherwise read is a memo from before this write —
           * so a patient who fills Outcome before ever completing the retrieval
           * step got no rows, no error and no explanation, while saving the
           * same step a second time worked. Intermittent by construction.
           */
          await syncCycleInventory(
            stepId,
            stepData,
            existingPhase?.startDate ?? today,
          );
        }
        // A frozen-oocyte cycle: mark the eggs picked for this cycle thawed.
        // The fertilized count itself is not written per specimen; the
        // fertilization phase already carries `fertilized_oocytes` as a
        // fertility metric (the lab funnel reads it), and the CryoVault
        // derives the cohort's fertilized total from that same metric across
        // consuming cycles. Idempotent per specimen so a re-save does not
        // duplicate writes. Best-effort: a failure here does not undo the
        // step save; a correction can still be recorded from the CryoVault
        // lifecycle dialog.
        if (
          usingFrozenOocytes &&
          cycle?.id &&
          stepId === "fertilization" &&
          typeof stepData.eggsFertilized === "number" &&
          stepData.eggsFertilized > 0
        ) {
          const materialIds = cycle.contextSnapshot?.materialIds ?? [];
          if (materialIds.length > 0) {
            // Split materialIds into two paths: members of an existing
            // `oocyte-cohort` record get their thaw count patched on the
            // cohort (individual specimen POSTs return 404 for historical
            // cohort members); everything else writes `thaw` on the
            // specimen itself.
            const materialIdSet = new Set(materialIds);
            const cohortMemberIds = new Set<string>();
            const cohortsUpdated: Promise<unknown>[] = [];
            for (const cohort of oocyteCohorts) {
              const matchedMembers = cohort.members.filter((member) =>
                materialIdSet.has(member.id),
              );
              if (matchedMembers.length === 0) continue;
              matchedMembers.forEach((m) => cohortMemberIds.add(m.id));
              // Write the thaw count as an absolute value, not an increment.
              // The step-save can be replayed (retry after a failed write,
              // an accidental double-tap, a re-open of the same edit), and
              // an additive write would count the same eggs a second time.
              // Using max() preserves any prior thaws recorded outside this
              // cycle while making this cycle's write idempotent: on retry,
              // max(N, N) = N. Never exceeds the cohort's original size.
              const nextNumberThawed = Math.min(
                cohort.numberFrozen ?? matchedMembers.length,
                Math.max(cohort.numberThawed ?? 0, matchedMembers.length),
              );
              cohortsUpdated.push(
                updateOocyteCohort.mutateAsync({
                  cohortId: cohort.id,
                  data: {
                    providerId: cohort.providerId ?? null,
                    originCycleId: cohort.originCycleId ?? null,
                    retrievalDate: cohort.retrievalDate ?? null,
                    notes: cohort.notes ?? null,
                    status: "partially_used",
                    reportedFacts: {
                      numberThawed: nextNumberThawed,
                    },
                  },
                }),
              );
            }
            const individualSpecimenIds = materialIds.filter(
              (id) => !cohortMemberIds.has(id),
            );
            // Awaited before the save handler resolves, so the step-save
            // does not report success while the vault still shows the eggs
            // as fully frozen. A thaw-write failure is logged and does not
            // throw: the fertilization phase itself has already been
            // written, and unwinding that record over a downstream
            // reconciliation error would confuse the timeline. She can
            // retry the write from the CryoVault lifecycle dialog.
            try {
              await Promise.all([
                individualSpecimenIds.length > 0
                  ? recordCycleThaw.mutateAsync({
                      cycleId: cycle.id,
                      specimenIds: individualSpecimenIds,
                      hasDecisionAndConsent: true,
                    })
                  : Promise.resolve(),
                ...cohortsUpdated,
              ]);
            } catch (thawError) {
              console.error(
                "[CycleDetailScreen] frozen-oocyte thaw write failed",
                thawError,
              );
              // Otherwise the vault keeps showing these eggs frozen with no
              // word to her; the toast leads to where she can fix it.
              showCryoVaultFollowUpToast(
                "error",
                t("care.cycle_detail.alerts.thaw_not_recorded_in_vault"),
              );
            }
          }
        }
        // Offer to seed the CryoVault with the embryos she just recorded
        // as frozen. Only IVF/ICSI produces embryos through this step;
        // the count comes straight from the step's own `embryosFrozen`
        // field. The dialog itself only carries what she typed — no
        // invented defaults for PGT, fertilization method, or day of
        // development. Blank stays blank, and the intake modal will keep
        // Save disabled until she fills the required fields.
        if (
          stepId === "embryo_development" &&
          (cycleType === "ivf" || cycleType === "icsi") &&
          typeof stepData.embryosFrozen === "number" &&
          stepData.embryosFrozen > 0
        ) {
          setEmbryoAddPromptCount(stepData.embryosFrozen);
        }
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["cycles"], type: "all" }),
          queryClient.refetchQueries({
            queryKey: ["oocyte-cohorts"],
            type: "all",
          }),
          queryClient.refetchQueries({
            queryKey: ["specimens"],
            type: "all",
          }),
        ]);
      } catch (e) {
        console.error(
          "[CycleDetailScreen] handleUpdateStepProgress failed for step",
          stepId,
          "with data",
          data,
          "→",
          e,
        );
        // A preceding write may have committed even when a later coupled
        // write failed (or its response was lost). Reconcile the phase query
        // before handing the error back to the hub so a retry can PATCH the
        // confirmed phase instead of POSTing a duplicate.
        await queryClient
          .refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          })
          .catch(() => undefined);
        // Re-throw so the hub card can revert its optimistic state and surface
        // the failure instead of showing the step as saved.
        throw e;
      }
    },
    [
      syncCycleInventory,
      createAppointment,
      updateAppointment,
      deleteAppointment,
      patient?.counterparties,
      cycle,
      hubPhases,
      activeCycle.phases,
      createPhase,
      updatePhase,
      patientId,
      tenantId,
      updateCycleMutation,
      queryClient,
      fetPlanForCycleQuery,
      reportFETTransferSelf,
      updateFETProcedureReport,
      isPartnerCycle,
      fetBetaLifecycleQuery,
      fetProcedureReportRequestIdRef,
      recordCycleUse,
      refetchLinkedSpecimens,
      usingFrozenOocytes,
      recordCycleThaw,
      oocyteCohorts,
      updateOocyteCohort,
      cycleType,
      showCryoVaultFollowUpToast,
      showToast,
      t,
    ],
  );

  const handleRecordEggRetrievalOutcome = useCallback(
    async (saved: SpecimenPrefill) => {
      const retrievalData: Record<string, string | number | boolean | null> =
        {};
      const retrievalDate = saved.retrievalDate || saved.freezeDate;
      if (retrievalDate) retrievalData.procedureDate = retrievalDate;
      if (saved.totalRetrieved != null) {
        retrievalData.eggsCollected = saved.totalRetrieved;
      }
      /**
       * The rest of what the retrieval found.
       *
       * `retrieved = mature + immature + degenerated`, and the first two of
       * those already travelled this bridge — mature by way of the Outcome tab
       * below. These two went only into each frozen row's `maturityStage`,
       * which cannot hold them: the intake writes one row per frozen oocyte and
       * an immature or degenerated one is never frozen, so past the frozen
       * count there was no row and the figures were gone on reopening. They are
       * kept apart rather than summed because a clinic reads them as different
       * findings.
       *
       * They belong to the retrieval, not the Outcome tab, and
       * `buildFertilityMetrics` round-trips each metric row's `id` from the
       * existing phase — which is what keeps a second save from adding a
       * duplicate row, since `(phase_id, type)` carries no unique constraint.
       */
      if (saved.immatureMI != null) {
        retrievalData.immatureMI = saved.immatureMI;
      }
      if (saved.degenerated != null) {
        retrievalData.degenerated = saved.degenerated;
      }

      /*
       * What she took back, translated into the fields the phase records.
       *
       * Split by step because each write should only speak for the counts it
       * owns — the retrieval has three, the freezing tab has `matureEggs`.
       *
       * Not a safety net, though: `STEP_TO_PHASE_TYPE` maps both steps onto the
       * same `egg_retrieval` phase, so either write could reach any of the four
       * rows. Mis-routing a count would erase it, not quietly do nothing. The
       * ownership lives in `OOCYTE_COUNT_TARGET` for that reason.
       */
      const nowCleared = new Set(saved.clearedCounts ?? []);
      /*
       * What she has withdrawn: held unknown when the form opened, no
       * longer marked, and with no figure typed in its place. A null row still
       * reads back as "I don't know", so leaving one would re-select the toggle
       * she just turned off — with no number to type as a way out.
       */
      const withdrawn = (specimenPrefill?.clearedCounts ?? []).filter(
        (metric) => !nowCleared.has(metric) && saved[metric] == null,
      );

      const fieldsFor = (
        metrics: readonly OocyteCountMetric[],
        step: "egg_retrieval" | "freezing",
      ) => [
        ...new Set(
          metrics
            .map((metric) => OOCYTE_COUNT_TARGET[metric])
            .filter((target) => target.step === step)
            .map((target) => target.field),
        ),
      ];
      const clearedForRetrieval = fieldsFor(
        saved.clearedCounts ?? [],
        "egg_retrieval",
      );
      const clearedForFreezing = fieldsFor(
        saved.clearedCounts ?? [],
        "freezing",
      );
      const withdrawnForRetrieval = fieldsFor(withdrawn, "egg_retrieval");
      const withdrawnForFreezing = fieldsFor(withdrawn, "freezing");

      if (
        Object.keys(retrievalData).length > 0 ||
        clearedForRetrieval.length ||
        withdrawnForRetrieval.length
      ) {
        await handleUpdateStepProgress(
          "egg_retrieval",
          {
            status: "completed",
            data: retrievalData,
          },
          {
            clearedFields: clearedForRetrieval,
            // The intake has just written the specimens; the sync must
            // not write them again from the count this echoes back.
            skipInventorySync: true,
            removedFields: withdrawnForRetrieval,
          },
        );
      }

      // Egg freezing's Outcome tab shares the retrieval phase. Once the
      // specimen intake has recorded frozen eggs, complete that tab from the
      // same saved values instead of asking the patient to enter them again.
      const freezingData: Record<string, string | number | boolean | null> = {};
      if (saved.matureMII != null) {
        freezingData.matureEggs = saved.matureMII;
      }
      if (saved.numberFrozen != null) {
        freezingData.eggsFrozen = saved.numberFrozen;
      }
      if (saved.freezeDate) {
        freezingData.freezeDate = saved.freezeDate;
      }
      if (
        Object.keys(freezingData).length > 0 ||
        clearedForFreezing.length ||
        withdrawnForFreezing.length
      ) {
        await handleUpdateStepProgress(
          "freezing",
          {
            status: "completed",
            data: freezingData,
          },
          {
            clearedFields: clearedForFreezing,
            // The intake has just written the specimens; the sync must
            // not write them again from the count this echoes back.
            skipInventorySync: true,
            removedFields: withdrawnForFreezing,
          },
        );
      }

      /**
       * Take her to what she just recorded.
       *
       * The specimen intake is opened from the retrieval step, and it leaves her
       * there — while the counts she entered are displayed on the journey's last
       * step, which is also where she can correct them. So the save appeared to
       * do nothing, and the retrieval step went on offering to record results
       * she had already given.
       *
       * The last step of the flow rather than a step id: egg and sperm freezing
       * end on `freezing`, embryo freezing has no such step and ends on embryo
       * development or PGT.
       */
      const closingStepId = steps[steps.length - 1]?.id;
      if (closingStepId) {
        treatmentHubRef.current?.selectStep(closingStepId);
      }
    },
    [handleUpdateStepProgress, steps, specimenPrefill?.clearedCounts],
  );

  const handleCompletePreservation = useCallback(async () => {
    if (!cycle?.id) return;

    // Transfer-bearing cycles enter the two-week wait — DO NOT close. A
    // freeze-all/no-transfer cycle instead closes as complete below; it must
    // never receive a synthetic luteal phase or beta date.
    if (treatmentGoal === "pregnancy") {
      try {
        const today = toLocalDateString(new Date());
        const latestPhases =
          queryClient.getQueryData<ApiPhase[]>(
            phaseKeys.cycle(tenantId ?? "", cycle.id),
          ) ?? hubPhases;
        const performedProcedure = findLatestPerformedProcedurePhase(
          latestPhases.filter(
            (phase) => phase.startDate && phase.startDate <= today,
          ),
          ["transfer", "insemination"],
        );
        /*
         * No confirmed transfer, so there is no wait to enter — and closing is
         * not this function's decision to take. It used to close the cycle here
         * outright: `status: "closed"`, today's `endDate`, a null outcome and a
         * reset to Care overview, with nothing asked. Every caller now gates on
         * `hasConfirmedTransfer` before reaching this, but that gate reads the
         * render's phases while this reads a fresher cache, so the two can
         * disagree — a transfer deleted between render and tap landed straight
         * in that close. Ending a cycle goes through the dialog that asks.
         */
        if (!hasConfirmedPregnancyProcedure(latestPhases)) {
          setEndCycleMode("complete");
          setEndCycleVisible(true);
          return;
        }
        if (!performedProcedure?.startDate) {
          showToast({
            variant: "info",
            message: t("care.treatment_hub.waiting_requires_performed_message"),
          });
          return;
        }
        const existingLuteal = latestPhases.find(
          (p) => p.type === "luteal_phase",
        );
        // Anchor the wait window on the transfer / insemination date —
        // beta hCG tests are scheduled relative to that, not relative to
        // when the user happens to tap "Enter Waiting Phase". For IVF /
        // ICSI / FET we look at the `transfer` phase; for IUI / OI it's
        // `insemination`. A missing performed procedure is blocked above.
        const transferDate = performedProcedure.startDate;
        const start = transferDate;
        // Default test date = transfer + 14 days. If the user previously
        // set a real test date (window ≥ 7 days), preserve it so repeated
        // clicks don't overwrite a customized choice.
        const defaultEnd = (() => {
          const d = new Date(start + "T00:00:00");
          d.setDate(d.getDate() + 14);
          // toLocalDateString avoids the toISOString() UTC shift that
          // would silently subtract a day for users east of UTC and
          // produce a 13-day window instead of 14.
          return toLocalDateString(d);
        })();
        const existingWindow =
          existingLuteal?.startDate && existingLuteal?.endDate
            ? Math.round(
                (new Date(existingLuteal.endDate + "T00:00:00").getTime() -
                  new Date(existingLuteal.startDate + "T00:00:00").getTime()) /
                  (1000 * 60 * 60 * 24),
              )
            : 0;
        const endDate =
          existingWindow >= 7 ? existingLuteal!.endDate! : defaultEnd;
        if (existingLuteal?.id) {
          await updatePhase({
            cycleId: cycle.id,
            phaseId: existingLuteal.id,
            body: { startDate: start, endDate },
          });
        } else {
          await createPhase({
            cycleId: cycle.id,
            body: {
              type: "luteal_phase",
              startDate: start,
              endDate,
            },
          });
        }
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
      } catch {
        await queryClient
          .refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          })
          .catch(() => undefined);
        showToast({
          variant: "error",
          message: t("care.treatment_hub.save_failed_message"),
        });
      }
      return;
    }

    // Preservation cycles — terminal close, navigate home.
    try {
      // Routed through `useCompleteCycle` rather than a bare status PATCH so the
      // close creates an outcome row. Without one there is nothing to
      // acknowledge, and the ending never reaches Home's closing message: a
      // sperm-freezing cycle closed this way was silently ending with no record
      // of *when*, unlike the same cycle closed through CompleteCycleModal.
      await createSnapshot.mutateAsync(cycle.id).catch(() => {
        // Best effort: a missing snapshot degrades the past-cycle view but must
        // not block the close.
      });
      // Reuse the existing outcome id when there is one. The cycle/outcome
      // relation is 1:1, so omitting it forces an INSERT and the server rejects
      // it with a unique-constraint error — see `useRecordCycleOutcome`.
      await completeCycle.mutateAsync({
        cycleId: cycle.id,
        outcomeId: cycleOutcome?.id,
        // A PATCH replaces the whole outcome sub-object, so anything already
        // stored has to be sent back or it is destroyed.
        existingNotes: cycleOutcome?.notes,
        existingDateRecorded: cycleOutcome?.dateRecorded,
      });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["active-cycle"], type: "all" }),
        queryClient.refetchQueries({ queryKey: ["cycles"], type: "all" }),
        queryClient.refetchQueries({ queryKey: ["treatment-summaries"] }),
      ]);
      navigation.reset({
        index: 0,
        routes: [{ name: "CareOverview" }],
      });
    } catch {
      // Keep the completion view in place. Its CTA remains available so the
      // patient can retry the close operation without being routed to a
      // screen that implies the cycle was completed.
      showToast({
        variant: "error",
        message: t("care.treatment_hub.save_failed_message"),
      });
    }
  }, [
    cycle,
    treatmentGoal,
    hubPhases,
    createPhase,
    updatePhase,
    completeCycle,
    createSnapshot,
    cycleOutcome?.id,
    cycleOutcome?.notes,
    cycleOutcome?.dateRecorded,
    queryClient,
    navigation,
    tenantId,
    showToast,
    t,
  ]);

  // Persist the user's chosen pregnancy test date onto luteal_phase.endDate.
  // Triggered from WaitingPhaseView's date picker.
  const handleSetTestDate = useCallback(
    async (date: string) => {
      if (!cycle?.id || !date) return;
      try {
        setIsSavingTestDate(true);
        setPendingTestDate(date);
        setTestDateSaveError(null);
        const today = toLocalDateString(new Date());
        const latestPhases =
          queryClient.getQueryData<ApiPhase[]>(
            phaseKeys.cycle(tenantId ?? "", cycle.id),
          ) ?? hubPhases;
        const existing = latestPhases.find((p) => p.type === "luteal_phase");
        if (existing?.id) {
          await updatePhase({
            cycleId: cycle.id,
            phaseId: existing.id,
            body: {
              startDate: existing.startDate ?? today,
              endDate: date,
            },
          });
        } else {
          await createPhase({
            cycleId: cycle.id,
            body: {
              type: "luteal_phase",
              startDate: today,
              endDate: date,
            },
          });
        }
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
        setPendingTestDate(null);
      } catch {
        await queryClient
          .refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          })
          .catch(() => undefined);
        setTestDateSaveError(
          t("care.treatment_hub.waiting_phase.test_date_save_failed"),
        );
      } finally {
        setIsSavingTestDate(false);
      }
    },
    [cycle?.id, hubPhases, createPhase, updatePhase, queryClient, tenantId, t],
  );

  // Submit the beta result modal. FET writes to the accepted typed beta order
  // and remains an open result series; older cycle types retain their typed
  // test-result plus outcome compatibility path.
  const handleConfirmBetaResult = useCallback(
    async ({
      type,
      resultStatus,
      pregnancyOutcome,
      miscarriageOutcome,
      biochemicalPregnancyObserved,
      notes,
      betaHcgValue,
      collectionAt,
      collectionTimeUnknown,
      timeZone,
      sourceDocumentId,
      mode,
      transferDate,
    }: {
      mode: "cycle_outcome" | "fet_beta_order";
      type: "pregnancy" | "no_pregnancy" | "miscarriage" | null;
      resultStatus: CycleOutcomeResultStatus;
      pregnancyOutcome?: "live_birth" | "multiple_live_birth" | null;
      miscarriageOutcome?:
        | "complete_miscarriage"
        | "incomplete_miscarriage"
        | "missed_miscarriage"
        | "still_birth"
        | "recurrent_miscarriage"
        | "chemical_pregnancy"
        | "ectopic_pregnancy"
        | "anembryonic_pregnancy"
        | null;
      biochemicalPregnancyObserved?: boolean | null;
      notes: string;
      betaHcgValue?: number | null;
      collectionAt?: string | null;
      collectionTimeUnknown?: boolean;
      timeZone?: string;
      sourceDocumentId?: string | null;
      transferDate?: string;
    }) => {
      if (!cycle?.id) return;
      try {
        const performedProcedure = findLatestPerformedProcedurePhase(
          hubPhases,
          ["transfer", "insemination"],
        );
        if (betaHcgValue == null) {
          throw new Error("Beta hCG value is required");
        }
        if (mode === "fet_beta_order") {
          if (cycleType !== "fet") {
            throw new Error("FET beta results require an FET cycle");
          }
          if (fetBetaLifecycleQuery.isPending) {
            throw new FETBetaOrderLookupPendingError();
          }
          if (fetBetaLifecycleQuery.error) {
            throw fetBetaLifecycleQuery.error;
          }
          let lifecycle = fetBetaLifecycleQuery.data;
          // No transfer on record: the dialog asked for its day, so stand the
          // transfer up from her report first. The self-report endpoint decides
          // between "no plan yet" and "a plan without a procedure" server-side.
          if (!lifecycle && transferDate) {
            const transferRequestId =
              fetProcedureReportRequestIdRef.current ?? createIdempotencyKey();
            fetProcedureReportRequestIdRef.current = transferRequestId;
            await reportFETTransferSelf.mutateAsync({
              cycleId: cycle.id,
              actualDate: transferDate,
              clientRequestId: transferRequestId,
            });
            fetProcedureReportRequestIdRef.current = null;
            lifecycle = (await fetBetaLifecycleQuery.refetch()).data ?? null;
          }
          if (!lifecycle) {
            throw new FETBetaOrderUnavailableError();
          }
          let order = selectRecordableFETBetaOrder(lifecycle);
          if (!order) {
            const orderRequestId =
              fetBetaOrderRequestIdRef.current ?? createIdempotencyKey();
            fetBetaOrderRequestIdRef.current = orderRequestId;
            order = await createFETBetaOrder.mutateAsync({
              cycleId: cycle.id,
              procedureId: lifecycle.procedure.id,
              status: "collected",
              orderedAt: new Date().toISOString(),
              clientRequestId: orderRequestId,
            });
          }
          const clientRequestId =
            fetBetaResultRequestIdRef.current ?? createIdempotencyKey();
          fetBetaResultRequestIdRef.current = clientRequestId;
          await recordFETBetaResult.mutateAsync({
            cycleId: cycle.id,
            procedureId: lifecycle.procedure.id,
            orderId: order.id,
            value: betaHcgValue,
            collectionAt,
            collectionTimeUnknown,
            timeZone,
            sourceDocumentId,
            clientRequestId,
          });
          await queryClient.refetchQueries({
            queryKey: fetBetaLifecycleQueryKey(patientId, cycle.id),
            type: "all",
          });
        }
        // The FET beta ledger is the value's home on an FET cycle, so no legacy
        // test-result row is written for it — storing the number twice would let
        // the two disagree. The outcome below is still recorded either way: it is
        // what ends the wait and lets the cycle close, and skipping it left an
        // FET cycle sitting on a countdown with its result already in hand.
        let sourceRecordId: string | null = null;
        if (mode !== "fet_beta_order") {
          sourceRecordId = betaTestResult?.id ?? null;
          if (!sourceRecordId) {
            const result = await createBetaTestResult.mutateAsync(
              buildBetaTestResultPayload({
                cycleId: cycle.id,
                phaseId:
                  hubPhases.find((phase) => phase.type === "luteal_phase")
                    ?.id ?? null,
                value: betaHcgValue,
                date: toLocalDateString(new Date()),
                notes,
              }),
            );
            sourceRecordId = result.data.id;
            setBetaTestResult({ id: sourceRecordId, value: betaHcgValue });
          } else if (betaTestResult?.value !== betaHcgValue) {
            // The outcome names this row as its numeric source, so a corrected
            // value has to reach the row too — otherwise the record says 120 in
            // the outcome and still says 12 in the result it points at.
            await updateBetaTestResult.mutateAsync({
              testResultId: sourceRecordId,
              data: buildBetaTestResultUpdate({ value: betaHcgValue, notes }),
            });
            setBetaTestResult({ id: sourceRecordId, value: betaHcgValue });
          }
        }
        // One request id per record being written. A retry with a corrected
        // value keeps it, so a first attempt whose response was lost is
        // recognised as a replay rather than stored a second time.
        const requestId = resolveOutcomeWriteRequestId(
          betaOutcomeRequestIdRef.current,
          outcomeWriteTarget({ outcomeId: cycleOutcome?.id ?? null }),
          createIdempotencyKey,
        );
        betaOutcomeRequestIdRef.current = requestId;
        // A state she reported is final: a lab value may not undo a loss or a
        // birth she has already recorded. Only the update-pregnancy step changes
        // the state. The value itself is still saved either way.
        const protectedEnding = endingAfterBetaWrite(cycleOutcome, {
          type: betaWritableEnding(type) ?? null,
          pregnancyOutcome,
          miscarriageOutcome,
        });
        await recordCycleOutcome.mutateAsync({
          cycleId: cycle.id,
          outcomeId: cycleOutcome?.id ?? null,
          type: protectedEnding.type,
          resultStatus,
          sourceKind: PATIENT_BETA_OUTCOME_SOURCE_KIND,
          sourceRecordId,
          sourceDocumentId: sourceDocumentId ?? null,
          clientRequestId: requestId.requestId,
          procedureType:
            cycleType === "iui" || cycleType === "oi"
              ? "insemination"
              : "embryo_transfer",
          procedureModality:
            cycleType === "fet"
              ? "fet"
              : cycleType === "iui"
                ? "iui"
                : cycleType === "oi"
                  ? "oi"
                  : cycleType === "icsi"
                    ? "icsi"
                    : "ivf",
          procedureDate: performedProcedure?.startDate ?? null,
          procedureTime: performedProcedure?.startTime ?? null,
          pregnancyOutcome: protectedEnding.pregnancyOutcome,
          miscarriageOutcome: protectedEnding.miscarriageOutcome,
          biochemicalPregnancyObserved: biochemicalPregnancyObserved ?? null,
          notes,
          // "Update result" reaches this a second time — a repeat beta after a
          // borderline or first positive. That repeat is a correction, which
          // replaces the whole record, so the earlier value has to be sent back or
          // it is gone: the beta number lives only in `notes`.
          existingNotes: cycleOutcome?.notes,
          existingDateRecorded: cycleOutcome?.dateRecorded,
        });
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: ["active-cycle"],
            type: "all",
          }),
          queryClient.refetchQueries({ queryKey: ["cycles"], type: "all" }),
          // Both branches above wrote a beta the canonical series will now
          // carry — the FET one through its order, every other modality
          // through the test-result row. One refetch covers both, because the
          // series is the one place either of them is read back from.
          queryClient.refetchQueries({
            queryKey: betaHcgSeriesQueryKey(patientId, cycle.id),
            type: "all",
          }),
        ]);
        // Cleared only once the whole write has landed. Clearing them inside the
        // FET block meant a failed outcome write — which keeps the modal open and
        // invites a retry — re-entered the beta block with fresh ids and appended
        // a second `current` result for one draw. Nothing refuses that.
        fetBetaOrderRequestIdRef.current = null;
        fetBetaResultRequestIdRef.current = null;
        setBetaResultError(null);
        // Modal stays open and transitions to its empathetic confirmation
        // step. Navigation happens when the user taps Done — see
        // handleBetaResultComplete.
      } catch (e) {
        // Keep API details out of patient-facing copy. The modal remains open
        // with the entered result so this same outcome write can be retried —
        // except for a supporter, whose refusal no retry can fix, so the message
        // names whose result it is instead.
        setBetaResultError(
          betaResultErrorMessage(
            e,
            t,
            {
              isSupporting: isPartnerCycle,
              cycleOwnerName: partnerDisplayName,
            },
            cycleOutcome,
          ),
        );
        throw e;
      }
    },
    [
      cycle?.id,
      // The whole record now, not three of its fields: its provenance decides
      // what a refusal may be attributed to, and depending on the object covers
      // the id, notes and date the write itself reads.
      cycleOutcome,
      cycleType,
      hubPhases,
      betaTestResult,
      createBetaTestResult,
      updateBetaTestResult,
      createFETBetaOrder,
      recordFETBetaResult,
      recordCycleOutcome,
      queryClient,
      patientId,
      fetBetaLifecycleQuery,
      fetBetaOrderRequestIdRef,
      fetBetaResultRequestIdRef,
      fetProcedureReportRequestIdRef,
      reportFETTransferSelf,
      isPartnerCycle,
      partnerDisplayName,
      t,
    ],
  );

  // User finished reading the confirmation content and tapped Done. Dismiss the
  // modal and leave them on the cycle, where the result they just recorded is
  // now shown against it along with the action that closes it.
  //
  // This used to `navigation.reset` to Care home. Recording also closed the
  // cycle at the time, so the patient went from typing a number to a wiped
  // navigation stack in one step, with no way back to the cycle they had just
  // ended — the abruptness this flow was reworked to fix.
  const handleBetaResultComplete = useCallback(() => {
    setBetaResultVisible(false);
    setBetaResultError(null);
    setBetaTestResult(null);
    fetBetaOrderRequestIdRef.current = null;
    fetBetaResultRequestIdRef.current = null;
    betaOutcomeRequestIdRef.current = null;
  }, []);

  /**
   * Close a cycle whose ending is already recorded.
   *
   * Opens the shared end-cycle modal rather than closing directly, so a cycle is
   * ended through one flow wherever the patient starts it — here, the Care page
   * CTA, or the hero on Home. The modal preselects the recorded ending, so this
   * confirms it instead of asking for it again, and it owns the snapshot and the
   * navigation that follow.
   */
  const handleCloseRecordedCycle = useCallback(() => {
    setEndCycleVisible(true);
  }, []);

  const { height: windowHeight } = useWindowDimensions();
  const scrollRef = useRef<ScrollView>(null);
  /** Where the treatment hub starts, measured rather than assumed. */
  const [hubOffsetY, setHubOffsetY] = useState<number | null>(null);
  /**
   * The step an arrival asked for, kept once the param is spent.
   *
   * The param is cleared as soon as it is read, for two reasons that pull the
   * same way. It has to be cleared, or the focus effect fires on every return to
   * this screen — from Documents, from lab results, from the cryo vault, all
   * reachable further down this very page — and drags her back to the hub,
   * losing her place. And it cannot merely be read, or the hub would lose the
   * step she came for the moment it goes. So the request outlives the param, and
   * only the scroll is one-shot.
   */
  const [hubStepRequest, setHubStepRequest] = useState<string | null>(null);
  /**
   * Counts arrivals, because the id alone cannot.
   *
   * The request deliberately outlives the param (see above), so a second arrival
   * for the same step sets `hubStepRequest` to the value it already holds — no
   * prop change, nothing for the hub to react to. She pressed "record my beta",
   * came back to the hub, tapped another tab, pressed it again, and the page
   * scrolled to a hub still sitting on the tab she had chosen.
   */
  const [hubStepRequestCount, setHubStepRequestCount] = useState(0);
  const pendingHubScrollRef = useRef(false);

  /**
   * Home's pending-beta CTA promises to schedule the test, not merely show the
   * cycle. Consume that arrival once the cycle's editable waiting surface is
   * ready and open the same prefilled appointment modal as the test-date row.
   * FET keeps its clinic-owned beta order read-only, so Home does not send this
   * focus for that cycle type.
   */
  useEffect(() => {
    if (
      route.params?.focus !== "beta" ||
      cycleType === "fet" ||
      !isInWaitingPhase
    ) {
      return;
    }
    openBetaAppointmentModal();
    navigation.setParams({ focus: undefined });
  }, [
    cycleType,
    isInWaitingPhase,
    navigation,
    openBetaAppointmentModal,
    route.params?.focus,
  ]);

  useEffect(() => {
    const focus = route.params?.focus;
    if (
      focus !== "outcome" &&
      focus !== "screening" &&
      focus !== "sperm_collection" &&
      focus !== "analysis" &&
      focus !== "transfer"
    ) {
      return;
    }
    setHubStepRequest(focus);
    setHubStepRequestCount((count) => count + 1);
    pendingHubScrollRef.current = true;
    navigation.setParams({ focus: undefined });
  }, [navigation, route.params?.focus]);

  // Not animated: she asked to be somewhere, and watching the page travel there
  // is not part of the answer.
  useFocusEffect(
    useCallback(() => {
      if (!pendingHubScrollRef.current || hubOffsetY === null) return;
      pendingHubScrollRef.current = false;
      scrollRef.current?.scrollTo({
        y: Math.max(0, hubOffsetY - 8),
        animated: false,
      });
    }, [hubOffsetY]),
  );

  const todayStr = useMemo(() => getTodayString(), []);
  // ── API hooks (replacing hardcoded data) ──
  const { data: apiMedications } = usePatientMedications();

  // Reconcile phase.startDate against the earliest medication actually
  // attached to that phase. ensurePhaseExists has to stamp *something*
  // when it creates a phase from an "Add medication" tap, and `today` is
  // wrong whenever the patient is back-filling a regimen that began
  // earlier (or before the cycle was even registered in the app).
  // Pulling the floor down to the earliest med makes "Started X · Day N"
  // in the hub, calendar, and timeline reflect the patient's truth.
  useEffect(() => {
    if (!cycle?.id || !apiMedications || hubPhases.length === 0) return;
    for (const phase of hubPhases) {
      if (!phase.id) continue;
      const meds = apiMedications.filter(
        (m) =>
          m.cycleId === cycle.id &&
          m.phaseId === phase.id &&
          typeof m.startDate === "string" &&
          m.startDate.length > 0,
      );
      if (meds.length === 0) continue;
      let earliest = meds[0]?.startDate ?? null;
      for (const m of meds) {
        if (m.startDate && (!earliest || m.startDate < earliest)) {
          earliest = m.startDate;
        }
      }
      if (earliest && (!phase.startDate || earliest < phase.startDate)) {
        updatePhase({
          cycleId: cycle.id,
          phaseId: phase.id,
          body: { startDate: earliest },
        }).catch((err) => {
          console.warn(
            "[CycleDetailScreen] phase startDate backfill failed:",
            err,
          );
        });
      }
    }
  }, [cycle?.id, apiMedications, hubPhases, updatePhase]);

  const handleEditMedicationById = useCallback(
    (medicationId: string, fallbackName?: string) => {
      const pm = apiMedications?.find((p) => p.id === medicationId);
      if (!pm) return;
      const timeTaken = Array.isArray(pm.timeTaken)
        ? (pm.timeTaken as string[])
        : [];
      setEditMed({
        id: pm.id,
        name: pm.medication?.name ?? fallbackName ?? "",
        dose: pm.dose,
        doseUnit: pm.doseUnit,
        route: pm.route ?? null,
        form: pm.form ?? null,
        timeTaken,
        startDate: pm.startDate ?? undefined,
        endDate: pm.endDate ?? null,
        notes: pm.notes,
      });
    },
    [apiMedications],
  );
  const handleEditMedication = useCallback(
    (med: MedicationScheduleItem, period?: CycleProtocolMedicationPeriod) => {
      const groupedMedication = med as CycleProtocolMedication;
      const latestPeriod = [...(groupedMedication.periods ?? [])].sort((a, b) =>
        b.startDate.localeCompare(a.startDate),
      )[0];
      handleEditMedicationById(
        period?.id ?? latestPeriod?.id ?? med.id.slice(0, 36),
        med.name,
      );
    },
    [handleEditMedicationById],
  );
  const { data: apiTestResults } = useTestResults();
  const { data: apiNotes } = usePatientNotes();
  const { data: apiMedicationDoseLogs = [] } = useMedicationDoseLogs({
    startDate: cycle?.startDate ?? undefined,
    endDate: cycle?.endDate ?? undefined,
    enabled: !!cycle?.id,
  });

  // Protocol notes are date-anchored timeline context. Keep legacy protocol
  // adjustment notes readable, but do not let unrelated journal notes leak
  // into a cycle's medication timeline.
  const protocolNotes = useMemo(() => {
    if (!apiNotes || apiNotes.length === 0) return [];
    return [...apiNotes]
      .filter((n) => {
        if (!n.date) return false;
        // Her words about a pregnancy that ended are not timeline context.
        if (isPregnancyDetailsNote(n.text)) return false;
        const isLegacyProtocolNote = n.text?.includes(
          "__protocol_adjustment__",
        );
        const belongsToCycle = n.cycleId === cycle?.id;
        const withinCycle =
          (!cycle?.startDate || n.date >= cycle.startDate) &&
          (!cycle?.endDate || n.date <= cycle.endDate);
        return withinCycle && (belongsToCycle || isLegacyProtocolNote);
      })
      .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
      .map((n) => ({
        id: n.id,
        date: n.date!,
        dayLabel: "",
        text: n.text ?? "",
      }));
  }, [apiNotes, cycle?.endDate, cycle?.id, cycle?.startDate]);

  // The timing line the API's phase dates give each phase.
  const apiJourneyPhases = useMemo(() => {
    if (!activeCycle.phases || activeCycle.phases.length === 0) return null;

    const today = new Date();
    return activeCycle.phases.map((p: Phase) => {
      const type = p.type ?? "protocol";
      const startDate = p.startDate ?? null;
      const endDate = p.endDate ?? null;
      let status: "completed" | "active" | "upcoming" = "upcoming";
      if (endDate && new Date(endDate + "T23:59:59") < today) {
        status = "completed";
      } else if (startDate && new Date(startDate + "T00:00:00") <= today) {
        status = "active";
      }
      return {
        timing: (() => {
          if (!startDate) return "Upcoming";
          const startMs = new Date(startDate + "T00:00:00").getTime();
          const todayMs = new Date(todayStr + "T00:00:00").getTime();
          const startLabel = formatDate(new Date(startMs), locale, {
            month: "short",
            day: "numeric",
          });
          if (status === "completed" && endDate) {
            const endLabel = formatDate(
              new Date(endDate + "T00:00:00"),
              locale,
              { month: "short", day: "numeric" },
            );
            return `${startLabel} – ${endLabel}`;
          }
          if (status === "active") {
            if (!isDurationPhaseType(type)) {
              return t("care.cycle_detail.timeline.started_on", {
                params: { date: startLabel },
              });
            }
            const dayNum = Math.max(
              1,
              Math.round((todayMs - startMs) / DAY_IN_MS) + 1,
            );
            return t("care.cycle_detail.timeline.started_marker", {
              params: { date: startLabel, day: String(dayNum) },
            });
          }
          return t("care.cycle_detail.timeline.starts", {
            params: { date: startLabel },
          });
        })(),
      };
    });
  }, [activeCycle.phases, todayStr, t, locale]);

  // Derive cycle info from API
  const cycleLabel = activeCycle.cycle
    ? t("care.cycle_detail.cycle_label.with_type", {
        params: {
          type: (activeCycle.cycle.cycleType ?? "IVF").toUpperCase(),
        },
      })
    : t("care.cycle_detail.cycle_label.fallback");
  const cycleStartDate = (activeCycle.cycle as Record<string, unknown>)
    ?.startDate as string | undefined;
  const cycleEndDate = (activeCycle.cycle as Record<string, unknown>)
    ?.endDate as string | undefined;
  const currentCycleProtocolData = useMemo<CycleProtocolData>(() => {
    const medicationRowsByKey = new Map<string, CycleProtocolMedication>();
    const medicationPeriods: CycleProtocolMedicationPeriod[] = [];

    // Protocol retains every medication period that overlaps this cycle. We
    // include:
    //   1. Anything tagged to this cycle (added via the hub).
    //   2. Anything fertility-related the patient logged elsewhere
    //      (cycleId null/missing) — these are still part of the protocol
    //      from the patient's perspective even if they weren't entered
    //      through the hub's "Add medication" flow.
    const FERTILITY_MOLECULES = new Set([
      "hcg",
      "gonadotropin",
      "gnrh_agonist",
      "gnrh_antagonist",
      "progestogen",
      "estrogen",
      "clomiphene_citrate",
      "letrozole",
      "folate_supplement",
    ]);
    for (const medication of apiMedications ?? []) {
      const isThisCycle = medication.cycleId === cycle?.id;
      const isCycleOrphanFertility =
        !medication.cycleId &&
        FERTILITY_MOLECULES.has(medication.medication?.moleculeType ?? "");
      if (!isThisCycle && !isCycleOrphanFertility) continue;
      if (
        cycleStartDate &&
        medication.endDate &&
        medication.endDate < cycleStartDate
      ) {
        continue;
      }
      if (
        cycleEndDate &&
        medication.startDate &&
        medication.startDate > cycleEndDate
      ) {
        continue;
      }
      const name =
        medication.medication?.name ??
        medication.rawMedicationName ??
        t("care.cycle_detail.medication.default_name");
      const dose = formatMedicationDose(medication.dose, medication.doseUnit);
      const times = normalizeMedicationTimes(medication.timeTaken);
      const form = API_FORM_TO_UI[medication.form ?? ""] ?? "pill";
      const route = medication.route ?? "";
      const sinceDay = getCycleDayLabel(
        cycleStartDate ?? null,
        medication.startDate ?? null,
        t,
      );
      const untilDay = getCycleDayLabel(
        cycleStartDate ?? null,
        medication.endDate ?? null,
        t,
      );
      // An undated course keeps its own row: merged into a dated row it would
      // have no timeline period and no way to open it for editing.
      const medicationKey = medication.startDate
        ? `${name.trim().toLowerCase()}-${form}-${route}`
        : `undated-${medication.id}`;
      const existing = medicationRowsByKey.get(medicationKey);
      // An undated course stays in the medication list but has no place on
      // the cycle timeline.
      const periods: CycleProtocolMedicationPeriod[] = medication.startDate
        ? [
            {
              id: medication.id,
              name,
              doseLabel: dose || null,
              form,
              route,
              startDate: medication.startDate,
              endDate: medication.endDate ?? null,
              timeTaken: times,
              notes: medication.notes ?? null,
              stopped: /^stopped(?:\s|:|$)/i.test(medication.notes ?? ""),
            },
          ]
        : [];
      medicationPeriods.push(...periods);
      const historyEntry = dose
        ? {
            cycle: cycleLabel,
            dosage: dose,
            note: times.length
              ? t("care.cycle_detail.medication.scheduled_at", {
                  params: { times: times.join(", ") },
                })
              : undefined,
          }
        : undefined;

      if (existing) {
        const nextDoseBadges = existing.dosageBadges ?? [];
        if (dose && !nextDoseBadges.includes(dose)) {
          nextDoseBadges.push(dose);
        }
        const existingTimes = existing.userContext?.timeTaken ?? [];
        const mergedTimes = Array.from(new Set([...existingTimes, ...times]));
        const existingHistory = existing.userContext?.history ?? [];
        const existingPeriods = existing.periods ?? [];
        const existingStartDate = existing.userContext?.startDate;
        const nextStartDate =
          medication.startDate &&
          (!existingStartDate || medication.startDate < existingStartDate)
            ? medication.startDate
            : existingStartDate;
        const existingEndDate = existing.userContext?.endDate;
        const nextEndDate =
          existingEndDate === null || medication.endDate === null
            ? null
            : medication.endDate &&
                (!existingEndDate || medication.endDate > existingEndDate)
              ? medication.endDate
              : existingEndDate;
        const nextSinceDay = getCycleDayLabel(
          cycleStartDate ?? null,
          nextStartDate ?? null,
          t,
        );
        const nextUntilDay = getCycleDayLabel(
          cycleStartDate ?? null,
          nextEndDate ?? null,
          t,
        );
        existing.dosageBadges = nextDoseBadges;
        existing.dosage = nextDoseBadges[0] ?? existing.dosage;
        existing.time = mergedTimes.join(", ");
        existing.timing = getMedicationTiming(mergedTimes);
        existing.sinceDay = nextSinceDay;
        existing.untilDay = nextUntilDay;
        existing.userContext = {
          ...existing.userContext,
          dosage: nextDoseBadges.join(", "),
          timeTaken: mergedTimes,
          startDate: nextStartDate,
          endDate: nextEndDate,
          sinceDay: nextSinceDay,
          history: historyEntry
            ? [...existingHistory, historyEntry]
            : existingHistory,
        };
        existing.periods = [...existingPeriods, ...periods];
        continue;
      }

      medicationRowsByKey.set(medicationKey, {
        type: "medication",
        id: medication.id,
        name,
        brandName: name,
        dosage: dose,
        dosageBadges: dose ? [dose] : [],
        form,
        route,
        time: times.join(", "),
        timing: getMedicationTiming(times),
        color: semanticColors.accent.default,
        recurrence: deriveRecurrence({
          startDate: medication.startDate,
          endDate: medication.endDate,
        }),
        sinceDay,
        untilDay,
        userContext: {
          dosage: dose,
          route,
          selectedForm: medication.form ?? undefined,
          timeTaken: times,
          startDate: medication.startDate ?? undefined,
          endDate: medication.endDate ?? null,
          notes: medication.notes ?? undefined,
          sinceDay,
          history: historyEntry ? [historyEntry] : undefined,
        },
        periods,
      });
    }

    const medicationIds = new Set(medicationPeriods.map((period) => period.id));
    const doseLogs: CycleProtocolDoseLog[] = apiMedicationDoseLogs
      .filter((log) => medicationIds.has(log.medicationId))
      .map((log) => ({
        medicationId: log.medicationId,
        scheduledDate: log.scheduledDate,
        scheduledTime: log.scheduledTime,
        status: log.status === "taken_late" ? "late" : log.status,
      }))
      .filter(
        (log): log is CycleProtocolDoseLog =>
          log.status === "taken" ||
          log.status === "late" ||
          log.status === "missed",
      );

    return {
      id: activeCycle.cycle?.id ?? null,
      startDate: cycleStartDate ?? null,
      endDate: cycleEndDate ?? null,
      medications: Array.from(medicationRowsByKey.values()),
      medicationPeriods,
      doseLogs,
      notes: protocolNotes,
    };
  }, [
    activeCycle.cycle?.id,
    apiMedications,
    apiMedicationDoseLogs,
    cycle?.id,
    cycleLabel,
    cycleEndDate,
    cycleStartDate,
    protocolNotes,
    t,
  ]);
  /**
   * The doses the injection coach reads (#2267).
   *
   * `form` is passed raw rather than through `API_FORM_TO_UI`: that map exists to
   * pick an illustration and collapses `pre_filled_syringe` into `"vial"`, which
   * would lose the one distinction the coach needs — a pen and a pre-filled
   * syringe are different procedures.
   */
  /**
   * Doses that are actually due today, not every medication still running.
   *
   * `EligibilityInput.scheduledDoses` is specified as today's doses, and the
   * filter only checked that the course had not ended. A trigger prescribed for
   * tomorrow therefore offered its coach today — and with the dose recorded from
   * the completion screen, a walk taken to read ahead could be answered "yes, I
   * did it" against a dose that was not due. The start date is the other half of
   * the window and was missing.
   */
  const injectionCoachDoses = useMemo(() => {
    const today = toLocalDateString(new Date());
    return (apiMedications ?? [])
      .filter(
        (medication) =>
          (!medication.endDate || medication.endDate >= today) &&
          (!medication.startDate || medication.startDate <= today),
      )
      .map((medication) => ({
        medicationId: medication.id,
        name: medication.medication?.name ?? medication.rawMedicationName ?? "",
        intakeType: medication.route,
        apiForm: medication.form,
      }));
  }, [apiMedications]);

  const timezone = useTimezone();
  const logMedicationDose = useLogMedicationDose();

  /**
   * Records the dose the coach just walked her through (#2267).
   *
   * The completion screen tells her the trigger shot is recorded, and until now
   * nothing recorded it — the walk ended and her medication stayed as untaken as
   * it was before she picked up the pen. It writes the same medication log the
   * calendar and the homepage write, so a coached dose is a dose like any other
   * everywhere it is read back.
   *
   * Throws rather than failing quietly: the panel catches it and says the dose was
   * not saved, which is the one thing a screen claiming otherwise must never do.
   */
  const handleInjectionCoachCompleted = useCallback(
    async (medicationId: string) => {
      const medication = apiMedications?.find(
        (candidate) => candidate.id === medicationId,
      );
      const record = buildInjectionDoseRecord({
        medication: medication
          ? {
              id: medication.id,
              name: medication.medication?.name ?? medication.rawMedicationName,
              dose: medication.dose,
              doseUnit: medication.doseUnit,
              timeTaken: Array.isArray(medication.timeTaken)
                ? (medication.timeTaken as string[])
                : null,
            }
          : null,
        takenAt: new Date().toISOString(),
        today: getTodayString(timezone),
      });
      if (!record) {
        throw new Error(
          `[CycleDetailScreen] Cannot record the coached dose: medication ${medicationId} is no longer among the patient's medications.`,
        );
      }
      // A stable key, so the same scheduled dose cannot be written twice —
      // walking the steps again, or a retry after a timeout, resolves to the
      // same occurrence rather than a second row against one trigger shot.
      await logMedicationDose.mutateAsync({
        ...record,
        idempotencyKey: `injection-coach:${medicationId}:${record.scheduledFor ?? getTodayString(timezone)}`,
      });
    },
    [apiMedications, logMedicationDose, timezone],
  );

  const currentCycleDocuments = useMemo(
    () => getCycleDocuments(documents, cycle?.id),
    [documents, cycle?.id],
  );
  const currentCycleTestResults = useMemo(
    () =>
      cycle?.id
        ? (apiTestResults ?? []).filter((result) => result.cycleId === cycle.id)
        : [],
    [apiTestResults, cycle?.id],
  );
  const currentCycleDocumentLabel = getCycleDocumentFilterLabel({
    cycleType: displayName,
    cycleNumber,
  });
  const openCurrentCycleDocuments = useCallback(
    (openUpload = false, initialCategory?: string) => {
      if (openUpload) {
        openDocumentUpload({
          defaultCategory: initialCategory ?? "medical_records",
          defaultTags: cycle?.id ? [getCycleDocumentTag(cycle.id)] : [],
        });
        return;
      }

      navigation.navigate("Documents", {
        initialCategory,
        initialDocumentFilter: cycle?.id
          ? getCycleDocumentTag(cycle.id)
          : undefined,
        initialDocumentFilterLabel: currentCycleDocumentLabel,
        openUpload,
      });
    },
    [currentCycleDocumentLabel, cycle?.id, navigation, openDocumentUpload],
  );
  const currentCycleTeamMembers = useMemo(
    () =>
      buildCycleTeamMembers({
        members: cycleCareTeamMembers,
        clinicName:
          cycleClinic?.organization?.displayName ??
          cycleClinic?.legacyProvider ??
          cycle?.provider ??
          null,
        recordedConnections: cycle?.id
          ? careConnections.filter((connection) =>
              connection.cycleIds.includes(cycle.id),
            )
          : [],
      }),
    [
      careConnections,
      cycleCareTeamMembers,
      cycle?.id,
      cycle?.provider,
      cycleClinic,
    ],
  );
  const openPatientCareTeam = useCallback(
    (initialKind?: "clinic" | "provider") => {
      setCareTeamModalVisible(false);
      navigation
        .getParent<NavigationProp<MainTabParamList>>()
        ?.getParent<NavigationProp<RootStackParamList>>()
        ?.navigate("Profile", {
          screen: "ConnectionsDetail",
          params: { cycleId: cycle?.id, initialKind },
        });
    },
    [cycle?.id, navigation],
  );

  const handleCycleTeamAction = useCallback(() => {
    setCareTeamModalVisible(true);
  }, []);

  const handleAddProviderOrClinic = useCallback(() => {
    openPatientCareTeam();
  }, [openPatientCareTeam]);

  const handleAddProvider = useCallback(() => {
    openPatientCareTeam("provider");
  }, [openPatientCareTeam]);

  const cycleHeaderState = useCycleHeaderState({
    cycleId: cycle?.id ?? route.params?.cycleId,
    pgtPlan,
  });
  const cycleLabelsForChip = useCycleTypeLabels();
  const currentPhaseLabel = useMemo(() => {
    // Single source of truth — same step the treatment hub considers current.
    const copy = presentCycleHeaderForCare({
      state: cycleHeaderState,
      t,
      locale,
      cycleLabels: cycleLabelsForChip,
    });
    if (!copy.hasActiveCycle) return null;
    return copy.title;
  }, [cycleHeaderState, t, locale, cycleLabelsForChip]);

  // ── Cycle details info ──
  const cycleStartFormatted = useMemo(() => {
    if (!cycle?.startDate) return "\u2014";
    try {
      return formatDateTime(cycle.startDate, locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return cycle.startDate;
    }
  }, [cycle?.startDate, locale]);

  const cycleChartTimeline = useMemo<CycleChartTimelineItem[]>(() => {
    const todayMs = getLocalDateMs(todayStr) ?? Date.now();
    const timelineSteps = buildCycleTimelineSteps(steps, cycleType, t);
    // The timeline rows carry ids and copy only, so which steps the cycle
    // decides has to come from the step configs they were built from.
    const derivedProgressStepIds = new Set(
      steps.filter((step) => step.derivedProgress).map((step) => step.id),
    );
    const actualPhaseByType = new Map<string, Phase>();
    for (const phase of activeCycle.phases ?? []) {
      if (!phase.type) continue;
      const existing = actualPhaseByType.get(phase.type);
      const existingStart = getLocalDateMs(existing?.startDate) ?? 0;
      const currentStart = getLocalDateMs(phase.startDate) ?? 0;
      if (!existing || currentStart >= existingStart) {
        actualPhaseByType.set(phase.type, phase);
      }
    }
    const apiPhaseByType = new Map<
      string,
      NonNullable<typeof apiJourneyPhases>[number]
    >();
    (activeCycle.phases ?? []).forEach((phase, index) => {
      const item = apiJourneyPhases?.[index];
      if (phase.type && item) apiPhaseByType.set(phase.type, item);
    });

    const cycleStartMs =
      getLocalDateMs(cycle?.startDate) ??
      getLocalDateMs(activeCycle.phases?.[0]?.startDate) ??
      todayMs;

    return timelineSteps.map((step) => {
      const phaseType = getPhaseTypeForStep(step.id);
      const phase = phaseType ? actualPhaseByType.get(phaseType) : null;
      const apiPhase = phaseType ? apiPhaseByType.get(phaseType) : null;
      const progressItem = derivedStepProgress.find(
        (item) => item.stepId === step.id,
      );
      const phaseStartMs = getLocalDateMs(phase?.startDate);
      const phaseEndMs = getLocalDateMs(phase?.endDate);
      const phaseIsActive =
        !!phaseStartMs &&
        phaseStartMs <= todayMs &&
        (!phaseEndMs || phaseEndMs >= todayMs);
      // A step whose progress the cycle decides takes no completion from the
      // phase it borrows. `outcome` shares the luteal phase's row, and that
      // phase's `endDate` is her pregnancy test date — so once the test date
      // passed, the strip ticked the Outcome tab off with nothing recorded.
      const takesCompletionFromPhase = !derivedProgressStepIds.has(step.id);
      const isComplete =
        progressItem?.status === "completed" ||
        progressItem?.status === "skipped" ||
        (takesCompletionFromPhase &&
          !!phaseEndMs &&
          phaseEndMs < todayMs &&
          step.id !== "trigger_shot" &&
          step.id !== "freezing");
      const isActive =
        step.id === currentStepId ||
        ((step.id === "protocol" || step.id === "stimulation") &&
          phaseIsActive);
      const status: CycleChartTimelineItem["status"] = isComplete
        ? "completed"
        : isActive
          ? "active"
          : "upcoming";
      /**
       * The outcome step borrows the luteal phase's row, so the ordinary path
       * would have it repeat that phase's dates word for word. It has better
       * dates of its own — the day a result came in, or the test she has
       * scheduled — and falls through to its own estimate when it has neither.
       */
      const outcomeTiming =
        step.id === "outcome"
          ? resolveOutcomeTiming({
              recordedOn: cycleOutcome?.dateRecorded,
              testDate: waitingTestDate,
            })
          : null;
      const actualTiming =
        step.id === "trigger_shot" || step.id === "freezing"
          ? null
          : outcomeTiming
            ? outcomeTiming.kind === "recorded"
              ? t("care.cycle_detail.timeline.result_recorded_on", {
                  params: {
                    date: formatTimelineDate(
                      getLocalDateMs(outcomeTiming.date) ?? todayMs,
                      locale,
                    ),
                  },
                })
              : outcomeTiming.kind === "test"
                ? t("care.cycle_detail.timeline.test_on", {
                    params: {
                      date: formatTimelineDate(
                        getLocalDateMs(outcomeTiming.date) ?? todayMs,
                        locale,
                      ),
                    },
                  })
                : null
            : formatPhaseTiming(phase, status, todayStr, t, locale);
      const estimatedWindow = estimateTimelineWindow({
        stepId: step.id,
        cycleType,
        cycleStartMs,
      });
      // Show the estimated window as real calendar dates ("Est. Jun 5 – Jun 15")
      // so patients can plan around them. The "Est." prefix keeps it read as a
      // rough textbook-average estimate rather than a firm commitment.
      const estimatedTiming = t("care.cycle_detail.timeline.est_window", {
        params: {
          window: formatTimelineWindow(
            estimatedWindow.startMs,
            estimatedWindow.endMs,
            locale,
          ),
        },
      });

      return {
        stepId: step.id,
        timing:
          actualTiming ??
          // The outcome step goes straight to its own estimate. Both fallbacks
          // below read the luteal phase it borrows — its progress row and its
          // API timing string — so either would put the luteal phase's dates
          // back on the tab that `resolveOutcomeTiming` just kept them off.
          (outcomeTiming
            ? estimatedTiming
            : isComplete && progressItem?.completedAt
              ? t("care.cycle_detail.timeline.filled", {
                  params: {
                    date: formatTimelineDate(
                      getLocalDateMs(progressItem.completedAt) ?? todayMs,
                      locale,
                    ),
                  },
                })
              : apiPhase?.timing && status !== "upcoming"
                ? apiPhase.timing
                : /**
                   * No estimate for a step she has already moved past.
                   *
                   * "Est. Jun 11 – Jun 13" beside a step whose own result is
                   * already recorded predicts days she has lived through, and
                   * reads as the app having lost track of her. A really
                   * recorded timing is never affected — every branch above this
                   * one wins, and this is the last fallback.
                   */
                  isStepPassedUnfilled(step.id, steps, derivedStepProgress)
                  ? null
                  : estimatedTiming),
        status,
      };
    });
  }, [
    activeCycle.phases,
    apiJourneyPhases,
    cycle?.startDate,
    cycleOutcome?.dateRecorded,
    cycleType,
    currentStepId,
    derivedStepProgress,
    steps,
    todayStr,
    waitingTestDate,
    t,
    locale,
  ]);

  const journeyTimingByStepId = useMemo(
    () =>
      Object.fromEntries(
        cycleChartTimeline
          .filter((item) => item.timing != null)
          .map((item) => [item.stepId, item.timing]),
      ) as Record<string, string>,
    [cycleChartTimeline],
  );

  // Clinically-derived per-step status (from the real phase dates) so the hub's
  // "you are here" pointer follows the cycle stage, not what's been logged.
  const journeyStepStatusById = useMemo(() => {
    const map = Object.fromEntries(
      cycleChartTimeline.map((item) => [item.stepId, item.status]),
    ) as Record<string, CycleChartTimelineItem["status"]>;
    // The Outcome step is where the patient is for both the two-week wait and a
    // recorded-but-unclosed result, so the hub stays on that tab across the whole
    // ending rather than jumping off it the moment a result lands.
    //
    // This used to point at `luteal_phase`, because the wait was rendered on that
    // tab for want of one of its own. It now has one, and the luteal step keeps
    // whatever status its own phase dates give it — she can still open it and see
    // the progesterone she is taking.
    if (isOnOutcomeStep) map["outcome"] = "active";
    return map;
  }, [cycleChartTimeline, isOnOutcomeStep]);

  /**
   * Save where the pregnancy stands.
   *
   * A patient-owned fact of its own, written to `/cycles/{id}/pregnancy-status`
   * and linked to the positive outcome — never a correction of that outcome
   * (#3492). It used to go through the beta's outcome write, which rewrote the
   * clinical record to carry her statement and could not work at all when the
   * positive result was authored by her clinic.
   *
   * The cycle is deliberately not closed here. Closing stays its own act, and the
   * end-cycle flow already keeps a recorded loss addressable when she gets there.
   */
  const recordPregnancyStatus = useRecordCyclePregnancyStatus();
  const pregnancyStatus = useCyclePregnancyStatus(
    cycle?.id ?? route.params?.cycleId ?? null,
  );
  /**
   * Her own words when the pregnancy ended. The status write has no field for
   * them, so they are a note linked to this cycle (`pregnancyDetailsNote`).
   */
  const pregnancyDetailsNote = usePregnancyDetailsNote(cycle?.id ?? null);
  const currentPregnancyStatus =
    pregnancyStatus.current === "unknown" ? null : pregnancyStatus.current;
  const pregnancyStatusHead =
    pregnancyStatus.head === "unknown" ? null : pregnancyStatus.head;
  /**
   * Her record was asked for and we do not have it — still in flight, or the
   * read failed.
   *
   * Both have to be one state, because `null` here means "she has said nothing",
   * which every reader takes as a pregnancy still standing. A failed read is not
   * that. False when the query is disabled, where there is no cycle to have a
   * statement about.
   */
  const pregnancyStatusUnknown = pregnancyStatus.current === "unknown";
  const updatePregnancyFollowUp = useUpdateCyclePregnancyFollowUp();
  const pregnancyFollowUp = useCyclePregnancyFollowUp(cycle?.id ?? null);

  /**
   * The recap that follows a first announcement.
   *
   * The arrival carousel's slides: what happened, that nothing needs deciding
   * today, and where things stand. They lived only on Home, which is not where she
   * says the pregnancy is going — that happens here, on the cycle's outcome step —
   * so the moment passed with nothing said back to her.
   */
  const [pregnancyRecapVisible, setPregnancyRecapVisible] = useState(false);
  /**
   * Queued rather than opened directly.
   *
   * Both of these are `Modal`s, and dismissing one while presenting another in
   * the same frame is the case iOS drops: the update sheet goes, and nothing
   * takes its place. So the save asks for the window, and the effect below opens
   * it once the sheet it replaces is actually gone.
   */
  const [pregnancyRecapPending, setPregnancyRecapPending] = useState(false);
  /**
   * The weeks she gave with the announcement, if she gave any.
   *
   * Read from what she just said rather than from the record: the write has
   * returned, but the cycle query has not refetched yet, so `cycleOutcome` at
   * this moment is still the version without them.
   */
  const [pregnancyRecapWeeks, setPregnancyRecapWeeks] = useState<number | null>(
    null,
  );
  /** Same bound the pregnancy-follow-up dialog uses, and for the same reason. */
  const recapMaxHeight = Math.min(760, windowHeight * 0.9);

  useEffect(() => {
    if (!pregnancyRecapPending || updatePregnancyVisible) return;
    const frame = requestAnimationFrame(() => {
      setPregnancyRecapPending(false);
      setPregnancyRecapVisible(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [pregnancyRecapPending, updatePregnancyVisible]);

  /**
   * The dates the window's steps carry, when there are any.
   *
   * Home dates the same cards from her reported weeks, so a window opened the
   * moment she reports them must not read "in the coming weeks" while the page
   * behind it names the day. Same pipeline, same gate: weeks she gave, counted
   * from today because today is when she said it.
   *
   * `pregnancyStands: "continuing"` is what she just told us, and it is the whole
   * permission `mayShowGestationalAge` asks for — no claim here about the
   * follow-up write, which is fired without being awaited.
   */
  const pregnancyRecapDating = useMemo(() => {
    const today = toLocalDateString(new Date());
    return buildEarlyPregnancyDating({
      t,
      locale,
      announcement: { pregnancyStands: "continuing" },
      gestation: gestationFromReportedWeeks({
        weekCount: pregnancyRecapWeeks,
        reportedOn: today,
        today,
      }),
      preciseAge: null,
      resultDate: cycleOutcome?.dateRecorded ?? null,
      today,
    });
  }, [cycleOutcome?.dateRecorded, locale, pregnancyRecapWeeks, t]);

  /**
   * The cards that recap a first announcement.
   *
   * `buildEarlyPregnancyWhatToKnowCards` is the set Home shows under "Bon à
   * savoir": what usually comes next with its steps, what a beta value can and
   * cannot say, and when not to wait for the next appointment. Dated when she
   * gave a week, undated when she did not — the cards carry both wordings.
   */
  const pregnancyRecapCards = useMemo(
    () =>
      buildEarlyPregnancyWhatToKnowCards(
        t,
        getPostProcedureOutcomeContext(cycle?.cycleType, "verified-positive")
          .contentKey,
        pregnancyRecapDating,
      ),
    [cycle?.cycleType, pregnancyRecapDating, t],
  );

  /**
   * Tells the server the pregnancy is being followed, once she has said there is
   * one.
   *
   * Her word is what starts this, not the positive beta, which the app wrote
   * rather than her. **Opt-in only.** Stopping a follow-up is no longer written
   * from here: recording `ended` or `birth` closes an opted-in follow-up inside
   * the same server command (#3492), which is what makes the two consistent — the
   * client's version sent an empty end reason the API would not store, so a
   * follow-up could stay opted in and keep deriving a gestational age for a
   * pregnancy she had told us was over.
   *
   * Still the follow-up *configuration* and nothing more: what she said lives in
   * the pregnancy-status record, and no reader may take this for her answer.
   *
   * Awaited before the shared refresh and never surfaced as a status-save error. The
   * status she just recorded has already been stored; telling her "that failed"
   * because a secondary record did not would be false. The only consequence of a
   * failure here is that no dates appear, which is the state she was already in —
   * so it is logged for us rather than raised at her.
   */
  const recordPregnancyFollowUp = useCallback(
    async (status: PregnancyStatus) => {
      const outcomeId = cycleOutcome?.id;
      if (!cycle?.id || !outcomeId) return;
      if (status.stands !== "continuing") return;

      const existingFollowUp = pregnancyFollowUp.data;
      try {
        await updatePregnancyFollowUp.mutateAsync({
          cycleId: cycle.id,
          invalidate: false,
          body: {
            outcomeId,
            status: "opted_in",
            // This app claims no dating of its own: a transfer anchor is only
            // usable with the embryo culture day — the API says as much,
            // "without it transfer dating derives no gestational age rather than
            // assuming a day" — and that day is recorded nowhere here. Hers is
            // the dating that works, from the weeks she reports.
            //
            // But the PUT replaces the record, so writing nulls would erase an
            // anchor someone else had set — a clinic-entered due date, a later
            // surface, anything this screen knows nothing about. Whatever is
            // already there is carried through untouched.
            datingMethod: existingFollowUp?.datingMethod ?? "unknown",
            datingAnchorDate: existingFollowUp?.datingAnchorDate ?? null,
            datingAnchorPrecision:
              existingFollowUp?.datingAnchorPrecision ?? null,
            transferEmbryoDay: existingFollowUp?.transferEmbryoDay ?? null,
            endedReason: null,
          },
        });
      } catch (error) {
        console.error(
          "[CycleDetailScreen] Could not record the pregnancy follow-up",
          error,
        );
      }
    },
    [
      cycle?.id,
      cycleOutcome?.id,
      pregnancyFollowUp.data,
      updatePregnancyFollowUp,
    ],
  );

  const handleSavePregnancyStatus = useCallback(
    async (status: PregnancyStatus, details: string | null) => {
      if (!cycle?.id || !cycleOutcome?.id || pregnancyStatusUnknown) return;
      setUpdatePregnancyError(null);
      try {
        // Her words first, so a failure saves nothing and a retry starts
        // clean: the note replays or is already there, and the status write
        // keeps its own request id. Only she can write them; a partner's
        // answer is refused below and must not leave a note in his account.
        // A role question, so it keys on `isPartnerCycle`, not the copy
        // perspective an AMAB owner of a shared cycle also reads.
        if (details !== null && !isPartnerCycle) {
          await pregnancyDetailsNote.save(
            details,
            toLocalDateString(new Date()),
          );
        }
        // One request id per record it is aimed at, the same rule the beta write
        // follows: a retry of the same save must replay rather than append, and
        // a save that has become a correction of a newly-read record needs a
        // fresh id because the API refuses one already spent on another write.
        const requestId = resolveOutcomeWriteRequestId(
          updatePregnancyRequestIdRef.current,
          pregnancyStatusWriteTarget(pregnancyStatusHead),
          createIdempotencyKey,
        );
        updatePregnancyRequestIdRef.current = requestId;
        await recordPregnancyStatus.mutateAsync({
          cycleId: cycle.id,
          afterRecord: () => recordPregnancyFollowUp(status),
          body: buildPatientPregnancyStatusWrite({
            status,
            outcomeId: cycleOutcome.id,
            clientRequestId: requestId.requestId,
            current: pregnancyStatusHead,
            today: toLocalDateString(new Date()),
          }),
        });
        updatePregnancyRequestIdRef.current = null;
        // Read before the write: it is the write that makes it `opted_in`, and
        // this is the one moment worth marking — the first time she tells us the
        // pregnancy is going. Every later correction, a week added or changed,
        // leaves it already opted in and opens nothing, because a recap she has
        // to dismiss on each edit is a door, not a welcome.
        // Never announced before, rather than not announced right now — and read
        // from what she has *said*, not from the follow-up. The follow-up is
        // tracking configuration and stands for her answer nowhere any more
        // (#3492); an unread or failed one also leaves its status undefined, which
        // reopened the recap on every later week edit.
        const isFirstAnnouncement =
          status.stands === "continuing" && pregnancyStatusHead === null;
        setUpdatePregnancyVisible(false);
        // No scrolling: it opens over whatever she was reading, which is the
        // point of it being a window rather than a section of this page.
        if (isFirstAnnouncement) {
          setPregnancyRecapWeeks(
            status.stands === "continuing" ? (status.weekCount ?? null) : null,
          );
          setPregnancyRecapPending(true);
        }
      } catch (error) {
        // Logged as well as mapped. The mapper's fallback is "something went
        // wrong, try again", which is the right thing to show her and tells us
        // nothing — a failure that reaches it is by definition one we did not
        // anticipate, and it is the one worth being able to read.
        console.error(
          "[CycleDetailScreen] handleSavePregnancyStatus failed",
          error,
        );
        // A conflict means the record she was correcting is not the one the
        // server holds, so the next attempt is aimed at a different record and
        // must carry a new request id. Kept on every other failure, which is
        // what makes a retry after a lost response a replay rather than a
        // second write.
        if (
          error instanceof CyclePregnancyStatusWriteError &&
          error.isConflict
        ) {
          updatePregnancyRequestIdRef.current = null;
        }
        // Through the mapper, never `error.message`. A raw API string in front of
        // a patient is not an explanation. Its own mapper rather than the beta's:
        // this write cannot fail because a clinic authored the result, which is
        // the point of it, so that copy must not be reachable from here.
        setUpdatePregnancyError(
          pregnancyStatusErrorMessage(error, t, {
            isSupporting: isPartnerCycle,
            cycleOwnerName: partnerDisplayName,
          }),
        );
      }
    },
    [
      recordPregnancyFollowUp,
      pregnancyDetailsNote,
      pregnancyStatusHead,
      pregnancyStatusUnknown,
      cycle?.id,
      cycleOutcome?.id,
      isPartnerCycle,
      partnerDisplayName,
      recordPregnancyStatus,
      t,
    ],
  );

  /*
   * `isPatientLoading` belongs here because `isPartnerCycle` — which decides
   * *which* cycles list `allCyclesStatus` is computed from — depends on the
   * patient. Without it, a partner viewer could be answered from the viewer's
   * own list while the patient loaded, get `cycleNumber: 1` from it, and then
   * watch the status flip back to "loading" as the partner list took over,
   * replacing rendered content with the blank layout.
   */
  if (
    activeCycle.isLoading ||
    isPatientLoading ||
    allCyclesStatus === "loading"
  ) {
    return (
      <GlassScreenLayout>
        <View testID="screen-loading" style={{ flex: 1 }} />
      </GlassScreenLayout>
    );
  }

  if (!activeCycle.cycle) {
    return (
      <GlassScreenLayout>
        {lastClosedOutcome ? (
          <PostCycleOrEmptyState
            lastOutcome={lastClosedOutcome}
            onBack={handleBack}
            onBackToCare={() => {
              navigation.reset({
                index: 0,
                routes: [{ name: "CareOverview" }],
              });
            }}
            onOpenArticle={(articleId: string) => {
              const rootNav =
                navigation.getParent<NavigationProp<MainTabParamList>>();
              rootNav?.navigate("Learn", {
                screen: "ArticleDetail",
                params: { articleId, returnTab: "Care" },
              });
            }}
          />
        ) : (
          <NoActiveCycleState
            onBack={handleBack}
            onStartTreatment={handleOpenStartTreatment}
            onAddPastCycle={() => setAddPastCycleVisible(true)}
          />
        )}
        <MoonStartTreatmentModal
          visible={startTreatmentVisible}
          onClose={handleCloseStartTreatment}
          onStartTreatment={handleStartTreatment}
          isLoading={
            createCycle.isPending ||
            createEmbryoFreezingPlan.isPending ||
            acceptEmbryoFreezingPlan.isPending ||
            updateCycleMutation.isPending
          }
          goal={treatmentGoal}
          gender={
            patient?.gender === "AMAB"
              ? "male"
              : patient?.gender === "AFAB"
                ? "female"
                : null
          }
          editData={editTreatmentData ?? undefined}
          initialStep={editTreatmentInitialStep}
          allowTreatmentTypeBackNavigation={allowTreatmentTypeBackNavigation}
          submitError={startTreatmentSubmitError}
          onFormChange={clearCycleSaveFailure}
        />
        <AddPastCycleDialog
          visible={addPastCycleVisible}
          onClose={() => setAddPastCycleVisible(false)}
        />
      </GlassScreenLayout>
    );
  }

  // The injection coach sits on the trigger-shot step for the same reason the
  // wait sits on Outcome: it belongs to one moment of the cycle, and pinning it
  // to that step means it cannot surface at a time its protocol does not
  // describe (#2267).
  const injectionCoachView = (
    <InjectionCoachPanel
      stepId={INJECTION_COACH_STEP_ID}
      hasActiveCycle={Boolean(cycle?.id)}
      scheduledDoses={injectionCoachDoses}
      confirmedForm={injectionCoachForm}
      onConfirmForm={setInjectionCoachForm}
      confirmedRoute={injectionCoachRoute}
      onConfirmRoute={setInjectionCoachRoute}
      onCompleted={handleInjectionCoachCompleted}
    />
  );

  /**
   * The two-week wait, for the cycle types that have no other surface for it.
   *
   * It renders as the terminal "Outcome" tab inside the hub (via
   * `renderStepPanelOverride`) rather than taking over the whole screen — and on
   * an FET cycle it no longer renders at all.
   *
   * Two surfaces were answering for the same tab. Before the transfer was
   * confirmed the tab fell through to the step's own panel: the result card, how
   * it works, what to expect, and the way back to where she left off. The moment
   * the transfer landed, `isInWaitingPhase` turned true and this view took the
   * tab over with a different design — a status line, a ring asking her to pick
   * a date, and its own pair of buttons. Same tab, same cycle, same wait, two
   * looks, and the switch happened on a fact that has nothing to do with how the
   * wait should look.
   *
   * The step panel is the one that stays. Nothing is lost by it: recording the
   * beta from there opens the very same modal this view's button opened, and the
   * test date it asked her to pick was already read-only on FET
   * (`onTestDateChange` was undefined — that clinic's order is not hers to set).
   *
   * IVF, ICSI, IUI and OI keep this view: their outcome step has no equivalent
   * panel, so removing it there would leave them with no wait surface at all.
   */
  const waitingPhaseView =
    isInWaitingPhase && cycleType !== "fet" ? (
      <WaitingPhaseView
        lutealStartDate={waitingStartDate}
        testDate={waitingTestDate}
        onTestDateChange={handleSetTestDate}
        onRecordResult={() => {
          setBetaResultError(null);
          setBetaResultVisible(true);
        }}
        onEditTestAppointment={openBetaAppointmentModal}
        onChatAboutThis={() => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Chat", {
            screen: "Assistant",
            params: {
              initialPrompt: t(
                "care.treatment_hub.waiting_phase.chat_seed_prompt",
              ),
              autoSendInitialPrompt: true,
            },
          });
        }}
        isSavingTestDate={isSavingTestDate}
        testDateSaveError={testDateSaveError}
        onRetryTestDate={
          pendingTestDate
            ? () => void handleSetTestDate(pendingTestDate)
            : undefined
        }
        isLoadingResult={recordCycleOutcome.isPending}
        perspective={perspective}
        partnerDisplayName={partnerDisplayName}
      />
    ) : null;

  /**
   * The state between recording an ending and closing the cycle.
   *
   * `isInWaitingPhase` goes false as soon as an outcome type exists, which used
   * to coincide with the cycle closing in the same PATCH. Now that recording
   * leaves the cycle open, this panel is what stands in that gap — otherwise the
   * wait view would simply vanish with nothing in its place.
   */
  /**
   * Not until her own statement has come back.
   *
   * The card decides its heading and whether to fire confetti on its first
   * render, from where the pregnancy stands. Mounted without that read it sees
   * nothing, reads a standing positive, and celebrates — over a card that a
   * moment later is headed "The pregnancy ended". A failed read is withheld for
   * the same reason as a slow one: neither is her saying nothing. The rest of
   * this step already waits on its queries; this one has to as well.
   */
  const recordedResultView =
    hasRecordedOpenResult && cycleOutcome?.type ? (
      <RecordedResultView
        pregnancyStatusUnknown={pregnancyStatusUnknown}
        kind={cycleOutcome.type}
        recordedDate={
          cycleOutcome.dateRecorded
            ? formatDate(
                new Date(`${cycleOutcome.dateRecorded}T00:00:00`),
                locale,
                { day: "numeric", month: "long", year: "numeric" },
              )
            : null
        }
        // Shown to her and to a partner alike — the summary is what may be seen
        // either way, and it never carries the kind of loss.
        // Her own statement, and nothing inferred from the result or the
        // follow-up. The endpoint already withholds it once the outcome it was
        // recorded against stops being the cycle's current positive one, so a
        // corrected beta cannot leave "Pregnant" printed under "This cycle didn't
        // end in a pregnancy".
        pregnancyStatus={summarisePregnancyStatus(currentPregnancyStatus)}
        // Ending the cycle is hers, a role question: an AMAB owner of a shared
        // cycle reads partner copy but still closes his own cycle.
        onCloseCycle={isPartnerCycle ? undefined : handleCloseRecordedCycle}
        onUpdateResult={() => {
          setBetaResultError(null);
          setBetaResultVisible(true);
        }}
        // Only the person who carried the cycle. Reporting a loss is a fact
        // about her body, not a partner's to enter.
        onUpdatePregnancy={
          isPartnerCycle
            ? undefined
            : () => {
                setUpdatePregnancyError(null);
                setUpdatePregnancyVisible(true);
              }
        }
        // Same perspective the wait view on this step uses, so the copy does not
        // switch voice the moment a result is recorded.
        perspective={perspective}
        isClosing={createSnapshot.isPending}
      />
    ) : null;

  return (
    <GlassScreenLayout edges={["bottom", "left", "right"]}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* ── HERO HEADER ── */}
        <View
          style={{
            paddingTop: insets.top + 12,
            paddingHorizontal: 20,
            paddingBottom: 28,
            borderBottomLeftRadius: 28,
            borderBottomRightRadius: 28,
            overflow: "hidden",
          }}
        >
          {/* Bronze-gradient background */}
          <Image
            source={require("../../assets/images/bronze-gradient.webp")}
            style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
            resizeMode="cover"
          />

          {/* Decorative illustration — top-right */}
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: insets.top + 16,
              right: 0,
            }}
          >
            <CycleIllustration cycleType={cycle?.cycleType} />
          </View>

          {/* Back */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("care.overview.screen_title")}
            onPress={() => {
              // Arrow-only control; its accessibility label is the copy.
              capturePress(
                pressLabel(t("care.overview.screen_title")),
                "back_to_care",
              );
              handleBack();
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: semanticColors.onDark.fill.subtle,
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <ArrowLeft size={18} color={semanticColors.onDark.text.secondary} />
          </Pressable>

          {/* Eyebrow */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              alignSelf: "flex-start",
              marginBottom: 6,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Activity
                size={13}
                color={semanticColors.onDark.accent}
                strokeWidth={1.5}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: semanticColors.onDark.accent,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t("care.cycle_detail.empty.active_cycle_eyebrow")}
              </Text>
            </View>
            {cycle && (
              <View
                style={{
                  backgroundColor: withAlpha(
                    semanticColors.onDark.accent,
                    0.15,
                  ),
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  borderRadius: 8,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    color: semanticColors.onDark.accent,
                  }}
                >
                  {currentPhaseLabel ??
                    t("care.cycle_detail.empty.starting_soon")}
                </Text>
              </View>
            )}
          </View>

          {/* Title row */}
          <View
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}
          >
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 22,
                fontWeight: "700",
                color: semanticColors.onDark.titleWarm,
                letterSpacing: -0.3,
                lineHeight: 27,
              }}
            >
              {cycle
                ? `${displayName} #${cycleNumber}`
                : t("care.cycle_detail.empty.no_active_cycle_main")}
            </Text>
            {cycle && (
              <View style={{ marginTop: -4 }}>
                <KebabMenu
                  buttonColor={semanticColors.onDark.text.secondary}
                  items={heroMenuItems}
                  accessibilityLabel="Cycle actions"
                />
              </View>
            )}
          </View>

          {/* Metadata chips */}
          {cycle && (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 16,
              }}
            >
              {cycle.provider ? (
                <Tag label={cycle.provider} variant="outline" tone="onDark" />
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("care.cycle_detail.buttons.add_clinic")),
                      "care.cycle_detail.buttons.add_clinic",
                    );
                    handleEditTreatmentWhereWhen();
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: semanticColors.onDark.fill.strong,
                  })}
                >
                  <Plus
                    size={11}
                    color={semanticColors.onDark.text.secondary}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                    }}
                  >
                    {t("care.cycle_detail.buttons.add_clinic")}
                  </Text>
                </Pressable>
              )}
              {cycle.startDate ? (
                <Tag
                  label={t("care.cycle_detail.cycle_label.started_chip", {
                    params: { date: cycleStartFormatted ?? "" },
                  })}
                  variant="outline"
                  tone="onDark"
                />
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel("Add start date"),
                      "add_cycle_start_date",
                    );
                    handleEditTreatmentWhereWhen();
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: semanticColors.onDark.fill.strong,
                  })}
                >
                  <Plus
                    size={11}
                    color={semanticColors.onDark.text.secondary}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                    }}
                  >
                    Add start date
                  </Text>
                </Pressable>
              )}
            </View>
          )}
        </View>

        {/* ── BODY ── */}
        <View
          // The hub opens this section, so where the body starts is where the hub
          // starts. An arrival that asked for a hub step scrolls here: this screen
          // stays mounted between visits, so navigating to it again would
          // otherwise land wherever it was left — for a patient sent to read her
          // result, the bottom of a long page.
          onLayout={(event) => {
            const { y } = event.nativeEvent.layout;
            setHubOffsetY((previous) => (previous === y ? previous : y));
          }}
          style={{ paddingHorizontal: 16, paddingTop: 8 }}
        >
          {/* ━━ TREATMENT HUB (the two-week wait renders as the terminal
              "Outcome" tab via renderStepPanelOverride, not a full takeover) ━━ */}
          <MoonTreatmentHubCard
            ref={treatmentHubRef}
            activeCycle={hubCycle}
            isCycleLoading={activeCycle.isLoading}
            displayTreatmentName={displayName}
            isReadOnly={isPartnerCycle}
            frozenEmbryoLabelsForTransfer={pickedEmbryoLabels}
            onEditTreatment={handleEditTreatment}
            onUpdateStepProgress={handleUpdateStepProgress}
            onOpenMedicationModal={handleOpenMedicationModal}
            hasSpecimens={totalEggs + totalEmbryos + totalSperm > 0}
            /**
             * Entering the two-week wait is not an ending — it adds the luteal
             * phase and the beta date, and the button says so. Everything else
             * here closes the cycle, and closing always goes through the dialog
             * that asks.
             *
             * Routing non-preservation cycle types straight at
             * `handleCompletePreservation` closed them on one tap: with
             * `treatmentGoal === "pregnancy"` and no confirmed transfer it
             * PATCHes `status: "closed"`, today's `endDate` and a null outcome,
             * then resets to Care overview. The button sits on the journey's
             * last step, which for ivf/icsi/iui/fet/oi is the appended
             * `outcome` tab — so a patient on day 6 of stimulation who opened
             * that tab to see what was coming could end her cycle with one
             * press and no way back.
             */
            onFinishTreatment={() => {
              if (hasConfirmedTransfer) {
                handleCompletePreservation();
                return;
              }
              setEndCycleMode("complete");
              setEndCycleVisible(true);
            }}
            onStartTreatment={handleOpenStartTreatment}
            goal={treatmentGoal}
            canEnterWaiting={hasConfirmedTransfer}
            celebrateCompletion={mayCelebrateCompletion}
            treatmentIsDone={treatmentIsDone}
            cycleNumber={cycleNumber}
            onEditMedication={handleEditMedication}
            onAddSpecimen={() => {
              setAddSpecimenForRetrieval(false);
              setAddSpecimenVisible(true);
            }}
            onCryoVaultFollowUp={showCryoVaultFollowUpToast}
            onRecordProcedureOutcome={(stepId) => {
              // The Outcome tab's only way to record a result used to be the
              // two-week-wait card, which does not render until a luteal phase
              // row exists — so a patient who never logged luteal support had
              // no way to record her beta from the hub at all.
              if (stepId === "outcome") {
                setBetaResultError(null);
                setBetaResultVisible(true);
                return;
              }
              if (cycle?.cycleType === "egg_freezing") {
                setAddSpecimenForRetrieval(true);
                setAddSpecimenVisible(true);
              }
            }}
            onScheduleProcedure={(stepId) => {
              const prefill = getProcedureAppointmentPrefill(stepId);
              if (!prefill) return;
              setAppointmentPrefill({
                ...prefill,
                cycleId: cycle?.id ?? null,
              });
              setAddAppointmentVisible(true);
            }}
            onAddResults={() =>
              openCurrentCycleDocuments(true, "medical_records")
            }
            usingFrozenSpecimens={usingFrozenOocytes}
            stepTimingById={journeyTimingByStepId}
            clinicalStepStatusById={journeyStepStatusById}
            clinicRecordedStepIds={clinicRecordedStepIds}
            renderCalendar={() => (
              <CalendarOverviewCard
                cycleId={cycle?.id ?? null}
                onOpenMedicationFactSheet={setCalendarMedicationSheet}
                onOpenAppointment={setCalendarAppointmentSheet}
                onOpenProcedureFactSheet={handleOpenCalendarProcedure}
                onLoggingOverlayChange={setCalendarSymptomOverlay}
                onAddMedication={() => setAddMedVisible(true)}
                onAddAppointment={() => setAddAppointmentVisible(true)}
              />
            )}
            onQuickAddMedication={() => setAddMedVisible(true)}
            onQuickAddAppointment={() => setAddAppointmentVisible(true)}
            onQuickAddTest={() =>
              openCurrentCycleDocuments(true, "medical_records")
            }
            requestedStepId={hubStepRequest}
            requestedStepArrival={hubStepRequestCount}
            /**
             * What the clinic filed against this cycle's beta order, on the
             * step she now watches it from.
             *
             * A single result reaches the panel through the recorded outcome,
             * but a series does not — and a rising or falling pair is the whole
             * clinical point of repeating the test. It is added to the step
             * rather than replacing it, so the record action and the guidance
             * stay where they are.
             *
             * Only once the lookup has answered: rendering it while the query
             * is still in flight says "no beta order" to a patient whose clinic
             * has filed one.
             */
            renderStepPanelExtra={(stepId) =>
              // `isFetched` alone was not enough on either side. It turns true
              // on a *failed* fetch as well, so a lookup that errored rendered
              // "your clinic has not reported a beta order" — asserting the
              // clinic filed nothing when the app had simply failed to look. And
              // with no transfer on record there is nothing for an order to hang
              // off, so on day one of every FET cycle, clinic connected or not,
              // the tab stated the same absence as a fact.
              stepId !== "outcome" ? null : cycleType === "fet" ? (
                fetBetaLifecycleQuery.isFetched &&
                !fetBetaLifecycleQuery.isError &&
                betaHcgSeriesQuery.isFetched &&
                !betaHcgSeriesQuery.isError &&
                isReportedFETTransfer(fetBetaLifecycleQuery.data?.procedure) ? (
                  <FETBetaResultSeries
                    lifecycle={fetBetaLifecycleQuery.data ?? null}
                    results={betaHcgRows}
                  />
                ) : null
              ) : // Every other modality gets the same measurements from the
              // same series, once available. The Outcome action already asks
              // for the first result; don't add an empty history panel as well.
              betaHcgSeriesQuery.isFetched &&
                !betaHcgSeriesQuery.isError &&
                betaHcgRows.length > 0 ? (
                <BetaHcgSeriesCard rows={betaHcgRows} />
              ) : null
            }
            renderStepPanelOverride={(stepId) => {
              if (stepId === "outcome") {
                // The wait and the recorded-but-unclosed result are successive
                // states of the same step, so they share its panel: the wait
                // gives way to the result the moment one is recorded. Luteal
                // support is its own tab now and keeps its own panel.
                return waitingPhaseView ?? recordedResultView ?? undefined;
              }
              // The panel decides for itself whether it has anything to show —
              // it returns null when the dose is not one it can guide — so the
              // step check here is only about which tab it belongs to.
              if (stepId === INJECTION_COACH_STEP_ID) return injectionCoachView;
              return undefined;
            }}
          />

          {/* ━━ CRYO VAULT SECTION (preservation cycles) ━━ */}
          {treatmentGoal === "preserve" && currentStepId === null && (
            <>
              <CycleSectionTitle
                title={t("care.cycle_detail.sections.cryo_vault")}
              />
              <CryoVaultOverviewCard
                onPress={() => navigation.navigate("CryoVault")}
                onAddSpecimen={() => setAddSpecimenVisible(true)}
                onNoSpecimens={() => setAddSpecimenVisible(true)}
                onPressSpecimen={(specimen) =>
                  setSelectedSpecimenId(specimen.specimenIds[0] ?? null)
                }
                specimens={bridgeSpecimens}
              />
            </>
          )}

          <CycleTestsSection
            testResults={currentCycleTestResults}
            cycleStartDate={cycle?.startDate ?? null}
            phases={phases}
            cycleLink={{
              label: currentCycleDocumentLabel,
              cycleId: cycle?.id,
            }}
            onOpenCycle={(cycleLink) => {
              navigation.navigate("CycleDetail", {
                cycleId: cycleLink.cycleId ?? cycle?.id,
              });
            }}
            onViewAll={() =>
              navigation.navigate("AllLabResults", {
                cycleId: cycle?.id,
                cycleLabel: currentCycleDocumentLabel,
              })
            }
            onAddTestResult={() =>
              openCurrentCycleDocuments(true, "medical_records")
            }
          />

          {/* ━━ PROTOCOL ━━ */}
          <CycleProtocolSection
            cycle={currentCycleProtocolData}
            onEditMedication={handleEditMedication}
            onAddMedication={() => setAddMedVisible(true)}
          />

          {/* ━━ MONITORING ━━ */}
          {/* Only for a cycle type that is actually monitored. This rendered
              unconditionally, which put a follicle-and-lining section on a
              sperm freeze, where there is nothing about the patient's cycle to
              follow — a section that could only ever show its empty state. */}
          <View>
            {isMonitoredCycle && (
              <OvarianStimulationMonitoringSection
                cycleId={cycle?.id}
                cycleType={cycle?.cycleType}
                cycleStartDate={cycle?.startDate}
                openEntryRequested={route.params?.focus === "monitoring"}
                onEntryRequestHandled={() => {
                  // Consume the arrival so pressing "add monitoring data" again
                  // re-opens the form. React Navigation reuses this screen when
                  // it is already the focused route and only swaps its params, so
                  // an uncleared `focus` makes every later request a no-op.
                  if (route.params?.focus) {
                    navigation.setParams({ focus: undefined });
                  }
                }}
                cycleLabel={currentCycleDocumentLabel}
                onUploadReport={(
                  dateISO,
                  onMonitoringExtracted,
                  onDocumentSaved,
                ) => {
                  void pickAndOpenDocumentUpload({
                    defaultCategory: "medical_records",
                    defaultTags: [
                      ...(cycle?.id ? [getCycleDocumentTag(cycle.id)] : []),
                      "monitoring-report",
                      `monitoring-date:${dateISO}`,
                    ],
                    onMonitoringExtracted,
                    onSave: onDocumentSaved,
                  });
                }}
                onOpenCycle={(cycleLink) => {
                  navigation.navigate("CycleDetail", {
                    cycleId: cycleLink.cycleId ?? cycle?.id,
                  });
                }}
                phases={hubPhases}
                derivedStepProgress={derivedStepProgress}
                defaultView="table"
              />
            )}
          </View>

          <CycleRelatedDocumentsSection
            documents={currentCycleDocuments}
            onViewAll={() => openCurrentCycleDocuments(false)}
            onUpload={() => openCurrentCycleDocuments(true, "medical_records")}
          />

          <View
            style={{
              marginTop: 28,
              marginBottom: 10,
              marginLeft: 4,
            }}
          >
            <SectionTitle icon={HeartHandshake}>
              {t("care.cycle_detail.sections.connect_care_team")}
            </SectionTitle>
          </View>
          <CycleClinicTeamCard
            members={currentCycleTeamMembers}
            clinicName={
              cycleClinic?.organization?.displayName ??
              cycleClinic?.legacyProvider ??
              cycle?.provider ??
              null
            }
            onEditTeam={handleCycleTeamAction}
            onAddProviderOrClinic={handleAddProviderOrClinic}
            onAddProvider={handleAddProvider}
          />
        </View>
      </ScrollView>
      <MedicationFactsheetSheet
        item={calendarMedicationSheet?.item ?? null}
        fallbackName={calendarMedicationSheet?.fallbackName}
        onClose={() => setCalendarMedicationSheet(null)}
        userContext={{
          ...calendarMedicationSheet?.userContext,
          source:
            apiMedications?.find(
              (p) => p.id === calendarMedicationSheet?.medicationId,
            )?.source ?? null,
        }}
        medicationId={calendarMedicationSheet?.medicationId}
        onEditPress={
          calendarMedicationSheet?.medicationId
            ? () => {
                const id = calendarMedicationSheet.medicationId;
                const name = calendarMedicationSheet.fallbackName;
                setCalendarMedicationSheet(null);
                if (id) handleEditMedicationById(id, name);
              }
            : undefined
        }
      />
      <AppointmentDetailSheet
        visible={calendarAppointmentSheet != null}
        appointment={calendarAppointmentSheet}
        onClose={() => setCalendarAppointmentSheet(null)}
        onEdit={(appointment) => {
          setCalendarAppointmentSheet(null);
          setEditAppointment(appointment);
        }}
        onOpenEducation={handleOpenCalendarAppointmentEducation}
      />
      <ProcedureDetailSheet
        item={calendarProcedureSheet?.item ?? null}
        appointment={
          calendarProcedureSheet &&
          !calendarProcedureEducationOnly &&
          calendarProcedureSheet.appointment.appointmentId
            ? {
                type: "appointment",
                id: calendarProcedureSheet.appointment.id,
                appointmentId: calendarProcedureSheet.appointment.appointmentId,
                title: calendarProcedureSheet.appointment.title,
                subtitle: calendarProcedureSheet.appointment.subtitle,
                time: calendarProcedureSheet.appointment.time,
                date: calendarProcedureSheet.appointment.date,
                ownership: (calendarProcedureSheet.appointment.ownership ??
                  undefined) as "patient" | "provider" | undefined,
                ownerId:
                  calendarProcedureSheet.appointment.ownerId ?? undefined,
                icon: CalendarClock,
                factsheetId: calendarProcedureSheet.item.id,
              }
            : undefined
        }
        onClose={() => {
          setCalendarProcedureSheet(null);
          setCalendarProcedureEducationOnly(false);
        }}
      />
      <MoonStartTreatmentModal
        visible={startTreatmentVisible}
        onClose={handleCloseStartTreatment}
        onStartTreatment={handleStartTreatment}
        isLoading={
          createCycle.isPending ||
          createEmbryoFreezingPlan.isPending ||
          acceptEmbryoFreezingPlan.isPending ||
          updateCycleMutation.isPending
        }
        goal={treatmentGoal}
        gender={
          patient?.gender === "AMAB"
            ? "male"
            : patient?.gender === "AFAB"
              ? "female"
              : null
        }
        editData={editTreatmentData ?? undefined}
        initialStep={editTreatmentInitialStep}
        allowTreatmentTypeBackNavigation={allowTreatmentTypeBackNavigation}
        submitError={startTreatmentSubmitError}
        onFormChange={clearCycleSaveFailure}
      />
      <CycleCareTeamModal
        visible={careTeamModalVisible}
        cycleId={cycle?.id}
        clinicName={
          cycleClinic?.organization?.displayName ??
          cycleClinic?.legacyProvider ??
          cycle?.provider ??
          null
        }
        onClose={() => setCareTeamModalVisible(false)}
        onOpenCareTeam={() => openPatientCareTeam()}
      />
      <AddAppointmentModal
        visible={addAppointmentVisible || editAppointment != null}
        onClose={() => {
          setAddAppointmentVisible(false);
          setEditAppointment(null);
          setAppointmentPrefill(null);
          // Defensive refetch — `useCreateAppointment` already
          // invalidates these keys on success, but the snapshot tile's
          // "Schedule visit" → date flip wasn't always landing after the
          // modal closed (UAT issue #33 follow-up). Re-invalidating
          // here guarantees the next render sees the just-saved
          // appointment without waiting for the prefix-match to fire.
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.patient(tenantId ?? "", patientId ?? ""),
          });
          queryClient.invalidateQueries({
            queryKey: calendarKeys.patient(tenantId ?? "", patientId ?? ""),
          });
        }}
        editAppointment={editAppointment}
        prefill={appointmentPrefill}
        onSaved={({
          appointmentId,
          startDate,
          startTime,
          consultationType,
        }) => {
          // Keep the two-week-wait card in sync: saving a beta hCG appointment
          // moves the pregnancy-test date (luteal-phase end date) that drives
          // the countdown, so the appointment and the ring never disagree.
          const isBetaTest = (consultationType ?? []).some(
            (activity) => activity.trim().toLowerCase() === "beta hcg",
          );
          if (isBetaTest && startDate) {
            handleSetTestDate(startDate);
          }
          // Scheduling a procedure appointment (egg retrieval, transfer, IUI,
          // sperm collection) mirrors the chosen date onto the matching step's
          // cycle phase so the treatment-hub step flips to "scheduled" — the
          // same date→phase mechanism the beta-hCG card uses above. The saved
          // appointment ID is also persisted on the procedure when available;
          // the date mirror keeps the hub responsive in the current client
          // state.
          const scheduled = findScheduledProcedureStep(consultationType);
          if (scheduled && startDate) {
            // Only carry a time if the appointment actually has one — a
            // date-only value round-trips through the phase without a
            // startTime, so the step shows no fabricated hour.
            const dateValue = startTime
              ? `${startDate}T${startTime}:00`
              : startDate;
            const optimisticData: Record<
              string,
              string | number | boolean | null
            > = {
              [scheduled.dateField]: dateValue,
              ...(appointmentId ? { appointmentId } : {}),
            };
            // Push "scheduled" into the hub's optimistic state before the
            // phase round-trip lands, otherwise the schedule row keeps
            // shadowing the just-scheduled state during the refetch window.
            treatmentHubRef.current?.applyScheduledOptimistic(
              scheduled.stepId,
              optimisticData,
            );
            // Explicitly persist as "scheduled" rather than "completed":
            // saving an appointment records the intent, not the outcome.
            // The old flow relied on `isFutureStart > today`, which quietly
            // flipped same-day scheduling to "completed" (endDate=nextDay)
            // and auto-advanced the journey to fertilization before the
            // patient could log any oocyte counts.
            // The appointment is already saved, by the modal, exactly as she
            // entered it. This call only mirrors its date onto the phase.
            void handleUpdateStepProgress(
              scheduled.stepId,
              { status: "scheduled", data: optimisticData },
              { skipCalendarSync: true },
            );
          }
        }}
      />
      <AddMedicationModal
        visible={addMedVisible || !!editMed}
        onClose={() => {
          setAddMedVisible(false);
          setHubMedCycleId(null);
          setHubMedPhaseId(null);
          setHubMedRole(null);
          setEditMed(null);
        }}
        cycleId={hubMedCycleId ?? cycle?.id ?? ""}
        menstruationStartDate={cycle?.menstruationStartDate}
        phaseId={hubMedPhaseId ?? undefined}
        medicationRole={hubMedRole ?? undefined}
        editMedication={editMed}
      />
      {/* A window, after a first pregnancy is logged. Not a page section: this is
          a moment, and it should not become part of the cycle page she scrolls
          every day.

          The cards are the early-pregnancy "what to know" set — what usually comes
          next with its steps, how a beta is read, and when not to wait — in the
          same carousel Home gives them, so the two cannot drift.

          Bounded and scrollable, for the reason its sibling documents: an
          illustration, a header, an equalized card and a button add up past a
          small phone's viewport, and the dialog clips rather than scrolls, so the
          button went off the bottom with only the ✕ left to dismiss it. */}
      <Dialog
        size="wide"
        visible={pregnancyRecapVisible}
        onClose={() => setPregnancyRecapVisible(false)}
        layout="centered"
        closeButton="minimal"
        testID="pregnancy-announcement-recap"
        surfaceStyle={{ maxHeight: recapMaxHeight }}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 14, paddingBottom: 4 }}
          style={{ maxHeight: recapMaxHeight - 72 }}
        >
          {/* The app's own illustration, the one its pregnancy-follow-up dialog
              already wears — so a window about the weeks ahead is recognisably the
              same family, and the moment is not a wall of text. */}
          <DialogIcon
            source={PREGNANCY_RECAP_ILLUSTRATION}
            width={84}
            height={88}
          />
          {/* Its own words. The card behind it already says "A positive result ·
              Congratulations, your clinic will confirm what comes next"; repeating
              that here made her read the same two sentences twice, one over the
              other. What this window is for is the weeks ahead. */}
          <DialogHeader
            align="center"
            title={t(
              "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.the_weeks_ahead",
            )}
            subtitle={t(
              "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.what_usually_comes_next_and_who_to_ask",
            )}
          />
          <WhatToKnowView cards={pregnancyRecapCards} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: semanticColors.text.tertiary,
              textAlign: "center",
            }}
          >
            {t(
              "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.you_will_find_this_on_your_home_page",
            )}
          </Text>
          <PrimaryButton
            label={t("homepage.cycle_closing.ui.done")}
            onPress={() => setPregnancyRecapVisible(false)}
          />
        </ScrollView>
      </Dialog>

      {/* Dating an upcoming collection of his sample, with no partner account to
          carry it. Framed as the one thing that is actually hers to do — send the
          invitation, once — because what follows it is automatic: a connected
          partner's calendar receives what concerns him without her passing
          anything on.

          Including this one. `computeAudience` does snapshot the audience at
          creation from the `appointments` sharing grant, and `updateAppointment`
          never recomputes it — but neither is the mechanism that matters:
          `audience-reconciliation.service.ts` maps that category onto the
          `appointments` table and appends the target to every row the patient
          owns, and both the sharing-preferences and partner services enqueue it.
          So a grant made after the booking reaches the booking. It lands on the same Connections
          screen the care-team entry point uses, rather than inventing a second
          invitation flow. */}
      <Dialog
        visible={partnerCalendarPromptVisible}
        onClose={() => setPartnerCalendarPromptVisible(false)}
        layout="centered"
        closeButton="minimal"
        testID="partner-calendar-prompt"
      >
        <View style={{ gap: 14 }}>
          <DialogHeader
            title={t("care.treatment_hub.partner_calendar.invite_your_partner")}
            subtitle={t(
              "care.treatment_hub.partner_calendar.once_connected_and_shared_their_calendar_receives_them",
            )}
          />
          <PrimaryButton
            label={t("care.treatment_hub.partner_calendar.invite_my_partner")}
            onPress={() => {
              setPartnerCalendarPromptVisible(false);
              openPatientCareTeam();
            }}
          />
          <TextActionButton
            label={t("care.treatment_hub.partner_calendar.not_now")}
            action="care.treatment_hub.partner_calendar.not_now"
            tone="tertiary"
            size="sm"
            onPress={() => setPartnerCalendarPromptVisible(false)}
            style={{ alignSelf: "center" }}
          />
        </View>
      </Dialog>

      <UpdatePregnancyModal
        visible={updatePregnancyVisible}
        onClose={() => setUpdatePregnancyVisible(false)}
        onSave={(status, details) =>
          void handleSavePregnancyStatus(status, details)
        }
        isSaving={recordPregnancyStatus.isPending}
        readReady={!pregnancyStatusUnknown && pregnancyDetailsNote.ready}
        current={currentPregnancyStatus}
        currentDetails={pregnancyDetailsNote.details}
        error={updatePregnancyError}
      />

      <EndCycleModal
        visible={endCycleVisible}
        onClose={() => setEndCycleVisible(false)}
        cycleId={cycle?.id}
        outcomeId={cycle?.outcome?.id ?? null}
        preservationCycleType={cycle?.cycleType}
        // This screen can target a cycle other than the active one, so the modal
        // must preserve *this* cycle's outcome fields rather than the active
        // cycle's. A PATCH replaces the whole sub-object.
        existingOutcome={cycleOutcome}
        transferDate={
          hubPhases.find(
            (p) => p.type === "transfer" || p.type === "insemination",
          )?.startDate ?? null
        }
        hasPregnancyOpportunity={hasConfirmedPregnancyProcedure(hubPhases)}
        preservationSummary={
          endCycleMode === "complete" || hasRecordedPreservationOutcome
            ? cycle?.cycleType === "egg_freezing" ||
              cycle?.cycleType === "embryo_freezing" ||
              cycle?.cycleType === "sperm_freezing"
              ? {
                  treatmentName: displayName,
                  clinicName: cycle.provider ?? null,
                  startDate: cycle.startDate ?? null,
                  cycleNumber,
                  outcomeCounts: preservationOutcomeCounts,
                  specimens: storageGroups.map((group) => ({
                    type: group.type,
                    count: group.specimens.length,
                    facility: group.facility,
                  })),
                  onAddSpecimen: () => {
                    setEndCycleVisible(false);
                    /*
                     * Opened from THIS cycle's own closing recap, so the intake
                     * already knows which cycle these belong to.
                     *
                     * Without the flag `offersRelatedCycleQuestion` returned
                     * true and she was asked to pick a cycle from a list of
                     * eleven — from inside the cycle she was closing — and
                     * `onSaved` stayed undefined, so the result never reached
                     * the cycle either.
                     */
                    setAddSpecimenForRetrieval(true);
                    setAddSpecimenVisible(true);
                  },
                }
              : undefined
            : undefined
        }
        onBeforeSave={async () => {
          if (cycle?.id) await createSnapshot.mutateAsync(cycle.id);
        }}
        onSaved={() => {
          // Reset straight to Care overview so the screen never re-renders
          // into its "no active cycle" state — that state's persistent
          // post-cycle dialog would surface the same "we're with you" content
          // the modal just showed, and the user would see the moment twice.
          navigation.reset({
            index: 0,
            routes: [{ name: "CareOverview" }],
          });
        }}
        onOpenArticle={(articleId: string) => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Learn", {
            screen: "ArticleDetail",
            params: { articleId, returnTab: "Care" },
          });
        }}
      />
      <Dialog
        visible={embryoAddPromptCount !== null}
        onClose={() => setEmbryoAddPromptCount(null)}
        layout="centered"
        closeButton="minimal"
        contentStyle={{
          paddingTop: 28,
          paddingHorizontal: 24,
          paddingBottom: 24,
        }}
      >
        <DialogHeader
          title={t(
            "care.treatment_hub.frozen_oocyte_thaw.add_created_embryos_title",
          )}
          subtitle={
            embryoAddPromptCount !== null
              ? t(
                  "care.treatment_hub.frozen_oocyte_thaw.add_created_embryos_body",
                  { params: { count: String(embryoAddPromptCount) } },
                )
              : undefined
          }
        />
        <DialogActions style={{ marginTop: 24 }}>
          <SecondaryButton
            label={t(
              "care.treatment_hub.frozen_oocyte_thaw.add_created_embryos_dismiss",
            )}
            onPress={() => setEmbryoAddPromptCount(null)}
            flex={1}
          />
          <PrimaryButton
            label={t(
              "care.treatment_hub.frozen_oocyte_thaw.add_created_embryos_confirm",
            )}
            onPress={() => {
              const count = embryoAddPromptCount ?? 0;
              setEmbryoAddPromptCount(null);
              if (count <= 0) return;
              // Carry only what she actually entered on this cycle — no
              // invented defaults for PGT status, fertilization method, or
              // day of development. Blank fields stay blank; the intake
              // modal's Save button stays disabled until she fills them.
              // The freeze date defaults to today (she can revise), and
              // the clinic name mirrors the cycle's own provider.
              const fertilizationData =
                derivedStepProgress.find(
                  (progress) => progress.stepId === "fertilization",
                )?.data ?? {};
              const embryoDevData =
                derivedStepProgress.find(
                  (progress) => progress.stepId === "embryo_development",
                )?.data ?? {};
              const today = toLocalDateString(new Date());
              const totalEmbryosCreated =
                typeof fertilizationData.eggsFertilized === "number"
                  ? fertilizationData.eggsFertilized
                  : undefined;
              const fertilizationMethod =
                typeof fertilizationData.fertilizationMethod === "string"
                  ? fertilizationData.fertilizationMethod
                  : undefined;
              // Each embryo's grade, not one top grade copied to all of them.
              const embryoGrades = gradedEmbryoGrades(
                readEmbryoGrades(embryoDevData),
              );
              const dayOfDevelopment =
                typeof embryoDevData.dayOfDevelopment === "string"
                  ? embryoDevData.dayOfDevelopment
                  : undefined;
              setEmbryoAddPrefillOverride({
                type: "embryos",
                numberFrozen: count,
                clinicName: cycle?.provider ?? undefined,
                freezeDate: today,
                ...(fertilizationMethod ? { fertilizationMethod } : {}),
                ...(dayOfDevelopment
                  ? { embryoDayOfDevelopment: dayOfDevelopment }
                  : {}),
                ...(totalEmbryosCreated != null ? { totalEmbryosCreated } : {}),
                ...(embryoGrades.length > 0 ? { embryoGrades } : {}),
              });
              setAddSpecimenVisible(true);
            }}
            flex={1}
          />
        </DialogActions>
      </Dialog>
      <AddSpecimenModal
        visible={addSpecimenVisible}
        onClose={() => {
          setAddSpecimenVisible(false);
          setAddSpecimenForRetrieval(false);
          setEmbryoAddPrefillOverride(null);
        }}
        cycleId={cycle?.id}
        // Opened from the cycle's own retrieval step, so these specimens *are*
        // its result — they must not end up attached to no cycle at all.
        isCycleResultIntake={addSpecimenForRetrieval}
        prefill={embryoAddPrefillOverride ?? specimenPrefill}
        onSaved={
          /*
           * The bridge is egg-specific — it writes `egg_retrieval` and
           * `freezing` step data from oocyte fields. The retrieval step only
           * ever sets the flag for `egg_freezing`, but the recap's link sets it
           * for all three preservation types, and on a sperm cycle this would
           * have created an `egg_retrieval` phase, on an embryo cycle
           * overwritten the real retrieval date with the fertilisation date.
           */
          addSpecimenForRetrieval && cycle?.cycleType === "egg_freezing"
            ? handleRecordEggRetrievalOutcome
            : undefined
        }
      />
      <CryoSpecimenDetailModal
        specimen={
          cryoSpecimenDetails.find(
            (specimen) => specimen.id === selectedSpecimenId,
          ) ?? null
        }
        allSpecimens={cryoSpecimenDetails}
        visible={selectedSpecimenId != null}
        onClose={() => setSelectedSpecimenId(null)}
        getCycleNavigation={(cycleId) => {
          const navigate = resolveCycleNavigation(cycleId);
          if (!navigate) return undefined;
          return () => {
            setSelectedSpecimenId(null);
            navigate();
          };
        }}
      />
      <RecordBetaResultModal
        visible={betaResultVisible}
        onClose={() => {
          setBetaResultVisible(false);
          setBetaResultError(null);
          setBetaTestResult(null);
          fetBetaResultRequestIdRef.current = null;
          betaOutcomeRequestIdRef.current = null;
        }}
        onConfirm={handleConfirmBetaResult}
        onComplete={handleBetaResultComplete}
        transferDate={
          hubPhases.find(
            (p) => p.type === "transfer" || p.type === "insemination",
          )?.startDate ?? null
        }
        isLoading={
          recordCycleOutcome.isPending ||
          createBetaTestResult.isPending ||
          updateBetaTestResult.isPending ||
          recordFETBetaResult.isPending
        }
        error={betaResultError}
        isFETBetaOrder={cycleType === "fet"}
        needsTransferDate={
          cycleType === "fet" &&
          !isPartnerCycle &&
          fetBetaLifecycleQuery.isSuccess &&
          fetBetaLifecycleQuery.data === null
        }
        perspective={perspective}
        partnerDisplayName={partnerDisplayName}
        onOpenArticle={(articleId: string) => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Learn", {
            screen: "ArticleDetail",
            params: { articleId, returnTab: "Care" },
          });
        }}
      />
    </GlassScreenLayout>
  );
}

// ─── COMPONENTS ──────────────────────────────────────────────

function CycleSectionTitle({
  title,
  icon,
}: {
  title: string;
  icon?: LucideIcon;
}) {
  return (
    <View style={{ marginTop: 28, marginBottom: 10, marginLeft: 4 }}>
      <SectionTitle icon={icon}>{title}</SectionTitle>
    </View>
  );
}

function NoActiveCycleState({
  onBack,
  onStartTreatment,
  onAddPastCycle,
}: {
  onBack: () => void;
  onStartTreatment: () => void;
  onAddPastCycle: () => void;
}) {
  const t = useTranslations();

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingHorizontal: 16,
        paddingTop: SCREEN_PADDING_TOP,
        paddingBottom: 120,
        maxWidth: 520,
        width: "100%",
        alignSelf: "center",
      }}
    >
      <BackLink
        label={t("care.overview.screen_title")}
        accessibilityLabel={`Back to ${t("care.overview.screen_title")}`}
        action="back_to_care"
        onPress={onBack}
        style={{ marginBottom: 8 }}
      />

      <ScreenHeader
        title={t("care.cycle_detail.empty.no_active_cycle_title")}
        subtitle={t("care.cycle_detail.empty.no_active_cycle_subtitle")}
      />

      <View style={{ gap: 12 }}>
        <ActionRowCard
          leading={
            <Activity
              size={23}
              color={semanticColors.action.primary.background}
              strokeWidth={1.7}
            />
          }
          title={t("care.start_treatment.title_start")}
          subtitle={t("care.start_treatment.subtitle_step1")}
          onPress={onStartTreatment}
          accessibilityLabel={t("care.start_treatment.title_start")}
        />
        <ActionRowCard
          leading={
            <History
              size={23}
              color={semanticColors.action.primary.background}
              strokeWidth={1.7}
            />
          }
          title={t("care.all_past_cycles.add_button")}
          subtitle={t("care.all_past_cycles.add_subtitle")}
          onPress={onAddPastCycle}
          accessibilityLabel={t("care.all_past_cycles.add_button")}
        />
      </View>
    </ScrollView>
  );
}

type MedForm = "pen" | "vial" | "pill" | "suppository";

const API_FORM_TO_UI: Record<string, MedForm> = {
  pre_filled_pen: "pen",
  pre_filled_syringe: "vial",
  injection_solution: "vial",
  lyophilized_powder: "vial",
  tablet: "pill",
  capsule: "pill",
  oral_solution: "pill",
  sublingual_tablet: "pill",
  vaginal_gel: "suppository",
  vaginal_suppository: "suppository",
  transdermal_patch: "pill",
  nasal_spray: "pill",
  cream: "pill",
};

// ─── Post-cycle / first-time empty state ───────────────────────
// When no active cycle exists, we either show:
//  - An outcome-aware close-out (positive or negative) with a Learn shortcut,
//    for patients who just finished a cycle. Reached by re-entering Cycle
//    Detail after `recordCycleOutcome` closes the cycle.
//  - The original "Aucun cycle actif" placeholder, for first-time users with
//    no cycles at all.

type OutcomeKind =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other" | null;

function PostCycleOrEmptyState({
  lastOutcome,
  onBack,
  onBackToCare,
  onOpenArticle,
}: {
  lastOutcome: OutcomeKind;
  onBack: () => void;
  onBackToCare: () => void;
  onOpenArticle: (articleId: string) => void;
}) {
  const t = useTranslations();
  const isPositive = lastOutcome === "pregnancy";
  const isNegative =
    lastOutcome === "no_pregnancy" ||
    lastOutcome === "miscarriage" ||
    lastOutcome === "cancelled" ||
    lastOutcome === "other";

  if (!isPositive && !isNegative) {
    // First-time user — keep the original placeholder.
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 32,
        }}
      >
        <IconButton
          accessibilityLabel={`Back to ${t("care.overview.screen_title")}`}
          action="back_to_care"
          onPress={onBack}
          size={34}
          pressedOpacity={1}
          style={{ position: "absolute", top: SCREEN_PADDING_TOP, left: 20 }}
        >
          <ArrowLeft size={18} color={semanticColors.text.tertiary} />
        </IconButton>
        <ClipboardList
          size={36}
          color={semanticColors.text.faint}
          strokeWidth={1.2}
        />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 16,
            fontWeight: "600",
            color: semanticColors.text.tertiary,
            textAlign: "center",
            marginTop: 16,
            lineHeight: 22,
          }}
        >
          {t("care.cycle_detail.empty.no_active_cycle_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: semanticColors.text.faint,
            textAlign: "center",
            marginTop: 6,
            lineHeight: 19,
          }}
        >
          {t("care.cycle_detail.empty.no_active_cycle_subtitle")}
        </Text>
      </View>
    );
  }

  const primaryButton = (
    <DialogActions style={{ marginTop: 0 }}>
      <PrimaryButton
        label={t("care.cycle_detail.empty.back_to_care")}
        onPress={onBackToCare}
        flex={1}
        trailing={
          <ChevronRight
            size={16}
            color={semanticColors.action.primary.foreground}
            strokeWidth={2}
            aria-hidden
          />
        }
      />
    </DialogActions>
  );

  if (isPositive) {
    const positiveArticles = getPostCycleArticles(
      "positive",
      t("care.lab_result.tag_post_cycle"),
      t,
    );
    const firstArticle = positiveArticles[0];

    return (
      <Dialog
        size="wide"
        visible
        onClose={onBack}
        layout="centered"
        closeButton="minimal"
        dismissOnBackdropTap={false}
        testID="post-cycle-dialog"
      >
        {/* The content scrolls inside the dialog's maximum height; the
            button stays in view below it. */}
        <View style={{ gap: 18, flexShrink: 1 }}>
          <ScrollView
            style={{ flexShrink: 1 }}
            contentContainerStyle={{ gap: 18 }}
            showsVerticalScrollIndicator={false}
          >
            <View testID="post-cycle-illustration">
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            </View>

            <DialogHeader
              align="center"
              title={t("care.cycle_detail.empty.post_cycle_positive_title")}
              subtitle={t("care.cycle_detail.empty.post_cycle_positive_body")}
              style={{ alignItems: "center" }}
              subtitleStyle={{ textAlign: "center", width: "100%" }}
            />

            {firstArticle ? (
              <View testID="post-cycle-article-rail">
                <ArticleRailCard
                  article={firstArticle}
                  read={false}
                  onPress={() => onOpenArticle(firstArticle.id)}
                  fullWidth
                />
              </View>
            ) : null}
          </ScrollView>

          {primaryButton}
        </View>
      </Dialog>
    );
  }

  return (
    <Dialog
      size="wide"
      visible
      onClose={onBack}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={false}
      testID="post-cycle-dialog"
    >
      <View style={{ gap: 18, flexShrink: 1 }}>
        <ScrollView
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 18 }}
          showsVerticalScrollIndicator={false}
        >
          <View style={{ alignItems: "center", gap: 8 }}>
            <DialogIcon
              source={endingIllustration}
              width={68}
              height={72}
              style={{ marginBottom: 2 }}
            />
            <DialogTitleText style={{ textAlign: "center" }}>
              {t("care.beta_result.header_with_you")}
            </DialogTitleText>
          </View>

          <NegativeCloseoutSupport onOpenArticle={onOpenArticle} />
        </ScrollView>

        {primaryButton}
      </View>
    </Dialog>
  );
}
